/* eslint-disable no-unused-vars */
import { i18n } from '@lingui/core';
import { defineMessage, t } from '@lingui/macro';
import { getStartFinishTrackColorScope } from '../utils/getStartFinishTrackColorScope';
import { getUserTapsColorScope } from '../utils/getUserTapsColorScope';
import { circleColorExpression } from '../utils/colorExpression';
import * as V from './variables';
import { HOUSES_MAPBOX_PAINT, DYNAMIC_TARIFF_ZONES_STYLE, RESTRICTION_ZONES_V2_RIDING_STYLE, RESTRICTION_ZONES_V2_PARKING_STYLE, RESTRICTION_ZONES_V2_SPEED_STYLE, RESTRICTION_ZONES_V2_LIMITED_MAPBOX_PAINT_FILL, RESTRICTION_ZONES_V2_LIMITED_MAPBOX_PAINT_LINE, RESTRICTION_ZONES_V2_LIMITED_FICTITIOUS_PAINT_FILL, RESTRICTION_ZONES_V2_LIMITED_FICTITIOUS_PAINT_LINE, RESTRICTION_ZONES_V2_FORBIDDEN_MAPBOX_PAINT_FILL, RESTRICTION_ZONES_V2_FORBIDDEN_MAPBOX_PAINT_LINE, RESTRICTION_ZONES_V2_FORBIDDEN_PROPOSED_PAINT_LINE, RESTRICTION_ZONES_V2_FORBIDDEN_FICTITIOUS_PAINT_FILL, RESTRICTION_ZONES_V2_FORBIDDEN_FICTITIOUS_PAINT_LINE, RESTRICTION_ZONES_V2_PERMITTED_MAPBOX_PAINT_FILL, RESTRICTION_ZONES_V2_PERMITTED_MAPBOX_PAINT_LINE, RESTRICTION_ZONES_V2_PERMITTED_PROPOSED_PAINT_LINE, RESTRICTION_ZONES_V2_PERMITTED_FICTITIOUS_PAINT_FILL, RESTRICTION_ZONES_V2_PERMITTED_FICTITIOUS_PAINT_LINE, RESTRICTION_ZONES_V2_STOP_LIMIT_MAPBOX_PAINT_FILL, RESTRICTION_ZONES_V2_STOP_LIMIT_MAPBOX_PAINT_LINE, RESTRICTION_ZONES_V2_STOP_LIMIT_FICTITIOUS_PAINT_FILL, RESTRICTION_ZONES_V2_STOP_LIMIT_FICTITIOUS_PAINT_LINE, RESTRICTION_ZONES_V2_SPEED_MAPBOX_PAINT_FILL, RESTRICTION_ZONES_V2_SPEED_MAPBOX_PAINT_LINE, RESTRICTION_ZONES_V2_SPEED_FICTITIOUS_PAINT_FILL, RESTRICTION_ZONES_V2_SPEED_FICTITIOUS_PAINT_LINE, RESTRICTION_ZONES_V2_LIMITED_INNER_PAINT_LINE, RESTRICTION_ZONES_V2_FORBIDDEN_INNER_PAINT_LINE, RESTRICTION_ZONES_V2_PERMITTED_INNER_PAINT_LINE, RESTRICTION_ZONES_V2_STOP_LIMIT_INNER_PAINT_LINE, RESTRICTION_ZONES_V2_SPEED_INNER_PAINT_LINE, REBALANCE_FORECAST_STYLE, FACT_REBALANCE_COLORS_STYLE, FACT_REBALANCE_PERIODS, SCR_TRACKS_STYLE, SCR_INFRA_STYLE, SIM_ACCIDENTS_STYLE, CHARGING_OBJECTS_LOAD_STYLE, CHARGING_PROPOSALS_STYLE, CHARGING_PROPOSALS_PLAN_STYLE } from './layerStyles';
import {
    AGGREGATED_TRIPS_AUGUST_H3_GRADIENT_LEGEND,
    RECHARGING_STATS_H3_DYNAMIC_LEGEND,
    RESTRICTION_ZONES_V2_LEGEND,
    ADMIN_AGG_TRACKS_V2_LEGEND,
    ADMIN_AGG_TRACKS_MONTHLY_LEGEND,
    AGG_TRACKS_COHORT_LEGEND,
    USER_COHORTS_LEGEND,
    REG_TO_TRIP_CONVERSION_LEGEND,
    LOST_DEVICES_H3_LEGEND,
    WIFI_AP_H3_LEGEND,
    ROUTE_ANOMALIES_H3_LEGEND,
    WALKSCORE_LEGEND,
    CHARGING_OBJECTS_LOAD_LEGEND,
} from './legends';

const rebalancingAllRegionsDeficitExcess = {
    id: 'Агрегированный баланс самокатов на парковках',
    label: defineMessage({ message: 'Агрегированный баланс самокатов на парковках' }),
    type: 'fill',
    'source-layer': 'martin.deficit_excess_count_agg_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.deficit_excess_count_agg_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'deficit_excess_count_agg' ],
            -10,
            '#30123b',
            -5,
            '#4099ff',
            0,
            '#46f884',
            5,
            '#e1dd37',
            10,
            '#ef5811',
        ],
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const parkingIsochrones = {
    id: 'Изохроны доступности парковок',
    label: defineMessage({ message: 'Изохроны доступности парковок' }),
    type: 'fill',
    'source-layer': 'martin.dissolved_parkings_isochrones_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.dissolved_parkings_isochrones_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'with_scooters',
            stops: [
                [
                    '0',
                    '#369ac2',
                ],
                [
                    '1',
                    'red',
                ],
            ],
            default: 'black',
        },
        'fill-opacity': 0.5,
    },
    filter:[ 'all',
        [ '==', [ 'to-number', [ 'get', 'distance' ] ], 200  ],
        [ 'match', [ 'get', 'with_scooters' ], [ '0' ], true, false ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ServiceCentresIsochronesLabelsText = {
    id: 'Изохроны доступности сервисных центров_label_text',
    label: defineMessage({ message: 'Изохроны доступности сервисных центров_label_text' }),
    type: 'symbol',
    'source-layer': 'martin.sc_isochrones_label_mvt',
    minzoom: 8,
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.sc_isochrones_label_mvt.json`,
    },
    layout: {
        visibility: 'none',
        'text-field': [ 'get', 'name' ],
        'text-size': 12,
        'text-anchor': 'bottom',
        'text-offset': [ 0, -1 ],
    },
    paint: {
        'text-halo-color': '#fff',
        'text-halo-width': 1.5,
    },
    filter: [ 'match', [ 'get', 'type' ], [ 'ACTIVE' ], true, false ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// ServiceCentresIsochronesLabelsText
const ServiceCentresIsochronesLabels = {
    id: 'Изохроны доступности сервисных центров_label',
    label: defineMessage({ message: 'Изохроны доступности сервисных центров_label' }),
    type: 'circle',
    'source-layer': 'martin.sc_isochrones_label_mvt',
    // minzoom: 8,
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.sc_isochrones_label_mvt.json`,
    },
    layout: {
        visibility: 'none',
    },
    paint: {
        'circle-opacity': 0.7,
        'circle-color': {
            base: 1,
            type: 'categorical',
            property: 'type',
            stops: [
                [
                    'СЦ',
                    '#89CFF0',
                ],
                [
                    'СЗ',
                    'red',
                ],
                [
                    'ПСЦ-АДМИН',
                    'purple',
                ],
                [
                    'ПСЗ-АДМИН',
                    'pink',
                ],
                [
                    'Only city',
                    'yellow',
                ],
                [
                    'Шкаф',
                    'blue',
                ],
                [
                    'ПШкаф',
                    '#7B3F00',
                ],
                [
                    'ACTIVE',
                    '#00c04b',
                ],
                [
                    'PROSPECTIVE',
                    'orange',
                ],
            ],
            default: 'black',
        },
        'circle-radius': 6,
        'circle-stroke-color': {
            base: 1,
            type: 'categorical',
            property: 'status',
            stops: [
                [
                    'ADMIN',
                    'black',
                ],
                [
                    'BA',
                    'white',
                ],
            ],
            default: 'black',
        },
        'circle-stroke-width': 1,
    },
    filter: [ 'match', [ 'get', 'type' ], [ 'ACTIVE' ], true, false ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ServiceCentresIsochronesOutline = {
    id: 'Изохроны доступности сервисных центров_outline',
    label: defineMessage({ message: 'Изохроны доступности сервисных центров' }),
    type: 'line',
    'source-layer': 'martin.warehouse_iso_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.warehouse_iso_mvt.json`,
        promoteId: 'id',
    },
    layout: { visibility: 'none' },
    paint: {
        'line-width': 2,
        'line-color': {
            base: 1,
            type: 'categorical',
            property: 'type',
            stops: [
                [
                    'СЦ',
                    '#89CFF0',
                ],
                [
                    'СЗ',
                    'red',
                ],
                [
                    'ПСЦ-АДМИН',
                    'purple',
                ],
                [
                    'ПСЗ-АДМИН',
                    'pink',
                ],
                [
                    'Only city',
                    'yellow',
                ],
                [
                    'Шкаф',
                    'blue',
                ],
                [
                    'ПШкаф',
                    '#7B3F00',
                ],
                [
                    'ACTIVE',
                    '#00c04b',
                ],
                [
                    'PROSPECTIVE',
                    'orange',
                ],
            ],
            default: 'black',
        },
        'line-opacity': [
            'case',
            [ 'boolean', [ 'feature-state', 'selected' ], false ],
            1,
            0,
        ],
    },
    filter: [ 'all',
        [ '==', [ 'to-number', [ 'get', 'time' ] ], 15  ],
        [ 'match', [ 'get', 'type' ], [ 'ACTIVE' ], true, false ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ServiceCentresIsochrones = {
    id: 'Изохроны доступности сервисных центров',
    label: defineMessage({ message: 'Изохроны доступности сервисных центров' }),
    type: 'fill',
    'source-layer': 'martin.warehouse_iso_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.warehouse_iso_mvt.json`,
        promoteId: 'id',
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'type',
            stops: [
                [
                    'СЦ',
                    '#89CFF0',
                ],
                [
                    'СЗ',
                    'red',
                ],
                [
                    'ПСЦ-АДМИН',
                    'purple',
                ],
                [
                    'ПСЗ-АДМИН',
                    'pink',
                ],
                [
                    'Only city',
                    'yellow',
                ],
                [
                    'Шкаф',
                    'blue',
                ],
                [
                    'ПШкаф',
                    '#7B3F00',
                ],
                [
                    'ACTIVE',
                    '#00c04b',
                ],
                [
                    'PROSPECTIVE',
                    'orange',
                ],
            ],
            default: 'black',
        },
        'fill-opacity': 0.5,
        'fill-outline-color': 'black',
    },
    filter: [ 'all',
        [ '==', [ 'to-number', [ 'get', 'time' ] ], 15  ],
        [ 'match', [ 'get', 'type' ], [ 'ACTIVE' ], true, false ],
    ],
    additionalLayers: [
        ServiceCentresIsochronesLabels,
        ServiceCentresIsochronesLabelsText,
        ServiceCentresIsochronesOutline,
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// DA-15980 — A/B-сравнение источников слоя «Землепользование».
//
// Старый вариант (martin.good_bad_area_mvt → martin.landuse_boundaries_geom) считает питоновский
// op dwh_area_update_info.py по сырым тегам OSM, ~2 ч Авроры каждую ночь. Новый — витрина
// dwh.urban_zones_classified из пайплайна типологии территорий (DA-15202).
//
// НОВОЙ MVT-ФУНКЦИИ НЕ ЗАВОДИМ: переиспользуем уже существующую martin.urban_typology_zones_mvt,
// у неё есть bldg_class, а сведение в три категории делается выражением match прямо тут. Это
// проба, решение о переезде не принято — пока она не принята, бэкенд трогать незачем. Источник
// объявлен с тем же id, что у слоя «Типология территорий» (initLayer добавляет источник только
// если его ещё нет), поэтому тайлы грузятся один раз на оба слоя.
export const LANDUSE_NEW_SOURCE = {
    id: 'martin.urban_typology_zones_mvt_source',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.urban_typology_zones_mvt.json`,
};
export const LANDUSE_NEW_LAYER_ID = 'Землепользование (новый источник)';
// Цвета — те же, что у старого варианта, иначе сравнение показывало бы разницу палитр.
export const LANDUSE_BAD_COLOR = '#f891ff';
export const LANDUSE_POSITIVE_COLOR = '#ff9100';
export const LANDUSE_PRIVATE_COLOR = 'yellow';
// Четвёртая категория, которой у старого варианта нет вовсе. В Калуге unclassified — 28.5 км²,
// 19 % города; слить его с «плохим» значило бы спрятать ровно то расхождение, ради которого
// сравнение и делается.
export const LANDUSE_UNCLASSIFIED_COLOR = '#9CA3AF';
// Маппинг собран БУКВАЛЬНО ПО СПИСКАМ ТЕГОВ старого скрипта, а не «по смыслу»: иначе на карте
// была бы видна разница маппинга, а не разница источников.
//
// Смысл «плохого» — территории, на которых заведомо закрыт или ограничен проезд самокатов.
// Отсюда education (amenity=school/kindergarten) и healthcare (amenity=hospital) в «плохом»,
// sport (leisure=stadium/sports_centre) туда же. И наоборот campus: кампусы ВУЗов открыты для
// проката и дают большой поток, у старой схемы amenity=university лежит в «хорошем», и в
// типологии для этого есть отдельный от education класс.
//
// green_park (leisure=park/garden) — «хорошее», green (natural=wood, landuse=grass/forest) —
// «плохое»: у старой схемы park в positive, а wood и grass в bad.
//
// Класс service в витрине зон не встречается ни разу (есть только у зданий) и не маппится —
// если появится, уйдёт в дефолт вместе с unclassified.
export const LANDUSE_NEW_COLOR = [
    'match', [ 'get', 'bldg_class' ],
    'residential_individual', LANDUSE_PRIVATE_COLOR,
    'residential_multi', LANDUSE_POSITIVE_COLOR,
    'commercial', LANDUSE_POSITIVE_COLOR,
    'campus', LANDUSE_POSITIVE_COLOR,
    'green_park', LANDUSE_POSITIVE_COLOR,
    'green', LANDUSE_BAD_COLOR,
    'industrial', LANDUSE_BAD_COLOR,
    'education', LANDUSE_BAD_COLOR,
    'healthcare', LANDUSE_BAD_COLOR,
    'sport', LANDUSE_BAD_COLOR,
    'transport_hub', LANDUSE_BAD_COLOR,
    LANDUSE_UNCLASSIFIED_COLOR,
];
// Слой-компаньон старого «Землепользования»: собственного чекбокса в сайдбаре нет, видим ровно
// один из двух — какой, решают радиокнопки в легенде (mapFilters.landuseSource). Та же механика,
// что у пары «Типология застройки» плоская/3D.
//
// minzoom 10 повторяет старый слой: ниже читать заливку всё равно нельзя, а тайлы витрины на
// обзорных зумах тяжёлые (у urban_typology_zones_mvt нет ни simplify, ни lowzoom_group, см.
// DA-15393).
export const LANDUSE_NEW_SOURCE_LAYER = 'martin.urban_typology_zones_mvt';
const landuseNewLayer = {
    id: LANDUSE_NEW_LAYER_ID,
    type: 'fill',
    'source-layer': LANDUSE_NEW_SOURCE_LAYER,
    source: LANDUSE_NEW_SOURCE,
    minzoom: 10,
    layout: { visibility: 'none' },
    // Обводки нет намеренно — у старого варианта её тоже нет. Зоны типологии покрывают город
    // сплошь, и чёрный контур на паре тысяч полигонов перебил бы заливку: сравнивать надо
    // границы категорий, а не квартальную сетку (для неё есть подслой «Границы кварталов»
    // у «Типологии территорий»).
    paint: {
        'fill-color': LANDUSE_NEW_COLOR,
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const landuse = {
    id: 'Землепользование',
    label: defineMessage({ message: 'Землепользование' }),
    type: 'fill',
    'source-layer': 'martin.good_bad_area_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.good_bad_area_mvt.json`,
    },
    minzoom: 10,
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'type',
            stops: [
                [
                    'bad',
                    '#f891ff',
                ],
                [
                    'positive',
                    '#ff9100',
                ],
                [
                    'private',
                    'yellow',
                ],
            ],
            default: 'black',
        },
        'fill-opacity': 0.5,
    },
    additionalLayers: [ landuseNewLayer ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const enclosuresLabelsText = {
    id: 'Кварталы_label_text',
    label: defineMessage({ message: 'Кварталы_label_text' }),
    type: 'symbol',
    'source-layer': 'martin.enclosures_labels_mvt',
    minzoom: 12,
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.enclosures_labels_mvt.json`,
    },
    layout: {
        visibility: 'none',
        'text-field': [ 'get', 'scooter_final_flexible' ],
        'text-size': 10,
        'text-anchor': 'bottom',
    },
    paint: {
        'text-halo-color': 'white',
        'text-halo-width': 1,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const enclosuresLines = {
    id: 'Кварталы_обводка',
    label: defineMessage({ message: 'Кварталы_обводка' }),
    type: 'line',
    'source-layer': 'martin.enclosures_raw_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.enclosures_raw_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': 'black',
        'line-opacity': 0.7,
        'line-width': 0.7,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const enclosures = {
    id: 'Кварталы',
    label: defineMessage({ message: 'Кварталы (beta)' }),
    type: 'fill',
    'source-layer': 'martin.enclosures_raw_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.enclosures_raw_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'scooter_final_flexible' ] ],
            -1,
            'red',
            0,
            'white',
            5,
            '#0d0887',
            10,
            'rgb(127,3,168)',
            20,
            'rgb(203,71,120)',
            50,
            '#f07f4f',
            100,
            '#f0f921',
        ],
        'fill-opacity': 0.3,
    },
    additionalLayers: [ enclosuresLines, enclosuresLabelsText ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// Радиус пунсона парковки Whoosh по вместимости (тогл «Вместимость парковки»).
// Включён по умолчанию; выключение переключает слой на единый радиус.
export const WHOOSH_PARKING_CAPACITY_RADIUS = [
    'case',
    [ '<=', [ 'to-number', [ 'get', 'capacity' ] ], 0 ],
    3,
    [ 'interpolate',
        [ 'linear' ],
        [ 'get', 'capacity' ],
        5,
        3,
        10,
        5,
        15,
        7,
    ],
];
// Единый радиус пунсона при выключенном тогле вместимости
// (~30% меньше середины диапазона 3–7).
export const WHOOSH_PARKING_FLAT_RADIUS = 3.5;

const parkingLots = {
    id: 'Парковки whoosh',
    label: defineMessage({ message: 'Парковки Whoosh' }),
    type: 'circle',
    'source-layer': 'martin.function_parkings_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.function_parkings_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-color': {
            base: 1,
            type: 'categorical',
            property: 'status',
            stops: [
                [
                    V.DEACTIVATED,
                    '#3d5cf6',
                ],
                [
                    V.MANUAL_ADDED,
                    '#ff03bc',
                ],
                [
                    V.SYSTEM_ADDED,
                    '#F7FE2E',
                ],
                [
                    V.VERIFIED,
                    '#0dd75b',
                ],
                [
                    V.PRE_VERIFIED,
                    'red',
                ],
            ],
            default: '#3d5cf6',
        },
        'circle-stroke-color': [ 'case',
            [ 'all',
                [ 'match', [ 'get', 'parking_type' ], [ 'VIRTUAL' ], true, false ],
            ], 'black',
            [ 'all',
                [ 'match', [ 'get', 'parking_type' ], [ 'BICYCLE' ], true, false ],
            ], 'grey',
            [ 'all',
                [ 'match', [ 'get', 'parking_type' ], [ 'hotspot' ], true, false ],
            ], 'red',
            [ 'any',
                [ 'match', [ 'get', 'parking_type' ], [ 'hotspot', 'BICYCLE' ], true, false ],
            ], 'red',
            'black' ],
        'circle-stroke-width': [ 'case',
            [ 'all',
                [ 'match', [ 'get', 'parking_type' ], [ 'VIRTUAL' ], true, false ],
            ], 1.5,
            [ 'any',
                [ 'match', [ 'get', 'parking_type' ], [ 'BICYCLE' ], true, false ],
                [ 'match', [ 'get', 'parking_type' ], [ 'hotspot' ], true, false ],
            ], 2.5,
            3 ],
        'circle-radius': WHOOSH_PARKING_CAPACITY_RADIUS,
    },
    filter: [ '==', [ 'to-string', [ 'get', 'status' ] ], V.VERIFIED ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const parking1300 = {
    id: 'Парковки (1300)',
    label: defineMessage({ message: 'Парковки (1300)' }),
    type: 'circle',
    'source-layer': 'martin.parkings_1300_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parkings_1300_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-color': {
            base: 1,
            type: 'categorical',
            property: 'status_1300',
            stops: [
                [
                    'AGREED',
                    '#d9c22d',
                ],
                [
                    'APPLICATION_SUBMITTED',
                    '#452ef0',
                ],
                [
                    'PAID_FOR_PERIOD',
                    '#33a02c',
                ],
                [
                    'REFUSED',
                    '#e31a1c',
                ],
                [
                    'NOT_SUBMITTED',
                    '#3e3e3e',
                ],
            ],
            default: '#3d5cf6',
        },
        'circle-stroke-color': 'white',
        'circle-stroke-width': 1,
    },
    filter: [ 'any',
        [ 'match', [ 'get', 'status_1300' ], [ 'AGREED', 'APPLICATION_SUBMITTED' ], true, false ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const parkingDensity = {
    id: 'Плотность парковок',
    label: defineMessage({ message: 'Плотность парковок' }),
    type: 'circle',
    'source-layer': 'martin.parking_clusters_kde_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parking_clusters_kde_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 10,
    paint: {
        'circle-color': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'parkings_per_sq_km' ],
            // [ 'get', 'parkings_in_iso_200m' ],
            10,
            '#2a4858',
            80,
            '#fde725', // - yellow from Virids palette
        ],
        'circle-opacity': 1,
        'circle-radius': 3,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const parkingsDowntime48h = {
    id: 'Простой самокатов (48+ ч)',
    label: defineMessage({ message: 'Простой самокатов (48+ ч)' }),
    type: 'circle',
    'source-layer': 'martin.downtime_sb_48h_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.downtime_sb_48h_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 8,
    paint: {
        'circle-opacity': 0.8,
        'circle-stroke-color': [ 'case',
            [ 'all',
                [ '==', [ 'to-number', [ 'get', 'n_devices' ] ], 1 ],
            ], 'black',
            'black' ],
        'circle-stroke-width': [ 'case',
            [ 'any',
                [ '==', [ 'to-number', [ 'get', 'n_devices' ] ], 1 ],
                [ '==', [ 'to-number', [ 'get', 'n_devices' ] ], 0 ],
            ], 1.5,
            0.7 ],
        'circle-color': [
            'interpolate',
            [ 'linear' ],
            [
                'get',
                'n_devices',
            ],
            1,
            'yellow',
            3,
            '#11c1e8',
            9,
            '#c572ee',
            20,
            '#f6515c',
            30,
            '#f01625',
        ],
        'circle-radius': [
            'interpolate',
            [ 'linear' ],
            [
                'get',
                'n_devices',
            ],
            1,
            2.5,
            2,
            3,
            3,
            4,
            7,
            5,
            15,
            6.5,
            30,
            8,
        ],
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const rechargingStats = {
    id: 'Кол-во переходов в Change Battery',
    label: defineMessage({ message: 'Кол-во переходов в Change Battery' }),
    // Заголовок экспорта: период замен батарей (rechargingStatsPeriod: 1d/7d/30d/seasons).
    exportTitle: ({ filters }) => {
        const period = filters.rechargingStatsPeriod;
        if (!period) {
            return '';
        }
        if (period === 'seasons') {
            return i18n._(defineMessage({ message: 'Статистика по заменам батарей за сезоны 2024–2025 гг.' }));
        }
        const days = String(period).replace(/\D/g, '');
        return days
            ? i18n._(defineMessage({ message: 'Статистика по заменам батарей за {days} дней' }), { days })
            : '';
    },
    type: 'fill',
    minzoom: 4,
    'source-layer': 'martin.chb_recharging_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.chb_recharging_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        // Начальный paint — сезоны; динамически меняется через setRechargingPaint в Map.jsx (DA-14276)
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ '*',
                [ 'to-number', [ 'get', 'cnt_recharges_seasons' ] ],
                [ '^', 3, [ '-', [ 'to-number', [ 'get', 'zoom' ] ], 10 ] ],
            ],
            1,
            '#440154',
            5,
            '#414487',
            14,
            '#2a788e',
            42,
            '#22a884',
            100,
            '#7ad151',
            180,
            '#c7e020',
            250,
            '#fde725',
        ],
        'fill-opacity': 0.5,
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
    },
    legend: RECHARGING_STATS_H3_DYNAMIC_LEGEND,
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ClassifiedParkings = {
    id: 'Классифицированные парковки',
    label: defineMessage({ message: 'Классифицированные парковки' }),
    type: 'circle',
    'source-layer': 'martin.classified_parkings_by_region_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.classified_parkings_by_region_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-color': [ 'case',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'balanced' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'green',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'proficit' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'yellow',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'deficit' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'red',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'min' ], true, false ],
            ], '#0dd75b',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'less_5' ], true, false ],
            ], 'pink',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'zero' ], true, false ],
            ], 'white',
            '#ff9100',
        ],
        'circle-stroke-color': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    'white',
                ],
                [
                    'middle',
                    'black',
                ],
            ],
            default: 'black',
        },
        'circle-stroke-width': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    1,
                ],
                [
                    'middle',
                    1,
                ],
            ],
            default: 1,
        },
        'circle-radius': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    7,
                ],
                [
                    'middle',
                    5.5,
                ],
                [
                    'min',
                    3.5,
                ],
                [
                    'zero',
                    3.5,
                ],
            ],
            default: 4,
        },
    },
    filter: [ 'all',
        [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
        [ '==', [ 'get', 'dow' ], 0 ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ClassifiedParkingsDayPart = {
    id: 'Классифицированные парковки (по времени суток)',
    label: defineMessage({ message: 'Классифицированные парковки (по времени суток)' }),
    type: 'circle',
    'source-layer': 'martin.classified_parkings_by_region_daypart_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.classified_parkings_by_region_daypart_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-color': [ 'case',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'balanced' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'green',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'proficit' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'yellow',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'deficit' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'red',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'min' ], true, false ],
            ], '#0dd75b',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'less_5' ], true, false ],
            ], 'pink',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'zero' ], true, false ],
            ], 'white',
            '#ff9100',
        ],
        'circle-stroke-color': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    'white',
                ],
                [
                    'middle',
                    'black',
                ],
            ],
            default: 'black',
        },
        'circle-stroke-width': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    1,
                ],
                [
                    'middle',
                    1,
                ],
            ],
            default: 1,
        },
        'circle-radius': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    7,
                ],
                [
                    'middle',
                    5.5,
                ],
                [
                    'min',
                    3.5,
                ],
                [
                    'zero',
                    3.5,
                ],
            ],
            default: 4,
        },
    },
    filter: [ 'all',
        [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
        [ '==', [ 'get', 'day_part' ], 2 ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ClassifiedParkingsRebalanced = {
    id: 'Классифицированные парковки (ребаланс)',
    label: defineMessage({ message: 'Классифицированные парковки (ребаланс)' }),
    type: 'circle',
    'source-layer': 'martin.classified_parkings_by_region_rebalanced_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.classified_parkings_by_region_rebalanced_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-color': [ 'case',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'balanced' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'green',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'proficit' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'yellow',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'deficit' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'red',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'min' ], true, false ],
            ], '#0dd75b',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'less_5' ], true, false ],
            ], 'pink',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'zero' ], true, false ],
            ], 'white',
            '#ff9100',
        ],
        'circle-stroke-color': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    'white',
                ],
                [
                    'middle',
                    'black',
                ],
            ],
            default: 'black',
        },
        'circle-stroke-width': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    1,
                ],
                [
                    'middle',
                    1,
                ],
            ],
            default: 1,
        },
        'circle-radius': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    7,
                ],
                [
                    'middle',
                    5.5,
                ],
                [
                    'min',
                    3.5,
                ],
                [
                    'zero',
                    3.5,
                ],
            ],
            default: 4,
        },
    },
    filter: [ 'any',
        [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ClassifiedParkingsDayPartRebalanced = {
    id: 'Классифицированные парковки (ребаланс, по времени суток)',
    label: defineMessage({ message: 'Классифицированные парковки (ребаланс, по времени суток)' }),
    type: 'circle',
    'source-layer': 'martin.classified_parkings_by_region_daypart_rebalanced_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.classified_parkings_by_region_daypart_rebalanced_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-color': [ 'case',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'balanced' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'green',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'proficit' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'yellow',
            [ 'all',
                [ 'match', [ 'get', 'type_' ], [ 'deficit' ], true, false ],
                [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
            ], 'red',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'min' ], true, false ],
            ], '#0dd75b',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'less_5' ], true, false ],
            ], 'pink',
            [ 'all',
                [ 'match', [ 'get', 'class_' ], [ 'zero' ], true, false ],
            ], 'white',
            '#ff9100',
        ],
        'circle-stroke-color': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    'white',
                ],
                [
                    'middle',
                    'black',
                ],
            ],
            default: 'black',
        },
        'circle-stroke-width': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    1,
                ],
                [
                    'middle',
                    1,
                ],
            ],
            default: 1,
        },
        'circle-radius': {
            base: 1,
            type: 'categorical',
            property: 'class_',
            stops: [
                [
                    'top',
                    7,
                ],
                [
                    'middle',
                    5.5,
                ],
                [
                    'min',
                    3.5,
                ],
                [
                    'zero',
                    3.5,
                ],
            ],
            default: 4,
        },
    },
    filter: [ 'all',
        [ 'match', [ 'get', 'class_' ], [ 'top', 'middle' ], true, false ],
        [ 'match', [ 'get', 'day_part' ], [ 2 ], true, false ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// Таба/Форкаст ребаланса: объединённый слой на одном источнике.
// Режим переключается радиокнопкой в легенде (rebalanceForecastTabaMode):
//   'forecast' — Форкаст ребаланса (радиус по полю forecast, цвет по cluster)
//   'taba'     — Таба ребаланса    (радиус по |delta|, фильтр delta≠0)
// Начальный режим — 'taba'; Map.jsx применяет paint/filter при смене режима.
const rebalanceForecastTaba = {
    id: 'Таба/Форкаст',
    label: defineMessage({ message: 'Таба/Форкаст' }),
    // Прогноз и таба пересчитываются в течение дня — держим тайлы свежими (см. utils/liveTileRefresh.js)
    liveRefresh: true,
    // Заголовок экспорта по выбранному режиму (rebalanceForecastTabaMode).
    exportTitle: ({ filters }) => (
        filters.rebalanceForecastTabaMode === 'forecast'
            ? i18n._(defineMessage({ message: 'Прогноз ребаланса' }))
            : i18n._(defineMessage({ message: 'Таба ребаланса' }))
    ),
    type: 'circle',
    'source-layer': 'martin.rebalance_forecast_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.rebalance_forecast_mvt.json`,
    },
    layout: { visibility: 'none' },
    // Начальный paint — режим Форкаст
    paint: {
        'circle-color': [ 'match', [ 'to-number', [ 'get', 'cluster' ] ],
            0, REBALANCE_FORECAST_STYLE.categories[0].color,
            4, REBALANCE_FORECAST_STYLE.categories[1].color,
            5, REBALANCE_FORECAST_STYLE.categories[1].color,
            '#cccccc',
        ],
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'forecast' ], 0 ],
            0,   4,
            10,  6,
            30,  8,
            60,  11,
            100, 14,
            250, 18,
        ],
        'circle-opacity': 0.85,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
const ParkingRadiusPoly0 = {
    id: 'Парковки с радиусом_poly0',
    label: defineMessage({ message: 'Парковки с радиусом_poly1' }),
    type: 'fill',
    'source-layer': 'martin.parking_radius_poly_limited_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parking_radius_poly_limited_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'is_permitted',
            stops: [
                [
                    '0',
                    'orange',
                ],
                [
                    '1',
                    'green',
                ],
            ],
            default: 'black',
        },
        'fill-opacity': 0.5,
    },
    filter:[ 'any',
        [ 'match', [ 'get', 'is_permitted' ], [ '2' ], true, false ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ParkingRadiusPoly1 = {
    id: 'Парковки с радиусом_poly1',
    label: defineMessage({ message: 'Парковки с радиусом_poly1' }),
    type: 'fill',
    'source-layer': 'martin.parking_radius_poly_permitted_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parking_radius_poly_permitted_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'is_permitted',
            stops: [
                [
                    '0',
                    'orange',
                ],
                [
                    '1',
                    'green',
                ],
            ],
            default: 'black',
        },
        'fill-opacity': 0.5,
    },
    filter:[ 'any',
        [ 'match', [ 'get', 'is_permitted' ], [ '2' ], true, false ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ParkingRadiusPrep = {
    id: 'Парковки с радиусом',
    label: defineMessage({ message: 'Парковки с радиусом' }),
    type: 'circle',
    'source-layer': 'martin.parking_radius_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parking_radius_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-color': [ 'case',
            [ 'all',
                [ '<', [ 'to-number', [ 'get', 'parking_radius' ] ], 15 ],
            ], 'red',
            [ 'all',
                [ '>=', [ 'to-number', [ 'get', 'parking_radius' ] ], 15 ],
                [ '<', [ 'to-number', [ 'get', 'parking_radius' ] ], 25 ],
            ], 'white',
            [ 'all',
                [ '==', [ 'to-number', [ 'get', 'parking_radius' ] ], 25 ],
            ], 'green',
            [ 'all',
                [ '>', [ 'to-number', [ 'get', 'parking_radius' ] ], 25 ],
            ], 'yellow',
            'blue',
        ],
        'circle-stroke-color': 'black',
        'circle-stroke-width': 1,
        'circle-radius': [ 'case',
            [ 'any',
                [ '<', [ 'to-number', [ 'get', 'parking_radius' ] ], 15 ],
            ], 5.5,
            [ 'any',
                [ '>=', [ 'to-number', [ 'get', 'parking_radius' ] ], 15 ],
                [ '<', [ 'to-number', [ 'get', 'parking_radius' ] ], 25 ],
            ], 3,
            [ 'any',
                [ '==', [ 'to-number', [ 'get', 'parking_radius' ] ], 25 ],
            ], 3,
            [ 'any',
                [ '>', [ 'to-number', [ 'get', 'parking_radius' ] ], 25 ],
            ], 4.5,
            5,
        ],
        'circle-opacity': 0.9,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ParkingRadius = {
    id: 'Парковки с радиусом_fin',
    label: defineMessage({ message: 'Парковки с радиусом' }),
    availableFor: [ V.ADMIN, V.DESIGNER ],
    additionalLayers: [
        ParkingRadiusPoly0,
        ParkingRadiusPoly1,
        ParkingRadiusPrep,
    ],
};
const housesHeatmap = {
    id: 'Жилые дома - heatmap',
    label: defineMessage({ message: 'Жилые дома - heatmap' }),
    type: 'heatmap',
    'source-layer': 'martin.houses_ref_zhkh_union_smartloc_mvt',
    source: {
        id: 'Жилые дома_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.houses_ref_zhkh_union_smartloc_mvt.json`,
    },
    maxzoom: 16,
    layout: { visibility: 'none' },
    paint: {
        'heatmap-weight': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'total_ppl' ],
            2,
            0,
            100,
            1.3,
        ],
        'heatmap-intensity': [
            'interpolate',
            [ 'linear' ],
            [ 'zoom' ],
            9,
            0,
            22,
            5,
        ],
        'heatmap-color': [
            'interpolate',
            [ 'linear' ],
            [ 'heatmap-density' ],
            0.2,
            'hsla(240, 100%, 50%, 0)',
            0.25,
            '#4169e1',
            0.35,
            '#00ffff',
            0.5,
            '#00ff00',
            0.7,
            '#ffff00',
            1,
            '#ff0000',
        ],
        'heatmap-radius': [
            'interpolate',
            [ 'linear' ],
            [ 'zoom' ],
            6,
            2,
            14,
            10,
            16,
            26,
        ],
        'heatmap-opacity': [
            'interpolate',
            [ 'linear' ],
            [ 'zoom' ],
            9,
            1,
            17,
            0.05,
        ],
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const houses = {
    id: 'Жилые дома',
    label: defineMessage({ message: 'Жилые дома' }),
    type: 'circle',
    'source-layer': 'martin.houses_ref_zhkh_union_smartloc_mvt',
    source: {
        id: 'Жилые дома_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.houses_ref_zhkh_union_smartloc_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 10,
    paint: HOUSES_MAPBOX_PAINT,
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const newBuildings = {
    id: 'Новостройки',
    label: defineMessage({ message: 'Новостройки' }),
    type: 'circle',
    'source-layer': 'martin.new_buildings_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.new_buildings_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 8,
    paint: {
        'circle-color': {
            base: 1,
            type: 'categorical',
            property: 'object_class',
            get stops () {
                return [
                    [
                        t`Комфорт`,
                        '#3d5cf6',
                    ],
                    [
                        t`Типовой`,
                        '#cf00cc',
                    ],
                    [
                        t`Элитный`,
                        '#F7FE2E',
                    ],
                    [
                        t`Бизнес`,
                        '#0dd75b',
                    ],
                    [
                        t`Другое`,
                        'red',
                    ],
                ];
            },
            default: '#3d5cf6',
        },
        'circle-opacity': 0.5,
        'circle-radius': 4.5, // Can't change radius with no reason
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const poi = {
    id: 'POI',
    label: defineMessage({ message: 'POI' }),
    type: 'circle',
    'source-layer': 'martin.osm_poi_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.osm_poi_mvt.json`,
    },
    layout: { visibility: 'none' },
    // minzoom: 10,
    paint: {
        'circle-color': circleColorExpression,
        'circle-radius': {
            base: 1,
            type: 'categorical',
            property: 'category',
            stops: [
                [ 'subway_entrance',
                    3.5,
                ],
            ],
            default: 2.5,
        },
        // ATP-фичи (source='atp') рисуем «колечками»: прозрачная заливка + цветной
        // контур категории — ненавязчивая визуальная дифференциация вклада AllThePlaces
        // от сплошных OSM-точек (DA-7087).
        'circle-opacity': [ 'case', [ '==', [ 'get', 'source' ], 'atp' ], 0, 1 ],
        'circle-stroke-color': [ 'case',
            [ '==', [ 'get', 'source' ], 'atp' ], circleColorExpression,
            'black' ],
        'circle-stroke-width': [ 'case', [ '==', [ 'get', 'source' ], 'atp' ], 1.2, 0.5 ],
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const SbWithErrors = {
    id: 'Самокаты с ошибками',
    label: defineMessage({ message: 'Самокаты с ошибками' }),
    type: 'symbol',
    'source-layer': 'martin.current_sb_with_errors_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.current_sb_with_errors_mvt.json`,
    },
    layout: {
        visibility: 'none',
        'icon-image': [
            'case',
            [ '==', [ 'get', 'error_value' ], '21' ], 'scooterRed',
            [ '==', [ 'get', 'error_value' ], '50' ], 'scooterPurple',
            'default_icon',
        ],
        'icon-size': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'error_duration_minutes' ] ],
            0,
            0.025,
            50,
            0.03,
            200,
            0.04,
            1000,
            0.05,
            1500,
            0.06,
        ],
        'icon-allow-overlap': true,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const devicesDeficit = {
    id: 'Текущий дефицит самокатов',
    label: defineMessage({ message: 'Текущий дефицит самокатов' }),
    type: 'heatmap',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.deficit_excess_mvt.json`,
    },
    maxzoom: 18,
    'source-layer': 'martin.deficit_excess_mvt',
    layout: { visibility: 'none' },
    paint: {
        'heatmap-weight': [
            'interpolate',
            [ 'exponential', 0.81 ],
            [ 'get', 'deficit_excess_count' ],
            -7,
            10,
            0,
            0,
            58,
            0,
        ],
        'heatmap-intensity': [
            'interpolate',
            [ 'exponential', 0.66 ],
            [ 'zoom' ],
            9,
            0,
            22,
            1,
        ],
        'heatmap-color': [
            'interpolate',
            [ 'linear' ],
            [ 'heatmap-density' ],
            0.2,
            'hsla(301, 98%, 24%, 0)',
            0.3,
            '#9CECFB',
            0.5,
            '#65C7F7',
            1,
            '#0052D4',
        ],
        'heatmap-radius': 20,
        'heatmap-opacity': 0.4,
    },
    filter: [
        'any',
        [
            '==',
            [ 'to-string', [ 'get', 'status' ] ],
            'deficit',
        ],
        [
            'all',
            [ '==', [ 'get', 'actual_count' ], 0 ],
            [ '==', [ 'to-string', [ 'get', 'status' ] ], 'undefined' ],
        ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const devicesExcess = {
    id: 'Текущий избыток самокатов',
    label: defineMessage({ message: 'Текущий избыток самокатов' }),
    type: 'heatmap',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.deficit_excess_mvt.json`,
    },
    maxzoom: 18,
    'source-layer': 'martin.deficit_excess_mvt',
    layout: { visibility: 'none' },
    paint: {
        'heatmap-weight': [
            'interpolate',
            [ 'exponential', 0.76 ],
            [ 'get', 'deficit_excess_count' ],
            -7,
            0,
            2,
            0,
            58,
            3,
        ],
        'heatmap-intensity': [
            'interpolate',
            [ 'exponential', 0.66 ],
            [ 'zoom' ],
            9,
            0,
            22,
            1,
        ],
        'heatmap-color': [
            'interpolate',
            [ 'linear' ],
            [ 'heatmap-density' ],
            0.2,
            'hsla(301, 98%, 24%, 0)',
            0.3,
            '#ffa3e2',
            0.5,
            '#FF0099',
            1.1,
            '#7d0055',
        ],
        'heatmap-radius': 20,
        'heatmap-opacity': 0.4,
    },
    filter: [ '==', [ 'to-string', [ 'get', 'status' ] ], 'excess' ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const devicesBalanceAtOperationAreas = {
    id: 'Баланс самокатов в операционных зонах',
    label: defineMessage({ message: 'Баланс самокатов в операционных зонах' }),
    type: 'fill',
    'source-layer': 'martin.deficit_excess_operation_area_agg_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.deficit_excess_operation_area_agg_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'deficit_excess_count' ],
            -50,
            '#30123b',
            -20,
            '#4099ff',
            0,
            '#46f884',
            20,
            '#e1dd37',
            50,
            '#ef5811',
        ],
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const devicesBalanceAtOperationAreasAccordingToLot = {
    id: 'Приведенный к 1 парковке баланс самокатов в операционных зонах',
    label: defineMessage({ message: 'Приведенный к 1 парковке баланс самокатов в операционных зонах' }),
    type: 'fill',
    'source-layer': 'martin.deficit_excess_operation_area_agg_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.deficit_excess_operation_area_agg_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'deficit_excess_count_by_parking' ],
            -0.5,
            '#30123b',
            -0.25,
            '#4099ff',
            0,
            '#46f884',
            0.25,
            '#e1dd37',
            0.5,
            '#ef5811',
        ],
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const adminBikePath = {
    id: 'Велодорожки',
    label: defineMessage({ message: 'Велодорожки' }),
    type: 'line',
    'source-layer': 'martin.bicycle_routes_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.bicycle_routes_mvt.json`,
    },
    layout: { visibility: 'none' }, // 03fc98
    paint: {
        // DA-15814: цвет по паре source + type — линии от города, велодорожки OSM
        // и велополосы OSM (cycleway=* на проезжей части).
        'line-color': [
            'case',
            [ '==', [ 'get', 'source' ], 'administration' ], '#50C1E9',
            [ '==', [ 'get', 'type' ], 'lane' ], '#1B7A3E',
            '#03fc98',
        ],
        'line-width': 2,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const adminSupposedBikePath = {
    id: 'Предлагаемые велодорожки',
    label: defineMessage({ message: 'Предлагаемые велодорожки' }),
    type: 'line',
    'source-layer': 'martin.bicycle_routes_suggest_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.bicycle_routes_suggest_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': {
            base: 1,
            type: 'categorical',
            property: 'stage',
            stops: [
                [ 1, '#ffff0d' ],
                [ 2, '#50C1E9' ],
                [ 3, '#dbd3ba' ],
            ],
            default: '#3d5cf6',
        },
        'line-opacity': {
            base: 1,
            type: 'categorical',
            property: 'stage',
            stops: [
                [ 1, 1 ],
                [ 2, 0.8 ],
                [ 3, 0.4 ],
            ],
            default: 1,
        },
        'line-width': 3,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const AggregatedTripsAugustH3 = {
    id: 'Агрегированные поездки (август)',
    label: defineMessage({
        message: 'Агрегированные поездки (август)',
        description: 'Aggregated trips (August) - H3 hexagonal grid layer with dynamic zoom and year selection',
    }),
    // Заголовок экспорта (DA-15019): подставляем выбранный год (mapFilters.aggTripsAugustYear).
    exportTitle: ({ filters }) => {
        const year = filters.aggTripsAugustYear;

        return year ? i18n._(defineMessage({ message: 'Поездки за август {year}' }), { year }) : '';
    },
    type: 'fill',
    minzoom: 0,
    maxzoom: 24,
    'source-layer': 'martin.hist_aggregated_tracks_mvt',
    source: {
        id: 'aggregated_trips_august_h3_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.hist_aggregated_tracks_mvt.json`,
        requiredQueryParams: [ 'h3_zoom' ],
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            // Динамическое масштабирование: trips_2025 * 7^(zoom - 10)
            // Это компенсирует разницу в размере H3 ячеек на разных zoom уровнях
            [ '*',
                [ 'to-number', [ 'get', 'trips_2025' ] ],
                [ '^', 7, [ '-', [ 'to-number', [ 'get', 'zoom' ] ], 10 ] ],
            ],
            0,
            'hsla(240, 100%, 27%, 0)',
            100,
            'hsla(240, 100%, 27%, 0.4)',
            300,
            '#0d0887',
            1000,
            '#6b00a8',
            4000,
            '#e16461',
            8000,
            '#fca736',
            25000,
            '#f0f921',
        ],
        'fill-opacity': 0.6,
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
    },
    legend: AGGREGATED_TRIPS_AUGUST_H3_GRADIENT_LEGEND,
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const aggStartAugust = {
    id: 'Агрегированные старты (август)',
    label: defineMessage({ message: 'Агрегированные старты (август)' }),
    type: 'fill',
    minzoom: 8,
    'source-layer': 'martin.agg_start_august_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.agg_start_august_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'n_trips_2026' ] ],
            9,
            '#0d0887',
            100,
            '#6b00a8',
            300,
            '#e16461',
            900,
            '#fca736',
            1000,
            '#f0f921',
        ],
        'fill-opacity': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'n_trips_2026' ] ],
            10,
            0.4,
            100,
            0.5,
            300,
            0.6,
            900,
            0.6,
            1000,
            0.6,
        ],
    },
    filter: [ 'all',
        [ '>=', [ 'to-number', [ 'get', 'n_trips_2026' ] ], 2  ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const aggFinishAugust = {
    id: 'Агрегированные финиши (август)',
    label: defineMessage({ message: 'Агрегированные финиши (август)' }),
    type: 'fill',
    minzoom: 8,
    'source-layer': 'martin.agg_finish_august_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.agg_finish_august_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'n_trips_2026' ] ],
            10,
            '#0d0887',
            100,
            '#6b00a8',
            300,
            '#e16461',
            900,
            '#fca736',
            1000,
            '#f0f921',
        ],
        'fill-opacity': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'n_trips_2026' ] ],
            10,
            0.4,
            100,
            0.5,
            300,
            0.6,
            900,
            0.6,
            1000,
            0.6,
        ],
    },
    filter: [ 'all',
        [ '>=', [ 'to-number', [ 'get', 'n_trips_2026' ] ], 2  ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const trackDiffStartFinishAugust = {
    id: 'Агрегированая разница стартов и финишей (август)',
    label: defineMessage({ message: 'Агрегированая разница стартов и финишей (август)' }),
    type: 'fill',
    'source-layer': 'martin.agg_diff_august_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.agg_diff_august_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 8,
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'diff_perc_2026' ] ],
            -100,
            '#d7191c',
            -50,
            '#f17c4a',
            -15,
            '#ffffbf',
            0,
            '#ffffbf',
            15,
            '#c7e9ad',
            50,
            '#80bfac',
            100,
            '#2b83ba',
        ],
        'fill-opacity': 0.5,
    },
    filter: [ 'all',
        [ '>=', [ 'to-number', [ 'get', 'starts_2026' ] ], 1 ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// Прозрачная заливка для кликов внутри зон катания конкурентов
// case-выражение Mapbox: цвет по оператору (Yandex..Voi, включая SmartBike),
// fallback — серый. Цвета берутся из functionalAreasYandexUrentTypes.
const operatorColorCase = (prop = 'operator') => [
    'case',
    [ '==', [ 'get', prop ], 'Yandex' ], V.functionalAreasYandexUrentTypes[0].fill,
    [ '==', [ 'get', prop ], 'Urent' ], V.functionalAreasYandexUrentTypes[1].fill,
    [ '==', [ 'get', prop ], 'Jet' ], V.functionalAreasYandexUrentTypes[2].fill,
    [ '==', [ 'get', prop ], 'Lime' ], V.functionalAreasYandexUrentTypes[3].fill,
    [ '==', [ 'get', prop ], 'Bolt' ], V.functionalAreasYandexUrentTypes[4].fill,
    [ '==', [ 'get', prop ], 'SmartBike' ], V.functionalAreasYandexUrentTypes[5].fill,
    [ '==', [ 'get', prop ], 'Voi' ], V.functionalAreasYandexUrentTypes[6].fill,
    [ '==', [ 'get', prop ], 'LetsGo' ], V.functionalAreasYandexUrentTypes[7].fill,
    [ '==', [ 'get', prop ], 'Eleven' ], V.functionalAreasYandexUrentTypes[8].fill,
    [ '==', [ 'get', prop ], 'Lite' ], V.functionalAreasYandexUrentTypes[9].fill,
    [ '==', [ 'get', prop ], 'Yango' ], V.functionalAreasYandexUrentTypes[10].fill,
    [ '==', [ 'get', prop ], 'Yandex/Yango' ], V.functionalAreasYandexUrentTypes[11].fill,
    '#808080',
];
const ridingZonesCompetitorsFill = {
    id: 'Зоны катания (конкуренты)_fill',
    label: defineMessage({ message: 'Зоны катания (конкуренты)_fill' }),
    type: 'fill',
    'source-layer': 'martin.areas_competitors_mvt',
    // Используем тот же source что и родительский line-слой (по ID, не объектом),
    // чтобы не создавать дублирующий источник данных
    source: { id: 'Зоны катания (конкуренты)_source', type: 'vector', url: `${V.API_GW_URL}/rpc/martin.areas_competitors_mvt.json` },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': 'rgba(0,0,0,0)',
        'fill-opacity': 0,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'company' ] ], [ 'none' ], true, false ],
};
// Зоны катания конкурентов (бывший functionalAreasYandexUrent)
const ridingZonesCompetitors = {
    id: 'Зоны катания (конкуренты)',
    label: defineMessage({ message: 'Зоны катания' }),
    exportTitle: defineMessage({ message: 'Зоны катания по операторам' }),
    type: 'line',
    'source-layer': 'martin.areas_competitors_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.areas_competitors_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': {
            base: 1,
            type: 'categorical',
            property: 'company',
            stops: [
                [
                    'Yandex',
                    V.functionalAreasYandexUrentTypes[0].fill,
                ],
                [
                    'Urent',
                    V.functionalAreasYandexUrentTypes[1].fill,
                ],
                [
                    'Jet',
                    V.functionalAreasYandexUrentTypes[2].fill,
                ],
                [
                    'Lime',
                    V.functionalAreasYandexUrentTypes[3].fill,
                ],
                [
                    'Bolt',
                    V.functionalAreasYandexUrentTypes[4].fill,
                ],
                [
                    'Voi',
                    V.functionalAreasYandexUrentTypes[6].fill,
                ],
                [
                    'LetsGo',
                    V.functionalAreasYandexUrentTypes[7].fill,
                ],
                [
                    'Eleven',
                    V.functionalAreasYandexUrentTypes[8].fill,
                ],
                [
                    'Lite',
                    V.functionalAreasYandexUrentTypes[9].fill,
                ],
                [
                    'Yango',
                    V.functionalAreasYandexUrentTypes[10].fill,
                ],
                [
                    'Yandex/Yango',
                    V.functionalAreasYandexUrentTypes[11].fill,
                ],
            ],
            'default': '#3d5cf6',
        },
        'line-width': 3,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'company' ] ], [ 'none' ], true, false ],
    additionalLayers: [ ridingZonesCompetitorsFill ],
    availableFor: [ V.SUPER_ADMIN ],
};
// DA-14686: Изменения зон конкурентов — заливка (полупрозрачная, по типу изменения)
const competitorsZonesChangesFill = {
    id: 'Изменения зон (конкуренты)_fill',
    label: defineMessage({ message: 'Изменения зон (конкуренты)_fill' }),
    type: 'fill',
    'source-layer': 'martin.competitors_zones_changes_mvt',
    source: { id: 'Изменения зон (конкуренты)_source', type: 'vector', url: `${V.API_GW_URL}/rpc/martin.competitors_zones_changes_mvt.json` },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'match', [ 'get', 'change_type' ],
            'new',            '#22c55e',
            'deleted',        '#ef4444',
            'geom_changed',   '#f97316',
            'attrs_changed',  '#ffffff',
            '#3d5cf6',
        ],
        'fill-opacity': 0.15,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'company' ] ], [ 'none' ], true, false ],
};
// DA-14686: Изменения зон конкурентов — предыдущая геометрия (жёлтый пунктир)
const competitorsZonesChangesPrevGeom = {
    id: 'Изменения зон (конкуренты)_prev',
    label: defineMessage({ message: 'Изменения зон (конкуренты)_prev' }),
    type: 'line',
    'source-layer': 'martin.competitors_zones_changes_mvt',
    source: { id: 'Изменения зон (конкуренты)_source', type: 'vector', url: `${V.API_GW_URL}/rpc/martin.competitors_zones_changes_mvt.json` },
    layout: { visibility: 'none' },
    paint: {
        'line-color': '#fbbf24',
        'line-width': 1,
        'line-dasharray': [ 4, 3 ],
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'company' ] ], [ 'none' ], true, false ],
};
// DA-14686: Изменения зон конкурентов — основной контур (цвет по типу изменения)
const competitorsZonesChangesOutline = {
    id: 'Изменения зон (конкуренты)',
    label: defineMessage({ message: 'Изменения зон конкурентов' }),
    exportTitle: defineMessage({ message: 'Изменения зон по операторам' }),
    type: 'line',
    'source-layer': 'martin.competitors_zones_changes_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_zones_changes_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': [
            'match', [ 'get', 'change_type' ],
            'new',            '#22c55e',
            'deleted',        '#ef4444',
            'geom_changed',   '#f97316',
            'attrs_changed',  '#ffffff',
            '#3d5cf6',
        ],
        'line-width': [
            'match', [ 'get', 'change_type' ],
            'attrs_changed', 1,
            3,
        ],
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'company' ] ], [ 'none' ], true, false ],
    additionalLayers: [ competitorsZonesChangesFill, competitorsZonesChangesPrevGeom ],
    availableFor: [ V.SUPER_ADMIN ],
};
const functionalAreas = {
    id: 'Функциональные зоны',
    label: defineMessage({ message: 'Функциональные зоны' }),
    type: 'line',
    'source-layer': 'martin.functional_areas_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.functional_areas_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': {
            base: 1,
            type: 'categorical',
            property: 'type',
            stops: [
                [
                    V.FORBIDDEN,
                    'red',
                ],
                [
                    V.LIMITED,
                    'green',
                ],
                [
                    V.PROPOSED_PERMITTED,
                    '#00ae9d',
                ],
                [
                    V.PERMITTED,
                    'yellow',
                ],
                [
                    V.PERMITTED_FICTITIOUS,
                    'yellow',
                ],
                [
                    V.PROPOSED_FORBIDDEN,
                    'pink',
                ],
            ],
            'default': '#3d5cf6',
        },
        'line-width': 3,
        'line-dasharray': [
            'case',
            // Либо фиктивная, либо PROPOSED - рисуем пунктир
            [ 'any',
                [ '==', [ 'get', 'is_fictitious' ], 1 ],
                [ 'match', [ 'get', 'type' ],
                    [ V.PROPOSED_PERMITTED, V.PROPOSED_FORBIDDEN, V.PERMITTED_FICTITIOUS ],
                    true,
                    false,
                ],
            ],
            [ 'literal', [ 0.8, 1.5 ] ],

            // Иначе - сплошная линия
            [ 'literal', [ 1, 0 ] ],
        ],
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'type' ] ], [ V.LIMITED, V.FORBIDDEN ], true, false ],
    availableFor: [],
};
// === RESTRICTION ZONES V2 - 11 слоёв (5 fill + 5 line + 1 badge + 1 speed_text) ===
// Общие параметры источника — id обязателен чтобы все слои делили ОДИН источник
const V2_SOURCE = {
    id: 'martin.functional_areas_mvt2',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.functional_areas_mvt2.json`,
};
const V2_SOURCE_LAYER = 'martin.functional_areas_mvt2';
// Фабрика слоя restriction zones V2: общий source/source-layer/layout/availableFor,
// различаются только id, type (fill|line), paint и filter.
const v2Layer = (id, type, paint, filter) => ({
    id,
    type,
    'source-layer': V2_SOURCE_LAYER,
    source: V2_SOURCE,
    layout: { visibility: 'none' },
    paint,
    filter,
    availableFor: [ V.SUPER_ADMIN ],
});
// --- Фильтры по вариантам зон (type + признак geom_type/fictitious/invisible/proposed) ---
const polygonFilter = type => [ 'all', [ '==', [ 'to-string', [ 'get', 'type' ] ], type ], [ '==', [ 'get', 'geom_type' ], 'polygon' ] ];
// Обычная обводка: только не-fictitious и не-invisible зоны
const normalLineFilter = type => [
    'all',
    [ '==', [ 'to-string', [ 'get', 'type' ] ], type ],
    [ '==', [ 'get', 'geom_type' ], 'outer_boundary' ],
    [ 'match', [ 'to-string', [ 'get', 'fictitious' ] ], [ 'true', '1' ], false, true ],
    [ 'match', [ 'to-string', [ 'get', 'invisible' ] ], [ 'true', '1' ], false, true ],
];
// proposed=true → другой цвет обводки
const proposedLineFilter = type => [
    'all',
    [ '==', [ 'to-string', [ 'get', 'type' ] ], type ],
    [ '==', [ 'get', 'geom_type' ], 'outer_boundary' ],
    [ 'match', [ 'to-string', [ 'get', 'proposed' ] ], [ 'true', '1' ], true, false ],
    [ 'match', [ 'to-string', [ 'get', 'fictitious' ] ], [ 'true', '1' ], false, true ],
    [ 'match', [ 'to-string', [ 'get', 'invisible' ] ], [ 'true', '1' ], false, true ],
];
// fictitious=true: dot-hatching поверх fill + сплошная обводка
const fictitiousFillFilter = type => [
    'all',
    [ '==', [ 'to-string', [ 'get', 'type' ] ], type ],
    [ 'match', [ 'to-string', [ 'get', 'fictitious' ] ], [ 'true', '1' ], true, false ],
    [ '==', [ 'get', 'geom_type' ], 'polygon' ],
];
const fictitiousLineFilter = type => [
    'all',
    [ '==', [ 'to-string', [ 'get', 'type' ] ], type ],
    [ 'match', [ 'to-string', [ 'get', 'fictitious' ] ], [ 'true', '1' ], true, false ],
    [ '==', [ 'get', 'geom_type' ], 'outer_boundary' ],
];
// invisible=true: crosshatch fill + граница
const invisibleFillFilter = type => [
    'all',
    [ '==', [ 'to-string', [ 'get', 'type' ] ], type ],
    [ 'match', [ 'to-string', [ 'get', 'invisible' ] ], [ 'true', '1' ], true, false ],
    [ '==', [ 'get', 'geom_type' ], 'polygon' ],
];
const invisibleLineFilter = type => [
    'all',
    [ '==', [ 'to-string', [ 'get', 'type' ] ], type ],
    [ 'match', [ 'to-string', [ 'get', 'invisible' ] ], [ 'true', '1' ], true, false ],
    [ '==', [ 'get', 'geom_type' ], 'outer_boundary' ],
];
// inner_boundary: внутренние контуры / дырки, толщина 1
const innerBoundaryFilter = type => [
    'all',
    [ '==', [ 'to-string', [ 'get', 'type' ] ], type ],
    [ '==', [ 'get', 'geom_type' ], 'inner_boundary' ],
];
// --- FILL слои (снизу вверх: limited → forbidden → permitted → stop_limit → speed) ---
const restrictionZonesV2LimitedFill = v2Layer('Зоны ограничений V2_limited', 'fill', RESTRICTION_ZONES_V2_LIMITED_MAPBOX_PAINT_FILL, polygonFilter('LIMITED'));
const restrictionZonesV2ForbiddenFill = v2Layer('Зоны ограничений V2_forbidden', 'fill', RESTRICTION_ZONES_V2_FORBIDDEN_MAPBOX_PAINT_FILL, polygonFilter('FORBIDDEN'));
const restrictionZonesV2PermittedFill = v2Layer('Зоны ограничений V2_permitted', 'fill', RESTRICTION_ZONES_V2_PERMITTED_MAPBOX_PAINT_FILL, polygonFilter('PERMITTED'));
const restrictionZonesV2StopLimitFill = v2Layer('Зоны ограничений V2_stop_limit', 'fill', RESTRICTION_ZONES_V2_STOP_LIMIT_MAPBOX_PAINT_FILL, polygonFilter('STOP_LIMIT'));
const restrictionZonesV2SpeedFill = v2Layer('Зоны ограничений V2_speed', 'fill', RESTRICTION_ZONES_V2_SPEED_MAPBOX_PAINT_FILL, polygonFilter('SPEED_LIMIT'));
// --- LINE слои (обычная обводка) ---
const restrictionZonesV2LimitedLine = v2Layer('Зоны ограничений V2_limited_lines', 'line', RESTRICTION_ZONES_V2_LIMITED_MAPBOX_PAINT_LINE, normalLineFilter('LIMITED'));
const restrictionZonesV2ForbiddenLine = v2Layer('Зоны ограничений V2_forbidden_lines', 'line', RESTRICTION_ZONES_V2_FORBIDDEN_MAPBOX_PAINT_LINE, normalLineFilter('FORBIDDEN'));
const restrictionZonesV2PermittedLine = v2Layer('Зоны ограничений V2_permitted_lines', 'line', RESTRICTION_ZONES_V2_PERMITTED_MAPBOX_PAINT_LINE, normalLineFilter('PERMITTED'));
const restrictionZonesV2StopLimitLine = v2Layer('Зоны ограничений V2_stop_limit_lines', 'line', RESTRICTION_ZONES_V2_STOP_LIMIT_MAPBOX_PAINT_LINE, normalLineFilter('STOP_LIMIT'));
const restrictionZonesV2SpeedLine = v2Layer('Зоны ограничений V2_speed_lines', 'line', RESTRICTION_ZONES_V2_SPEED_MAPBOX_PAINT_LINE, normalLineFilter('SPEED_LIMIT'));
// --- PROPOSED LINE слои (proposed=true) ---
const restrictionZonesV2ForbiddenProposedLine = v2Layer('Зоны ограничений V2_forbidden_proposed_line', 'line', RESTRICTION_ZONES_V2_FORBIDDEN_PROPOSED_PAINT_LINE, proposedLineFilter('FORBIDDEN'));
const restrictionZonesV2PermittedProposedLine = v2Layer('Зоны ограничений V2_permitted_proposed_line', 'line', RESTRICTION_ZONES_V2_PERMITTED_PROPOSED_PAINT_LINE, proposedLineFilter('PERMITTED'));
// --- FICTITIOUS FILL слои (dot-hatching поверх fill) ---
const restrictionZonesV2LimitedFictitiousFill = v2Layer('Зоны ограничений V2_limited_fictitious', 'fill', RESTRICTION_ZONES_V2_LIMITED_FICTITIOUS_PAINT_FILL, fictitiousFillFilter('LIMITED'));
const restrictionZonesV2ForbiddenFictitiousFill = v2Layer('Зоны ограничений V2_forbidden_fictitious', 'fill', RESTRICTION_ZONES_V2_FORBIDDEN_FICTITIOUS_PAINT_FILL, fictitiousFillFilter('FORBIDDEN'));
const restrictionZonesV2PermittedFictitiousFill = v2Layer('Зоны ограничений V2_permitted_fictitious', 'fill', RESTRICTION_ZONES_V2_PERMITTED_FICTITIOUS_PAINT_FILL, fictitiousFillFilter('PERMITTED'));
const restrictionZonesV2StopLimitFictitiousFill = v2Layer('Зоны ограничений V2_stop_limit_fictitious', 'fill', RESTRICTION_ZONES_V2_STOP_LIMIT_FICTITIOUS_PAINT_FILL, fictitiousFillFilter('STOP_LIMIT'));
const restrictionZonesV2SpeedFictitiousFill = v2Layer('Зоны ограничений V2_speed_fictitious', 'fill', RESTRICTION_ZONES_V2_SPEED_FICTITIOUS_PAINT_FILL, fictitiousFillFilter('SPEED_LIMIT'));
// --- FICTITIOUS LINE слои (сплошная обводка поверх dot-fill) ---
const restrictionZonesV2LimitedFictitiousLine = v2Layer('Зоны ограничений V2_limited_fictitious_line', 'line', RESTRICTION_ZONES_V2_LIMITED_FICTITIOUS_PAINT_LINE, fictitiousLineFilter('LIMITED'));
const restrictionZonesV2ForbiddenFictitiousLine = v2Layer('Зоны ограничений V2_forbidden_fictitious_line', 'line', RESTRICTION_ZONES_V2_FORBIDDEN_FICTITIOUS_PAINT_LINE, fictitiousLineFilter('FORBIDDEN'));
const restrictionZonesV2PermittedFictitiousLine = v2Layer('Зоны ограничений V2_permitted_fictitious_line', 'line', RESTRICTION_ZONES_V2_PERMITTED_FICTITIOUS_PAINT_LINE, fictitiousLineFilter('PERMITTED'));
const restrictionZonesV2StopLimitFictitiousLine = v2Layer('Зоны ограничений V2_stop_limit_fictitious_line', 'line', RESTRICTION_ZONES_V2_STOP_LIMIT_FICTITIOUS_PAINT_LINE, fictitiousLineFilter('STOP_LIMIT'));
const restrictionZonesV2SpeedFictitiousLine = v2Layer('Зоны ограничений V2_speed_fictitious_line', 'line', RESTRICTION_ZONES_V2_SPEED_FICTITIOUS_PAINT_LINE, fictitiousLineFilter('SPEED_LIMIT'));
// --- INVISIBLE FILL слои (crosshatch паттерн) ---
const restrictionZonesV2LimitedInvisibleFill = v2Layer('Зоны ограничений V2_limited_invisible', 'fill', { 'fill-pattern': 'crosshatch-pattern-limited', 'fill-opacity': 1 }, invisibleFillFilter('LIMITED'));
const restrictionZonesV2ForbiddenInvisibleFill = v2Layer('Зоны ограничений V2_forbidden_invisible', 'fill', { 'fill-pattern': 'crosshatch-pattern-forbidden', 'fill-opacity': 1 }, invisibleFillFilter('FORBIDDEN'));
const restrictionZonesV2PermittedInvisibleFill = v2Layer('Зоны ограничений V2_permitted_invisible', 'fill', { 'fill-pattern': 'crosshatch-pattern-permitted', 'fill-opacity': 1 }, invisibleFillFilter('PERMITTED'));
const restrictionZonesV2StopLimitInvisibleFill = v2Layer('Зоны ограничений V2_stop_limit_invisible', 'fill', { 'fill-pattern': 'crosshatch-pattern-stop_limit', 'fill-opacity': 1 }, invisibleFillFilter('STOP_LIMIT'));
const restrictionZonesV2SpeedInvisibleFill = v2Layer('Зоны ограничений V2_speed_invisible', 'fill', { 'fill-pattern': 'crosshatch-pattern-speed', 'fill-opacity': 1 }, invisibleFillFilter('SPEED_LIMIT'));
// --- INVISIBLE LINE слои (границы invisible=true зон, цвет обводки = обычный) ---
const restrictionZonesV2LimitedInvisibleLine = v2Layer('Зоны ограничений V2_limited_invisible_line', 'line', RESTRICTION_ZONES_V2_LIMITED_MAPBOX_PAINT_LINE, invisibleLineFilter('LIMITED'));
const restrictionZonesV2ForbiddenInvisibleLine = v2Layer('Зоны ограничений V2_forbidden_invisible_line', 'line', RESTRICTION_ZONES_V2_FORBIDDEN_MAPBOX_PAINT_LINE, invisibleLineFilter('FORBIDDEN'));
const restrictionZonesV2PermittedInvisibleLine = v2Layer('Зоны ограничений V2_permitted_invisible_line', 'line', RESTRICTION_ZONES_V2_PERMITTED_MAPBOX_PAINT_LINE, invisibleLineFilter('PERMITTED'));
const restrictionZonesV2StopLimitInvisibleLine = v2Layer('Зоны ограничений V2_stop_limit_invisible_line', 'line', RESTRICTION_ZONES_V2_STOP_LIMIT_MAPBOX_PAINT_LINE, invisibleLineFilter('STOP_LIMIT'));
const restrictionZonesV2SpeedInvisibleLine = v2Layer('Зоны ограничений V2_speed_invisible_line', 'line', RESTRICTION_ZONES_V2_SPEED_MAPBOX_PAINT_LINE, invisibleLineFilter('SPEED_LIMIT'));
// --- INNER BOUNDARY LINE слои (внутренние контуры / дырки) ---
const restrictionZonesV2LimitedInnerLine = v2Layer('Зоны ограничений V2_limited_inner_line', 'line', RESTRICTION_ZONES_V2_LIMITED_INNER_PAINT_LINE, innerBoundaryFilter('LIMITED'));
const restrictionZonesV2ForbiddenInnerLine = v2Layer('Зоны ограничений V2_forbidden_inner_line', 'line', RESTRICTION_ZONES_V2_FORBIDDEN_INNER_PAINT_LINE, innerBoundaryFilter('FORBIDDEN'));
const restrictionZonesV2PermittedInnerLine = v2Layer('Зоны ограничений V2_permitted_inner_line', 'line', RESTRICTION_ZONES_V2_PERMITTED_INNER_PAINT_LINE, innerBoundaryFilter('PERMITTED'));
const restrictionZonesV2StopLimitInnerLine = v2Layer('Зоны ограничений V2_stop_limit_inner_line', 'line', RESTRICTION_ZONES_V2_STOP_LIMIT_INNER_PAINT_LINE, innerBoundaryFilter('STOP_LIMIT'));
const restrictionZonesV2SpeedInnerLine = v2Layer('Зоны ограничений V2_speed_inner_line', 'line', RESTRICTION_ZONES_V2_SPEED_INNER_PAINT_LINE, innerBoundaryFilter('SPEED_LIMIT'));
// --- BADGE — два слоя: белая подложка + цветной щит поверх ---
const badgeFilter = [
    'all',
    [ '==', [ 'get', 'geom_type' ], 'centroid' ],
    [ '==', [ 'get', 'badge' ], true ],
    [ '!=', [ 'get', 'invisible' ], true ],
];
const restrictionZonesV2BadgeBg = {
    id: 'Зоны ограничений V2_badge_bg',
    type: 'symbol',
    'source-layer': V2_SOURCE_LAYER,
    source: V2_SOURCE,
    layout: {
        visibility: 'none',
        'icon-image': 'shield-icon',
        'icon-size': 0.48,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
    },
    paint: {
        'icon-color': '#ffffff',
        'icon-opacity': 1,
    },
    filter: badgeFilter,
    availableFor: [ V.SUPER_ADMIN ],
};
const restrictionZonesV2Badge = {
    id: 'Зоны ограничений V2_badge',
    type: 'symbol',
    'source-layer': V2_SOURCE_LAYER,
    source: V2_SOURCE,
    layout: {
        visibility: 'none',
        'icon-image': 'shield-icon',
        'icon-size': 0.4,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
    },
    paint: {
        'icon-color': [
            'match',
            [ 'get', 'type' ],
            'LIMITED', RESTRICTION_ZONES_V2_RIDING_STYLE.zones[0].color,
            'FORBIDDEN', RESTRICTION_ZONES_V2_RIDING_STYLE.zones[1].color,
            'PERMITTED', RESTRICTION_ZONES_V2_PARKING_STYLE.zones[0].color,
            'STOP_LIMIT', RESTRICTION_ZONES_V2_PARKING_STYLE.zones[1].color,
            'SPEED_LIMIT', RESTRICTION_ZONES_V2_SPEED_STYLE.zones[0].color,
            '#999999',
        ],
        'icon-opacity': 1,
    },
    filter: badgeFilter,
    availableFor: [ V.SUPER_ADMIN ],
};
// --- SPEED TEXT — подписи скорости ---
const restrictionZonesV2SpeedLabel = {
    id: 'Зоны ограничений V2_speed_text',
    type: 'symbol',
    'source-layer': V2_SOURCE_LAYER,
    source: V2_SOURCE,
    layout: {
        visibility: 'none',
        'text-field': [
            'concat',
            [
                'case',
                [
                    'all',
                    [ 'has', 'speed_limit' ],
                    [ '>', [ 'to-number', [ 'get', 'speed_limit' ], 0 ], 0 ],
                ],
                [ 'to-string', [ 'get', 'speed_limit' ] ],
                '-',
            ],
            ' км/ч',
        ],
        'text-size': 10,
        'text-anchor': 'center',
    },
    paint: {
        'text-halo-color': '#fff',
        'text-halo-width': 2,
    },
    filter: [
        'all',
        [ '==', [ 'get', 'type' ], 'SPEED_LIMIT' ],
        [ '==', [ 'get', 'geom_type' ], 'polygon' ],
    ],
    minzoom: 8,
    availableFor: [ V.SUPER_ADMIN ],
};
// Переопределение стиля слоя «Зонирование» ТОЛЬКО при экспорте в растр:
// фиктивные и невидимые зоны на печати выглядят как обычные, без спецмаркеров.
//   - фиктивные: единственное отличие — пунктирная обводка (line-dasharray),
//     заливка совпадает с обычной; убираем пунктир → сплошная обводка;
//   - невидимые: поверх обычной заливки лежит crosshatch-паттерн, обводка уже
//     обычная сплошная; прячем crosshatch-заливку (fill-opacity: 0) → остаётся
//     базовая заливка + обычная обводка.
// Ключи — id под-слоёв Mapbox; на карту/сайдбар не влияет (см. exportStyles.js).
const RESTRICTION_ZONES_V2_EXPORT_STYLE = {
    // Фиктивные обводки → сплошные (снимаем пунктир).
    [restrictionZonesV2LimitedFictitiousLine.id]: { paint: { 'line-dasharray': [ 1, 0 ] } },
    [restrictionZonesV2ForbiddenFictitiousLine.id]: { paint: { 'line-dasharray': [ 1, 0 ] } },
    [restrictionZonesV2PermittedFictitiousLine.id]: { paint: { 'line-dasharray': [ 1, 0 ] } },
    [restrictionZonesV2StopLimitFictitiousLine.id]: { paint: { 'line-dasharray': [ 1, 0 ] } },
    [restrictionZonesV2SpeedFictitiousLine.id]: { paint: { 'line-dasharray': [ 1, 0 ] } },
    // Невидимые crosshatch-заливки → скрыть (остаётся базовая заливка).
    [restrictionZonesV2LimitedInvisibleFill.id]: { paint: { 'fill-opacity': 0 } },
    [restrictionZonesV2ForbiddenInvisibleFill.id]: { paint: { 'fill-opacity': 0 } },
    [restrictionZonesV2PermittedInvisibleFill.id]: { paint: { 'fill-opacity': 0 } },
    [restrictionZonesV2StopLimitInvisibleFill.id]: { paint: { 'fill-opacity': 0 } },
    [restrictionZonesV2SpeedInvisibleFill.id]: { paint: { 'fill-opacity': 0 } },
};
const restrictionZonesV2 = {
    id: 'Зонирование',
    label: defineMessage({ message: 'Зонирование' }),
    type: 'fill',
    'source-layer': V2_SOURCE_LAYER,
    source: V2_SOURCE,
    layout: { visibility: 'none' },
    paint: {
        'fill-color': '#cccccc',
        'fill-opacity': 0,
    },
    legend: RESTRICTION_ZONES_V2_LEGEND,
    exportStyle: RESTRICTION_ZONES_V2_EXPORT_STYLE,
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    // 12 слоёв: 5 fill (снизу) + 5 line + 1 badge + 1 speed_text (сверху)
    // FORBIDDEN идёт последним среди fill и line — рисуется поверх всех прочих зон
    additionalLayers: [
        // ── FILLS (снизу вверх) ──────────────────────────────────────────────
        restrictionZonesV2LimitedFill,
        restrictionZonesV2PermittedFill,
        restrictionZonesV2StopLimitFill,
        restrictionZonesV2SpeedFill,
        restrictionZonesV2ForbiddenFill,
        // ── LINES — LIMITED, PERMITTED, SPEED (низкий приоритет) ────────────
        restrictionZonesV2LimitedLine,
        restrictionZonesV2PermittedLine,
        restrictionZonesV2SpeedLine,
        // ── FICTITIOUS FILL (dot-hatching) ───────────────────────────────────
        restrictionZonesV2LimitedFictitiousFill,
        restrictionZonesV2PermittedFictitiousFill,
        restrictionZonesV2StopLimitFictitiousFill,
        restrictionZonesV2SpeedFictitiousFill,
        restrictionZonesV2ForbiddenFictitiousFill,
        // ── FICTITIOUS LINES — LIMITED, PERMITTED, SPEED ─────────────────────
        restrictionZonesV2LimitedFictitiousLine,
        restrictionZonesV2PermittedFictitiousLine,
        restrictionZonesV2SpeedFictitiousLine,
        // ── INVISIBLE FILL (crosshatch) ───────────────────────────────────────
        restrictionZonesV2LimitedInvisibleFill,
        restrictionZonesV2PermittedInvisibleFill,
        restrictionZonesV2SpeedInvisibleFill,
        // ── INVISIBLE LINES — LIMITED, PERMITTED, SPEED ──────────────────────
        restrictionZonesV2LimitedInvisibleLine,
        restrictionZonesV2PermittedInvisibleLine,
        restrictionZonesV2SpeedInvisibleLine,
        // ── INNER BOUNDARY — LIMITED, PERMITTED, SPEED ───────────────────────
        restrictionZonesV2LimitedInnerLine,
        restrictionZonesV2PermittedInnerLine,
        restrictionZonesV2SpeedInnerLine,
        // ── STOP_LIMIT: все слои поверх LIMITED/PERMITTED ────────────────────
        restrictionZonesV2StopLimitFictitiousLine,
        restrictionZonesV2StopLimitInvisibleFill,
        restrictionZonesV2StopLimitInvisibleLine,
        restrictionZonesV2StopLimitInnerLine,
        restrictionZonesV2StopLimitLine,
        // ── FORBIDDEN: все слои поверх всего ────────────────────────────────
        restrictionZonesV2ForbiddenFictitiousLine,
        restrictionZonesV2ForbiddenInvisibleFill,
        restrictionZonesV2ForbiddenInvisibleLine,
        restrictionZonesV2ForbiddenInnerLine,
        restrictionZonesV2ForbiddenLine,
        // ── PROPOSED LINES ────────────────────────────────────────────────────
        restrictionZonesV2PermittedProposedLine,
        restrictionZonesV2ForbiddenProposedLine,
        // ── BADGE и TEXT (самые верхние) ──────────────────────────────────────
        restrictionZonesV2BadgeBg,
        restrictionZonesV2Badge,
        restrictionZonesV2SpeedLabel,
    ],
};
// ── Административные границы OSM (DA-13054) ───────────────────────────────
// Источник: dwh.administrative_boundaries_polygon, boundary='administrative'
// Три sub-слоя: fill (прозрачный, для кликов), line (обводка), labels (подписи)
const osmAdminBoundariesLine = {
    id: 'Административное деление_line',
    label: defineMessage({ message: 'Административное деление_line' }),
    type: 'line',
    'source-layer': 'martin.osm_admin_boundaries_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.osm_admin_boundaries_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': [
            'match', [ 'get', 'admin_level' ],
            '4',  '#ffffff',
            '5',  '#ffffaa',
            '6',  '#ffee55',
            '7',  '#ffcc00',
            '8',  '#ff9900',
            '9',  '#ff6600',
            '10', '#ff3300',
            '11', '#ff0000',
            '14', '#cc0000',
            '#ffffff',
        ],
        'line-opacity': 0.8,
        'line-width': [
            'interpolate', [ 'linear' ],
            [ 'to-number', [ 'get', 'admin_level' ] ],
            4,  4.0,
            6,  2.5,
            8,  1.5,
            10, 0.8,
            14, 0.4,
        ],
    },
};
const osmAdminBoundariesLabels = {
    id: 'Административное деление_labels',
    label: defineMessage({ message: 'Административное деление_labels' }),
    type: 'symbol',
    'source-layer': 'martin.osm_admin_boundaries_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.osm_admin_boundaries_mvt.json`,
    },
    minzoom: 5,
    layout: {
        visibility: 'none',
        'text-field': [ 'get', 'name' ],
        'text-size': [
            'interpolate', [ 'linear' ],
            [ 'to-number', [ 'get', 'admin_level' ] ],
            4,  14,
            8,  11,
            10,  9,
        ],
        'text-font': [ 'Open Sans Regular', 'Arial Unicode MS Regular' ],
        'text-anchor': 'center',
        'symbol-spacing': 500,
        'text-padding': 20,
    },
    paint: {
        'text-color': [
            'match', [ 'get', 'admin_level' ],
            '4',  '#ffffff',
            '5',  '#ffffaa',
            '6',  '#ffee55',
            '7',  '#ffcc00',
            '8',  '#ff9900',
            '9',  '#ff6600',
            '10', '#ff3300',
            '11', '#ff0000',
            '14', '#cc0000',
            '#ffffff',
        ],
        'text-halo-color': 'rgba(0,0,0,0.7)',
        'text-halo-width': 1.5,
    },
};
const osmAdminBoundaries = {
    id: 'Административное деление',
    label: defineMessage({ message: 'Административное деление' }),
    type: 'fill',
    'source-layer': 'martin.osm_admin_boundaries_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.osm_admin_boundaries_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': 'rgba(0,0,0,0)',
        'fill-opacity': 0,
    },
    additionalLayers: [ osmAdminBoundariesLine, osmAdminBoundariesLabels ],
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// ─────────────────────────────────────────────────────────────────────────────
const cityAreasPoly = {
    id: 'Границы города_poly',
    label: defineMessage({ message: 'Границы города' }),
    type: 'fill',
    'source-layer': 'martin.city_boundaries_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.city_boundaries_mvt.json`,
    },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'show_on_map',
            stops: [
                [
                    '1',
                    'white',
                ],
            ],
            default: 'black',
        },
        'fill-opacity': 0,
    },
    // minzoom: 15,
    layout: { visibility: 'none' },
    filter:[ 'any',
        [ 'match', [ 'get', 'show_on_map' ], [ '2' ], true, false ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const cityAreas = {
    id: 'Границы города',
    label: defineMessage({ message: 'Границы города' }),
    type: 'line',
    'source-layer': 'martin.city_boundaries_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.city_boundaries_mvt.json`,
    },
    // minzoom: 15,
    layout: { visibility: 'none' },
    paint: {
        'line-color': 'white',
        'line-width': 3,
    },
    additionalLayers: [ cityAreasPoly ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const regionRadiusCentroid = {
    id: 'Центроид регионов',
    label: defineMessage({ message: 'Центроид регионов' }),
    type: 'circle',
    'source-layer': 'martin.region_radius_labels_mvt',
    // minzoom: 8,
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.region_radius_labels_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-opacity': 0.7,
        'circle-color': 'yellow',
        'circle-radius': 6,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const regionRadius = {
    id: 'Радиус городов',
    label: defineMessage({ message: 'Радиус городов' }),
    type: 'fill',
    'source-layer': 'martin.region_radius_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.region_radius_mvt.json`,
    },
    // minzoom: 8,
    layout: { visibility: 'none' },
    paint: {
        'fill-color': 'red',
        'fill-opacity': 0.2,
    },
    additionalLayers: [ regionRadiusCentroid ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const speedLimitLabels = {
    id: 'Зоны ограничения скорости_label',
    label: defineMessage({ message: 'Зоны ограничения скорости_label' }),
    type: 'symbol',
    'source-layer': 'martin.speed_limits_label_mvt',
    minzoom: 8,
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.speed_limits_label_mvt.json`,
    },
    layout: {
        visibility: 'none',
        'text-field': [
            'concat',
            [
                'case',
                [ '!=', [ 'get', 'speed_limit' ], null ],
                [
                    'case',
                    [ '>=', [ 'get', 'speed_limit' ], 1 ],
                    [ 'get', 'speed_limit' ],
                    '-',
                ],
                '-',
            ],
            ` ${t`км/ч`}`,
        ],
        'text-size': 10,
    },
    paint: {
        'text-halo-color': '#fff',
        'text-halo-width': 2,
    },
    filter: [
        'all',
        [
            '==',
            [ 'to-string', [ 'get', 'status' ] ],
            V.ACTIVE,
        ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const speedLimitAreasLines = {
    id: 'Зоны ограничения скорости_обводка',
    label: defineMessage({ message: 'Зоны ограничения скорости_обводка' }),
    type: 'line',
    'source-layer': 'martin.speed_limits_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.speed_limits_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': [
            'match',
            [ 'to-string', [ 'get', 'group_' ] ],
            'SPEED_LIMIT', '#8184d5',
            'STOP_LIMIT', '#256B87',
            'STOP_LIMIT_with_pause', 'green',
            '#8184d5',
        ],
        'line-opacity': 0.9,
        'line-width': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const speedLimitAreas = {
    id: 'Зоны ограничения скорости',
    label: defineMessage({ message: 'Зоны ограничения скорости' }),
    type: 'fill',
    'source-layer': 'martin.speed_limits_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.speed_limits_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'group_',
            stops: [
                [
                    'SPEED_LIMIT',
                    '#8184d5',
                ],
                [
                    'STOP_LIMIT',
                    '#256B87',
                ],
                [
                    'STOP_LIMIT_with_pause',
                    '#256B87',
                ],
            ],
            'default': '#8184d5',
        },
        'fill-opacity': 0.3,
    },
    filter: [
        'all',
        [
            '==',
            [
                'to-string',
                [ 'get', 'status' ],
            ],
            V.ACTIVE,
        ],
    ],
    additionalLayers: [ speedLimitLabels, speedLimitAreasLines ],
    availableFor: [],
};
const analyticAreasLines = {
    id: 'Аналитические зоны_обводка',
    label: defineMessage({ message: 'Аналитические зоны_обводка' }),
    type: 'line',
    'source-layer': 'martin.analytic_area_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.analytic_area_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': 'white',
        'line-opacity': 0.7,
        'line-width': 2,
    },
    filter: [ '==', [ 'to-number', [ 'get', 'filter_' ] ], 1 ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const analyticAreas = {
    id: 'Аналитические зоны',
    label: defineMessage({ message: 'Аналитические зоны' }),
    type: 'fill',
    'source-layer': 'martin.analytic_area_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.analytic_area_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': '#008080',
        'fill-opacity': 0.4,
        'fill-outline-color': 'black',
    },
    filter: [ '==', [ 'to-number', [ 'get', 'filter_' ] ], 1 ],
    additionalLayers: [ analyticAreasLines ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// DA-15115: Фиче-зоны (public.feature_area) — read-only, все статусы ACTIVE/INACTIVE/DELETED,
// видимость статуса управляется 3 тумблерами в легенде (см. FeatureAreasLegend.jsx)
const FEATURE_AREAS_SOURCE = {
    id: 'martin.feature_areas_mvt',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.feature_areas_mvt.json`,
};
const FEATURE_AREAS_STATUS_COLOR = [
    'match', [ 'get', 'status' ],
    'ACTIVE', '#2ecc71',
    'INACTIVE', '#95a5a6',
    'DELETED', '#e74c3c',
    '#cccccc',
];
// Обводка — отдельный, более тёмный/насыщенный цвет (не тот же color с другой opacity),
// по конвенции слоя «Зонирование» (см. RESTRICTION_ZONES_V2_*_STYLE в layerStyles.js:
// color для заливки, отдельный borderColor + line-opacity: 1 для обводки)
const FEATURE_AREAS_STATUS_BORDER_COLOR = [
    'match', [ 'get', 'status' ],
    'ACTIVE', '#1e8449',
    'INACTIVE', '#616a6b',
    'DELETED', '#a93226',
    '#999999',
];
const FEATURE_AREAS_STATUS_OPACITY = [
    'match', [ 'get', 'status' ],
    'ACTIVE', 0.3,
    'INACTIVE', 0.18,
    'DELETED', 0.08,
    0.15,
];
// Дефолт легенды — включён только ACTIVE (mapFilters.featureAreasStatuses в reducers/mapFilters.js)
const FEATURE_AREAS_DEFAULT_FILTER = [ 'in', [ 'get', 'status' ], [ 'literal', [ 'ACTIVE' ] ] ];
const featureAreasLines = {
    id: 'Фиче-зоны_обводка',
    label: defineMessage({ message: 'Фиче-зоны_обводка' }),
    type: 'line',
    'source-layer': 'martin.feature_areas_mvt',
    source: FEATURE_AREAS_SOURCE,
    layout: { visibility: 'none' },
    paint: {
        'line-color': FEATURE_AREAS_STATUS_BORDER_COLOR,
        'line-width': 2.5,
        'line-opacity': 1,
    },
    filter: FEATURE_AREAS_DEFAULT_FILTER,
    availableFor: [ V.SUPER_ADMIN ],
};
const featureAreas = {
    id: 'Фиче-зоны',
    label: defineMessage({ message: 'Фиче-зоны' }),
    type: 'fill',
    'source-layer': 'martin.feature_areas_mvt',
    source: FEATURE_AREAS_SOURCE,
    layout: { visibility: 'none' },
    paint: {
        'fill-color': FEATURE_AREAS_STATUS_COLOR,
        'fill-opacity': FEATURE_AREAS_STATUS_OPACITY,
    },
    filter: FEATURE_AREAS_DEFAULT_FILTER,
    additionalLayers: [ featureAreasLines ],
    availableFor: [ V.SUPER_ADMIN ],
};
const operationAreasLabels = {
    id: 'Операционные зоны_label',
    label: defineMessage({ message: 'Операционные зоны_label' }),
    type: 'symbol',
    'source-layer': 'martin.operation_areas_label_mvt',
    minzoom: 8,
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.operation_areas_label_mvt.json`,
    },
    layout: {
        visibility: 'none',
        'text-field': [
            'format',
            [ 'get', 'name' ], {},
            '\n', {},
            [ 'get', 'number' ], {},
        ],
        'text-size': 8.5,
        'text-anchor': 'bottom',
        'text-offset': [ 0, -1 ],
    },
    paint: {
        'text-halo-color': '#fff',
        'text-halo-width': 2,
    },
    filter: [
        'all',
        [
            '==',
            [ 'to-string', [ 'get', 'status' ] ],
            V.ACTIVE,
        ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const operationAreasRotation = {
    id: 'Операционные зоны (rotation)',
    label: defineMessage({ message: 'Операционные зоны (rotation)' }),
    type: 'fill',
    'source-layer': 'martin.operations_area_rotation_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.operations_area_rotation_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'rotation_whoosh' ] ],
            0, 'hsl(242, 89%, 20%)',
            1, '#7300a8',
            2, '#9c179e',
            3, '#be3785',
            4, '#7ad151',
            5, '#f0f921',
        ],
        'fill-opacity': 0.6,
    },
    filter: [
        'all',
        [
            '==',
            [
                'to-number',
                [ 'get', 'period_' ],
            ],
            7,
        ],
        [ 'match', [ 'get', 'show_on_map' ], [ '2' ], true, false ],
    ],
    availableFor: [ V.ADMIN ],
};
// Заголовок экспорта для операционных зон (общий для основного и dev-слоя).
// Без тогла «Статистики по зонам» (showRotationStatuses) — пустой результат,
// откат на label «Операционные зоны». Со статистикой — «Статистика по
// {стартам/флоту/оборотам} в операционных зонах за {период}», и для оператора,
// отличного от Whoosh, добавляется « для оператора {оператор}».
const OA_METRIC_DATIVE = {
    trips:    defineMessage({ message: 'стартам' }),
    fleet:    defineMessage({ message: 'флоту' }),
    rotation: defineMessage({ message: 'оборотам' }),
};
const oaStatsExportTitle = ({ filters }) => {
    const statsOn = (filters.showRotationStatuses || []).includes('1');
    if (!statsOn) {
        return '';
    }
    const metric = i18n._(OA_METRIC_DATIVE[filters.oaRotationMetric] || OA_METRIC_DATIVE.rotation);
    const days = filters.oaRotationDays;
    const period = days === 1
        ? i18n._(defineMessage({ message: '{days} день' }), { days })
        : i18n._(defineMessage({ message: '{days} дней' }), { days });
    const base = i18n._(
        defineMessage({ message: 'Статистика по {metric} в операционных зонах за {period}' }),
        { metric, period }
    );
    const op = filters.oaRotationOperator;
    if (op && !String(op).startsWith('whoosh')) {
        const opLabel = (V.OaRotationDevOperatorTypes.find(o => o.value === op) || {}).label;
        const operator = opLabel ? i18n._(opLabel) : op;
        return i18n._(defineMessage({ message: '{base} для оператора {operator}' }), { base, operator });
    }
    return base;
};
const operationAreas = {
    id: 'Операционные зоны',
    label: defineMessage({ message: 'Операционные зоны' }),
    exportTitle: oaStatsExportTitle,
    type: 'line',
    'source-layer': 'martin.operation_areas_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.operation_areas_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': '#FF8C00',
        'line-opacity': 0.5,
        'line-width': 3,
    },
    filter: [
        'all',
        [
            '==',
            [
                'to-string',
                [ 'get', 'status' ],
            ],
            V.ACTIVE,
        ],
    ],
    additionalLayers: [ operationAreasLabels, operationAreasRotation ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const operationAreasLabelsDev = {
    id: 'Операционные зоны dev_label',
    label: defineMessage({ message: 'Операционные зоны dev_label' }),
    type: 'symbol',
    'source-layer': 'martin.operation_areas_label_mvt',
    minzoom: 8,
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.operation_areas_label_mvt.json`,
    },
    layout: {
        visibility: 'none',
        'text-field': [
            'format',
            [ 'get', 'name' ], {},
            '\n', {},
            [ 'get', 'number' ], {},
        ],
        'text-size': 8.5,
        'text-anchor': 'bottom',
        'text-offset': [ 0, -1 ],
    },
    paint: {
        'text-halo-color': '#fff',
        'text-halo-width': 2,
    },
    filter: [
        'all',
        [
            '==',
            [ 'to-string', [ 'get', 'status' ] ],
            V.ACTIVE,
        ],
    ],
    availableFor: [ V.SUPER_ADMIN ],
};
const operationAreasRotationDev = {
    id: 'Операционные зоны dev (rotation)',
    label: defineMessage({ message: 'Операционные зоны dev (rotation)' }),
    type: 'fill',
    'source-layer': 'martin.operations_area_rotation_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.operations_area_rotation_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'rotation_whoosh' ] ],
            0, 'hsl(242, 89%, 20%)',
            1, '#7300a8',
            2, '#9c179e',
            3, '#be3785',
            4, '#7ad151',
            5, '#f0f921',
        ],
        'fill-opacity': 0.6,
    },
    filter: [
        'all',
        [
            '==',
            [
                'to-number',
                [ 'get', 'period_' ],
            ],
            7,
        ],
        [ 'match', [ 'get', 'show_on_map' ], [ '2' ], true, false ],
    ],
    availableFor: [ V.SUPER_ADMIN ],
};
const operationAreasDev = {
    id: 'Операционные зоны dev',
    label: defineMessage({ message: 'Операционные зоны dev' }),
    exportTitle: oaStatsExportTitle,
    type: 'line',
    'source-layer': 'martin.operation_areas_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.operation_areas_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': '#FF8C00',
        'line-opacity': 0.5,
        'line-width': 3,
    },
    filter: [
        'all',
        [
            '==',
            [
                'to-string',
                [ 'get', 'status' ],
            ],
            V.ACTIVE,
        ],
    ],
    additionalLayers: [ operationAreasLabelsDev, operationAreasRotationDev ],
    availableFor: [ V.SUPER_ADMIN ],
};
const adminAggregatedTracksV2 = {
    id: 'Агрегированные треки за последние 7 дней',
    label: defineMessage({
        message: 'Агрегированные треки за последние 7 дней',
        description: 'Aggregated tracks last 7 days - H3 hexagonal grid with dynamic zoom (6-12)',
    }),
    // Заголовок экспорта: тип СИМ (adminAggTracksV2VehicleType) + под-период
    // (adminAggTracksV2Period: week/weekdays/weekends).
    exportTitle: ({ filters }) => {
        const vehicle = {
            all:      defineMessage({ message: 'всех СИМ' }),
            scooters: defineMessage({ message: 'самокатов' }),
            bikes:    defineMessage({ message: 'велосипедов' }),
        }[filters.adminAggTracksV2VehicleType];
        const period = {
            week:     defineMessage({ message: '7 дней' }),
            weekends: defineMessage({ message: 'последние выходные' }),
            weekdays: defineMessage({ message: 'будние дни последней недели' }),
        }[filters.adminAggTracksV2Period];
        if (!vehicle || !period) {
            return '';
        }
        return i18n._(
            defineMessage({ message: 'Агрегированные треки {vehicle} за {period}' }),
            { vehicle: i18n._(vehicle), period: i18n._(period) }
        );
    },
    type: 'fill',
    minzoom: 0,
    maxzoom: 24,
    'source-layer': 'martin.agg_hex_user_tracks_7days_mvt',
    source: {
        id: 'admin_agg_tracks_v2_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.agg_hex_user_tracks_7days_mvt.json`,
        requiredQueryParams: [ 'h3_zoom' ],
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            // Масштабирование: count_all * 7^(zoom - 10) — компенсирует разницу размера H3 ячеек
            [ '*',
                [ 'to-number', [ 'get', 'count_all' ] ],
                [ '^', 7, [ '-', [ 'to-number', [ 'get', 'zoom' ] ], 10 ] ],
            ],
            0,
            'hsla(240, 100%, 27%, 0)',
            100,
            'hsla(240, 100%, 27%, 0.4)',
            300,
            '#0d0887',
            1000,
            '#6b00a8',
            4000,
            '#e16461',
            8000,
            '#fca736',
            25000,
            '#f0f921',
        ],
        'fill-opacity': 0.6,
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
    },
    legend: ADMIN_AGG_TRACKS_V2_LEGEND,
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// DA-15259: Треки за 7 дней в разрезе продуктовых когорт (моделей тарификации).
// Когорта выбирается радиокнопкой и подставляется в property (<cohort>_cnt), период
// уходит query-параметром в MVT-функцию (week/weekdays/weekends) — она отдаёт только
// нужные строки. all_cnt приходит в тайле всегда: из него считается доля когорты.
const aggregatedTracksCohort = {
    id: 'Треки по моделям тарификации (7 дней)',
    label: defineMessage({
        message: 'Треки по моделям тарификации (7 дней)',
        description: 'Aggregated tracks last 7 days split by pricing model cohorts - H3 hexagonal grid with dynamic zoom (6-12)',
    }),
    // Заголовок экспорта: когорта (aggTracksCohortType) + под-период (aggTracksCohortPeriod).
    exportTitle: ({ filters }) => {
        const cohort = V.findAggTracksCohort(filters.aggTracksCohortType);
        const period = {
            week:     defineMessage({ message: '7 дней' }),
            weekends: defineMessage({ message: 'последние выходные' }),
            weekdays: defineMessage({ message: 'будние дни последней недели' }),
        }[filters.aggTracksCohortPeriod];
        if (!cohort || !period) {
            return '';
        }
        // Для подгруппы в заголовок идёт «Группа: подгруппа», для листа верхнего уровня — он сам.
        const group = cohort.parent ? `${i18n._(cohort.parent.label)}: ` : '';

        return i18n._(
            defineMessage({ message: 'Треки «{cohort}» за {period}' }),
            { cohort: `${group}${i18n._(cohort.leaf.label)}`, period: i18n._(period) }
        );
    },
    type: 'fill',
    minzoom: 0,
    maxzoom: 24,
    'source-layer': 'martin.agg_hex_user_tracks_cohort_7days_mvt',
    source: {
        id: 'agg_tracks_cohort_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.agg_hex_user_tracks_cohort_7days_mvt.json`,
        requiredQueryParams: [ 'h3_zoom', 'period' ],
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            // Масштабирование: all_cnt * 7^(zoom - 10) — компенсирует разницу размера H3 ячеек.
            // Property подменяется на <cohort>_cnt в setAggTracksCohortFilter (Map.jsx).
            [ '*',
                [ 'to-number', [ 'get', 'all_cnt' ] ],
                [ '^', 7, [ '-', [ 'to-number', [ 'get', 'zoom' ] ], 10 ] ],
            ],
            0,
            'hsla(240, 100%, 27%, 0)',
            100,
            'hsla(240, 100%, 27%, 0.4)',
            300,
            '#0d0887',
            1000,
            '#6b00a8',
            4000,
            '#e16461',
            8000,
            '#fca736',
            25000,
            '#f0f921',
        ],
        'fill-opacity': 0.6,
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
    },
    legend: AGG_TRACKS_COHORT_LEGEND,
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// DA-15415: Треки в разрезе когорт пользователей (возраст, пол, стаж, верификация).
// Бакет выбирается радиокнопкой и подставляется в property (<bucket>_cnt).
// Период и разрез уходят query-параметрами в MVT-функцию: period фильтрует строки и
// заодно выбирает таблицу-источник (неделя или адхоковый месяц),
// dimension определяет, колонки какого разреза приедут в тайле. all_cnt приходит
// всегда — из него считается доля бакета.
const aggregatedTracksUserCohorts = {
    id: 'Треки по когортам пользователей',
    label: defineMessage({
        message: 'Треки по когортам пользователей',
        description: 'Aggregated tracks split by user cohorts (age, gender, tenure, verification) over a selected period - last 7 days or an ad-hoc month - H3 hexagonal grid with dynamic zoom (6-12)',
    }),
    // Заголовок экспорта: бакет (userCohortsType) + под-период (userCohortsPeriod).
    exportTitle: ({ filters }) => {
        const cohort = V.findUserCohort(filters.userCohortsType);
        // Недельные периоды описываются словами, месяцы берут подпись из своего реестра.
        const foundPeriod = V.findUserCohortPeriod(filters.userCohortsPeriod);
        const period = {
            week:     defineMessage({ message: '7 дней' }),
            weekends: defineMessage({ message: 'последние выходные' }),
            weekdays: defineMessage({ message: 'будние дни последней недели' }),
        }[filters.userCohortsPeriod] || (foundPeriod && foundPeriod.parent ? foundPeriod.leaf.label : null);
        if (!cohort || !period) {
            return '';
        }
        // Для бакета в заголовок идёт «Разрез: бакет», для «Всех поездок» — он сам.
        const group = cohort.parent ? `${i18n._(cohort.parent.label)}: ` : '';

        return i18n._(
            defineMessage({ message: 'Треки «{cohort}» за {period}' }),
            { cohort: `${group}${i18n._(cohort.leaf.label)}`, period: i18n._(period) }
        );
    },
    type: 'fill',
    minzoom: 0,
    maxzoom: 24,
    'source-layer': 'martin.agg_hex_route_user_cohorts_7days_mvt',
    source: {
        id: 'agg_tracks_user_cohorts_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.agg_hex_route_user_cohorts_7days_mvt.json`,
        requiredQueryParams: [ 'h3_zoom', 'period', 'dimension' ],
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            // Масштабирование: all_cnt * 7^(zoom - 10) — компенсирует разницу размера H3 ячеек.
            // Property подменяется на <bucket>_cnt в setUserCohortsFilter (Map.jsx).
            [ '*',
                [ 'to-number', [ 'get', 'all_cnt' ] ],
                [ '^', 7, [ '-', [ 'to-number', [ 'get', 'zoom' ] ], 10 ] ],
            ],
            0,
            'hsla(240, 100%, 27%, 0)',
            100,
            'hsla(240, 100%, 27%, 0.4)',
            300,
            '#0d0887',
            1000,
            '#6b00a8',
            4000,
            '#e16461',
            8000,
            '#fca736',
            25000,
            '#f0f921',
        ],
        'fill-opacity': 0.6,
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
    },
    legend: USER_COHORTS_LEGEND,
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// Каркас динамического заголовка экспорта (DA-15019). ICU-плейсхолдеры {month}/
// {year} подставляются лениво в exportTitle через i18n._ — каталог даёт перевод
// каркаса, само название месяца локализует Intl.
const TRIPS_MONTH_TITLE = defineMessage({ message: 'Поездки за {month} {year} г.' });
// DA-14786: Агрегированные треки за календарный месяц (H3 zoom 6-12).
// Аналог 7-дневного слоя, но с выбором месяца через query-param 'month'.
const adminAggregatedTracksMonthly = {
    id: 'Агрегированные треки за месяц',
    label: defineMessage({
        message: 'Агрегированные треки за месяц',
        description: 'Aggregated tracks for a calendar month - H3 hexagonal grid with dynamic zoom (6-12) and month selection',
    }),
    // Динамический заголовок при экспорте растра (DA-15019): подставляем выбранный
    // в селекторе месяц (mapFilters.adminAggTracksMonthlyMonth = 'YYYY-MM').
    // Каркас локализуется через каталог (i18n._ с ICU-плейсхолдерами, см.
    // TRIPS_MONTH_TITLE), название месяца — штатным Intl под текущую локаль.
    exportTitle: ({ filters, locale }) => {
        const value = filters.adminAggTracksMonthlyMonth;
        if (!value) {
            return '';
        }

        const [ y, m ] = value.split('-').map(Number);
        const month = new Intl.DateTimeFormat(locale || 'ru', { month: 'long' })
            .format(new Date(y, m - 1, 1));

        return i18n._(TRIPS_MONTH_TITLE, { month, year: y });
    },
    type: 'fill',
    minzoom: 0,
    maxzoom: 24,
    'source-layer': 'martin.agg_hex_user_tracks_monthly_mvt',
    source: {
        id: 'admin_agg_tracks_monthly_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.agg_hex_user_tracks_monthly_mvt.json`,
        requiredQueryParams: [ 'h3_zoom', 'month' ],
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            // Масштабирование: count_all * 7^(zoom - 10) — компенсирует разницу размера H3 ячеек
            [ '*',
                [ 'to-number', [ 'get', 'count_all' ] ],
                [ '^', 7, [ '-', [ 'to-number', [ 'get', 'zoom' ] ], 10 ] ],
            ],
            0,
            'hsla(240, 100%, 27%, 0)',
            100,
            'hsla(240, 100%, 27%, 0.4)',
            300,
            '#0d0887',
            1000,
            '#6b00a8',
            4000,
            '#e16461',
            8000,
            '#fca736',
            25000,
            '#f0f921',
        ],
        'fill-opacity': 0.6,
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
    },
    legend: ADMIN_AGG_TRACKS_MONTHLY_LEGEND,
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// DA-14308: Выбросы GPS-треков по H3-гексагонам
// Два режима: "откуда" (from_h3_12, реальная позиция) и "куда" (to_h3_12, аномальная позиция).
// Paint динамически меняется через setRouteAnomaliesPaint в Map.jsx при смене src/метрики.
// Source URL переключается через setRouteAnomaliesDirection при смене откуда/куда.
const routeAnomaliesH3 = {
    id: 'Гео вылеты',
    label: defineMessage({
        message: 'Гео вылеты',
        description: 'GPS track anomalies aggregated by H3 hexagon, dynamic zoom 6-12, from/to switch (DA-14308)',
    }),
    // Заголовок экспорта (DA-15019): период в днях (routeAnomaliesPeriod = '7d'|'30d').
    exportTitle: ({ filters }) => {
        const days = String(filters.routeAnomaliesPeriod || '').replace(/\D/g, '');

        return days ? i18n._(defineMessage({ message: 'Гео вылеты за {days} дней' }), { days }) : '';
    },
    type: 'fill',
    minzoom: 0,
    maxzoom: 24,
    'source-layer': 'martin.route_anomalies_from_h3_mvt',
    source: {
        id: 'route_anomalies_from_h3_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.route_anomalies_from_h3_mvt.json`,
        requiredQueryParams: [ 'h3_zoom' ],
    },
    layout: { visibility: 'none' },
    paint: {
        // Начальный paint — медиана дистанции ALL src (plasma-палитра).
        // Динамически меняется через setRouteAnomaliesPaint в Map.jsx при смене фильтров (DA-14308).
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'median_dist_all' ] ],
            0,    '#0d0887',
            200,  '#6b00a8',
            350,  '#e16461',
            600,  '#fca736',
            1400, '#f0f921',
        ],
        'fill-opacity': 0.7,
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
    },
    legend: ROUTE_ANOMALIES_H3_LEGEND,
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// Dev group layers
// DA-13776: Сетка гексагонов H3
const h3GridLayer = {
    id: 'Адреса гексагонов H3',
    label: defineMessage({
        message: 'Адреса гексагонов H3',
        description: 'H3 hexagonal grid with index labels (DA-13776)',
    }),
    type: 'fill',
    minzoom: 0,
    maxzoom: 24,
    'source-layer': 'martin.h3_grid_mvt',
    source: {
        id: 'h3_grid_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.h3_grid_mvt.json`,
        requiredQueryParams: [ 'h3_zoom' ],
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': 'rgba(100, 149, 237, 0.15)',
        'fill-outline-color': 'rgba(100, 149, 237, 0.7)',
    },
    availableFor: [ V.SUPER_ADMIN ],
};
const devZonesWhoosh = {
    id: 'Зоны Whoosh',
    label: defineMessage({ message: 'Зоны Whoosh' }),
    type: 'line',
    'source-layer': 'martin.operation_areas_mvt', // TODO: Replace with actual source
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.operation_areas_mvt.json`, // TODO: Replace with actual source
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': '#00FF00',
        'line-opacity': 0.7,
        'line-width': 3,
    },
    filter: [
        'all',
        [
            '==',
            [
                'to-string',
                [ 'get', 'status' ],
            ],
            V.ACTIVE,
        ],
    ],
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
const devZonesCompetitors = {
    id: 'Зоны конкурентов',
    label: defineMessage({ message: 'Зоны конкурентов' }),
    type: 'line',
    'source-layer': 'martin.operation_areas_mvt', // TODO: Replace with actual source
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.operation_areas_mvt.json`, // TODO: Replace with actual source
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': '#FF0000',
        'line-opacity': 0.7,
        'line-width': 3,
    },
    filter: [
        'all',
        [
            '==',
            [
                'to-string',
                [ 'get', 'status' ],
            ],
            V.ACTIVE,
        ],
    ],
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
const operationAreasCompetitorsLines = {
    id: 'Операционные зоны (конкуренты)_lines',
    label: defineMessage({ message: 'Старты по операционным зонам' }),
    type: 'line',
    'source-layer': 'martin.oa_comp_statistics_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.oa_comp_statistics_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': {
            base: 1,
            type: 'categorical',
            property: 'market_leader',
            stops: [
                [
                    'whoosh',
                    V.CompetitorsLegend[0].color,
                ],
                [
                    'yandex',
                    V.CompetitorsLegend[1].color,
                ],
                [
                    'urent',
                    V.CompetitorsLegend[2].color,
                ],
                [
                    'jet',
                    V.CompetitorsLegend[3].color,
                ],
                [
                    'eleven',
                    V.CompetitorsLegend[4].color,
                ],
                [
                    'lime',
                    V.CompetitorsLegend[5].color,
                ],
            ],
            'default': 'red',
        },
        'line-opacity': 0.5,
        'line-width': 3,
    },
    filter:[ 'all',
        [ '==', [ 'to-number', [ 'get', 'period' ] ], 7 ],
    ],
    availableFor: [ V.SUPER_ADMIN ],
};
const operationAreasCompetitors = {
    id: 'Операционные зоны (конкуренты)',
    label: defineMessage({ message: 'Старты по операционным зонам' }),
    // Заголовок экспорта: добавляем выбранный период (oaCompetitorsDays).
    exportTitle: ({ filters }) => {
        const days = filters.oaCompetitorsDays;
        return days
            ? i18n._(defineMessage({ message: 'Старты по операционным зонам за {days} дней' }), { days })
            : i18n._(defineMessage({ message: 'Старты по операционным зонам' }));
    },
    type: 'fill',
    'source-layer': 'martin.oa_comp_statistics_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.oa_comp_statistics_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'market_leader',
            stops: [
                [
                    'whoosh',
                    V.CompetitorsLegend[0].color,
                ],
                [
                    'yandex',
                    V.CompetitorsLegend[1].color,
                ],
                [
                    'urent',
                    V.CompetitorsLegend[2].color,
                ],
                [
                    'jet',
                    V.CompetitorsLegend[3].color,
                ],
                [
                    'eleven',
                    V.CompetitorsLegend[4].color,
                ],
                [
                    'lime',
                    V.CompetitorsLegend[5].color,
                ],
            ],
            'default': 'red',
        },
        'fill-opacity': 0.5,
    },
    filter:[ 'all',
        [ '==', [ 'to-number', [ 'get', 'period' ] ], 7 ],
    ],
    additionalLayers: [ operationAreasLabels, operationAreasCompetitorsLines ],
    availableFor: [ V.SUPER_ADMIN ],
};
const restrictionZonesCompetitorsText = {
    id: 'Зоны ограничений (конкуренты)_text',
    label: defineMessage({ message: 'Зоны ограничений' }),
    type: 'symbol',
    'source-layer': 'martin.competitors_restriction_zones_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_restriction_zones_mvt.json`,
    },
    minzoom: 10,
    layout: {
        visibility: 'none',
        get 'text-field' () {
            return [
                'concat',
                [
                    'case',
                    [ '>=', [ 'get', 'speed_limit' ], 1 ],
                    [ 'get', 'speed_limit' ],
                    '-',
                ],
                ` ${t`км/ч`}`,
            ];
        },
        'text-size': 8,
    },
    paint: {
        'text-halo-color': '#fff',
        'text-halo-width': 2,
    },
    filter:[ 'all',
        [ 'match', [ 'get', 'zone' ], [ 'speed_limit' ], true, false ],
    ],
    availableFor: [ V.SUPER_ADMIN ],
};
const restrictionZonesCompetitorsLines = {
    id: 'Зоны ограничений (конкуренты)_lines',
    label: defineMessage({ message: 'Зоны ограничений' }),
    type: 'line',
    'source-layer': 'martin.competitors_restriction_zones_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_restriction_zones_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': [
            'case',
            // Проверяем оператора и используем его цвет, если он совпадает с functionalAreasYandexUrentTypes
            [ '==', [ 'get', 'operator' ], 'Yandex' ],
            V.functionalAreasYandexUrentTypes[0].fill,
            [ '==', [ 'get', 'operator' ], 'Urent' ],
            V.functionalAreasYandexUrentTypes[1].fill,
            [ '==', [ 'get', 'operator' ], 'Jet' ],
            V.functionalAreasYandexUrentTypes[2].fill,
            [ '==', [ 'get', 'operator' ], 'Lime' ],
            V.functionalAreasYandexUrentTypes[3].fill,
            [ '==', [ 'get', 'operator' ], 'Bolt' ],
            V.functionalAreasYandexUrentTypes[4].fill,
            [ '==', [ 'get', 'operator' ], 'Voi' ],
            V.functionalAreasYandexUrentTypes[6].fill,
            [ '==', [ 'get', 'operator' ], 'LetsGo' ],
            V.functionalAreasYandexUrentTypes[7].fill,
            [ '==', [ 'get', 'operator' ], 'Eleven' ],
            V.functionalAreasYandexUrentTypes[8].fill,
            [ '==', [ 'get', 'operator' ], 'Lite' ],
            V.functionalAreasYandexUrentTypes[9].fill,
            [ '==', [ 'get', 'operator' ], 'Yango' ],
            V.functionalAreasYandexUrentTypes[10].fill,
            [ '==', [ 'get', 'operator' ], 'Yandex/Yango' ],
            V.functionalAreasYandexUrentTypes[11].fill,
            // Если оператор не найден, используем цвета по типу зоны
            [ 'match', [ 'get', 'zone' ], [ 'speed_limit' ], true, false ],
            '#d4a9fc',
            [ 'match', [ 'get', 'zone' ], [ 'no_entry' ], true, false ],
            'red',
            [ 'match', [ 'get', 'zone' ], [ 'no_parking' ], true, false ],
            '#256B87',
            // Для всех остальных зон (other)
            '#808080',
        ],
        'line-opacity': 0.6,
        'line-width': 3,
    },
    filter:[ 'all',
        [ 'match', [ 'get', 'zone' ], [ 'speed_limit' ], true, false ],
    ],
    availableFor: [ V.SUPER_ADMIN ],
};
const restrictionZonesCompetitors = {
    id: 'Зоны ограничений (конкуренты)',
    label: defineMessage({ message: 'Зоны ограничений' }),
    exportTitle: defineMessage({ message: 'Зоны ограничений по операторам' }),
    type: 'fill',
    'source-layer': 'martin.competitors_restriction_zones_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_restriction_zones_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'zone',
            stops: [
                [
                    'no_parking',
                    '#256B87',
                ],
                [
                    'no_entry',
                    'red',
                ],
                [
                    'speed_limit',
                    '#d4a9fc',
                ],
                [
                    'parking',
                    'yellow',
                ],
            ],
            'default': '#808080',
        },
        'fill-opacity': 0.15,
    },
    filter:[ 'all',
        [ 'match', [ 'get', 'zone' ], [ 'speed_limit' ], true, false ],
    ],
    additionalLayers: [ restrictionZonesCompetitorsLines, restrictionZonesCompetitorsText ],
    availableFor: [ V.SUPER_ADMIN ],
};
// Парковки конкурентов (DA-13627)
// Источник: martin.competitors_parkings через функцию hexes_for_attention_clustered_mvt
// Точечный слой с динамической легендой операторов, цвета — как у functionalAreasYandexUrentTypes
const competitorsParkings = {
    id: 'Парковки (конкуренты)',
    label: defineMessage({ message: 'Парковки' }),
    // Перекрытие заголовка при экспорте растра (DA-15019): фиксированный текст
    // вместо короткого label «Парковки».
    exportTitle: defineMessage({ message: 'Парковки по операторам' }),
    type: 'circle',
    'source-layer': 'martin.hexes_for_attention_clustered_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.hexes_for_attention_clustered_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-color': operatorColorCase(),
        'circle-radius': 3,
        'circle-opacity': 0.7,
        'circle-stroke-width': 0,
        'circle-stroke-color': 'rgba(255, 255, 255, 0.1)',
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
    availableFor: [ V.SUPER_ADMIN ],
};
// DA-15432: Изменения парковок конкурентов
// Источник: martin.competitors_parkings_history через competitors_parkings_changes_mvt
// Цвет обводки — оператор (как в слое «Парковки»), тип изменения — форма:
//   new              — залитый белый круг
//   presumed_deleted — круг, перечёркнутый крест-накрест (иконка из SVG)
//   moved            — круг с точкой внутри, плюс бледная прежняя позиция и стрелка
//   attrs_changed    — круг без заливки
// Перечёркивание «предположительно удалённых» — иконка-крест поверх кружка.
// Иконка регистрируется в Map.jsx через registerParkingChangeIcons: рисуется из
// SVG в тройном разрешении, поэтому чистая на любом зуме. Через символьные слои
// со слешами ('/' и '\\') крест получался плохой — штрихи тонкие и не сходятся
// в центре.
const competitorsParkingsChangesSigns = {
    id: 'Изменения парковок (конкуренты)_signs',
    label: defineMessage({ message: 'Изменения парковок (конкуренты)_signs' }),
    type: 'symbol',
    'source-layer': 'martin.competitors_parkings_changes_mvt',
    source: {
        id: 'Изменения парковок (конкуренты)_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_parkings_changes_mvt.json`,
    },
    layout: {
        visibility: 'none',
        'icon-image': [
            'match', [ 'get', 'change_type' ],
            'presumed_deleted', 'parking-change-cross',
            '',
        ],
        // Иконка чуть больше диаметра кружка (2.5-5 px по зумам), чтобы крест
        // читался и выходил за его края. Логический размер иконки — 16 px.
        'icon-size': [
            'interpolate', [ 'linear' ], [ 'zoom' ],
            8,  0.55,
            12, 0.75,
            16, 1.05,
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
};
// Прежняя позиция переехавшей парковки — бледный кружок; от неё к новой точке
// ведёт стрелка. Обе сущности приходят в тайле отдельными фичами (record_type
// 'previous' и 'arrow') и только с 13 зума: ниже точки схлопнуты в мультиточки.
const competitorsParkingsChangesPrev = {
    id: 'Изменения парковок (конкуренты)_prev',
    label: defineMessage({ message: 'Изменения парковок (конкуренты)_prev' }),
    type: 'circle',
    'source-layer': 'martin.competitors_parkings_changes_mvt',
    source: {
        id: 'Изменения парковок (конкуренты)_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_parkings_changes_mvt.json`,
    },
    minzoom: 13,
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': [
            'interpolate', [ 'linear' ], [ 'zoom' ],
            13, 3,
            16, 4.5,
        ],
        'circle-color': 'rgba(0,0,0,0)',
        'circle-opacity': 0,
        'circle-stroke-width': 1,
        'circle-stroke-color': operatorColorCase(),
        'circle-stroke-opacity': 0.35,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
};
const competitorsParkingsChangesArrow = {
    id: 'Изменения парковок (конкуренты)_arrow',
    label: defineMessage({ message: 'Изменения парковок (конкуренты)_arrow' }),
    type: 'line',
    'source-layer': 'martin.competitors_parkings_changes_mvt',
    source: {
        id: 'Изменения парковок (конкуренты)_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_parkings_changes_mvt.json`,
    },
    minzoom: 13,
    layout: { visibility: 'none', 'line-cap': 'round' },
    paint: {
        'line-color': operatorColorCase(),
        'line-width': 1.5,
        'line-opacity': 0.35,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
};
// Направление переезда: «галочки» вдоль линии, как в перебросках флота конкурентов
const competitorsParkingsChangesArrowHeads = {
    id: 'Изменения парковок (конкуренты)_arrow_heads',
    label: defineMessage({ message: 'Изменения парковок (конкуренты)_arrow_heads' }),
    type: 'symbol',
    'source-layer': 'martin.competitors_parkings_changes_mvt',
    source: {
        id: 'Изменения парковок (конкуренты)_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_parkings_changes_mvt.json`,
    },
    minzoom: 13,
    layout: {
        visibility: 'none',
        // Одна стрелка на отдельной точке (record_type='arrow_head', 80% пути), а не
        // symbol-placement 'line': тот ставит галочки по всей длине линии.
        // Поворот приходит из тайла азимутом от прежней позиции к новой.
        'text-field': '▶',
        'text-size': [
            'interpolate', [ 'linear' ], [ 'zoom' ],
            13, 20,
            16, 30,
        ],
        'text-rotate': [ 'to-number', [ 'get', 'move_bearing' ], 0 ],
        'text-rotation-alignment': 'map',
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
    },
    paint: {
        'text-color': operatorColorCase(),
        'text-opacity': 0.35,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
};
// Точка внутри кружка — знак перемещённой парковки
const competitorsParkingsChangesMovedDot = {
    id: 'Изменения парковок (конкуренты)_moved_dot',
    label: defineMessage({ message: 'Изменения парковок (конкуренты)_moved_dot' }),
    type: 'circle',
    'source-layer': 'martin.competitors_parkings_changes_mvt',
    source: {
        id: 'Изменения парковок (конкуренты)_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_parkings_changes_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': [
            'interpolate', [ 'linear' ], [ 'zoom' ],
            8,  1,
            12, 1.4,
            16, 2,
        ],
        'circle-color': operatorColorCase(),
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
};
const competitorsParkingsChanges = {
    id: 'Изменения парковок (конкуренты)',
    label: defineMessage({ message: 'Изменения парковок' }),
    exportTitle: defineMessage({ message: 'Изменения парковок по операторам' }),
    type: 'circle',
    'source-layer': 'martin.competitors_parkings_changes_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_parkings_changes_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': [
            'interpolate', [ 'linear' ], [ 'zoom' ],
            8,  2.5,
            12, 3.5,
            16, 5,
        ],
        'circle-color': [
            'match', [ 'get', 'change_type' ],
            'new', '#ffffff',
            'rgba(0,0,0,0)',
        ],
        'circle-opacity': [
            'match', [ 'get', 'change_type' ],
            'new', 0.95,
            0,
        ],
        'circle-stroke-width': [
            'interpolate', [ 'linear' ], [ 'zoom' ],
            8,  1,
            12, 1.5,
            16, 2,
        ],
        'circle-stroke-color': operatorColorCase(),
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
    additionalLayers: [
        competitorsParkingsChangesPrev,
        competitorsParkingsChangesArrow,
        competitorsParkingsChangesArrowHeads,
        competitorsParkingsChangesSigns,
        competitorsParkingsChangesMovedDot,
    ],
    availableFor: [ V.SUPER_ADMIN ],
};
// Переброски флота конкурентов (DA-13611)
// Источник: martin.competitors_fleet_transfers через функцию competitors_fleet_transfers_mvt
// Линейный слой: ширина — кол-во ТС, цвет — оператор; стрелки и метки — additionalLayers
const fleetTransfersCompetitorsArrows = {
    id: 'Переброски флота (конкуренты)_arrows',
    label: defineMessage({ message: 'Переброски флота' }),
    type: 'symbol',
    'source-layer': 'martin.competitors_fleet_transfers_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_fleet_transfers_mvt.json`,
    },
    layout: {
        visibility: 'none',
        'symbol-placement': 'line',
        'text-field': '▶',
        'text-size': [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'num_vehicles' ] ], 0, 8, 50, 10, 200, 13, 500, 17, 1000, 22 ],
        'symbol-spacing': 120,
        'text-keep-upright': false,
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'text-rotation-alignment': 'map',
    },
    paint: {
        'text-color': operatorColorCase(),
        'text-halo-color': 'rgba(255,255,255,0.7)',
        'text-halo-width': 1,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
    availableFor: [ V.SUPER_ADMIN ],
};
// Стрелка на конце дуги (в точке «Куда»)
const fleetTransfersCompetitorsEndArrow = {
    id: 'Переброски флота (конкуренты)_end_arrow',
    label: defineMessage({ message: 'Переброски флота' }),
    type: 'symbol',
    'source-layer': 'martin.competitors_fleet_transfers_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_fleet_transfers_mvt.json`,
    },
    layout: {
        visibility: 'none',
        'symbol-placement': 'line-center',
        'text-field': '▶',
        'text-size': [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'num_vehicles' ] ], 0, 12, 50, 15, 200, 19, 500, 26, 1000, 32 ],
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-rotation-alignment': 'map',
    },
    paint: {
        'text-color': operatorColorCase(),
        'text-halo-color': 'rgba(255,255,255,0.85)',
        'text-halo-width': 2,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
    availableFor: [ V.SUPER_ADMIN ],
};
const fleetTransfersCompetitorsLabels = {
    id: 'Переброски флота (конкуренты)_labels',
    label: defineMessage({ message: 'Переброски флота' }),
    type: 'symbol',
    'source-layer': 'martin.competitors_fleet_transfers_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_fleet_transfers_mvt.json`,
    },
    minzoom: 5,
    layout: {
        visibility: 'none',
        'symbol-placement': 'line-center',
        'text-field': [
            'concat',
            [ 'case',
                [ '>', [ 'to-number', [ 'get', 'num_scooters' ] ], 0 ],
                [ 'concat', '🛴', [ 'to-string', [ 'get', 'num_scooters' ] ] ],
                '' ],
            [ 'case',
                [ '>', [ 'to-number', [ 'get', 'num_bicycles' ] ], 0 ],
                [ 'concat', ' 🚲', [ 'to-string', [ 'get', 'num_bicycles' ] ] ],
                '' ],
        ],
        'text-size': 10,
        'text-allow-overlap': false,
        'text-ignore-placement': false,
    },
    paint: {
        'text-color': '#333',
        'text-halo-color': '#fff',
        'text-halo-width': 2,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
    availableFor: [ V.SUPER_ADMIN ],
};
const fleetTransfersCompetitors = {
    id: 'Переброски флота (конкуренты)',
    label: defineMessage({ message: 'Переброски флота' }),
    exportTitle: defineMessage({ message: 'Переброски флота по операторам' }),
    type: 'line',
    'source-layer': 'martin.competitors_fleet_transfers_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.competitors_fleet_transfers_mvt.json`,
    },
    layout: {
        visibility: 'none',
        'line-cap': 'round',
        'line-join': 'round',
    },
    paint: {
        'line-color': operatorColorCase(),
        'line-width': [
            'interpolate', [ 'linear' ],
            [ 'to-number', [ 'get', 'num_vehicles' ] ],
            0, 1,
            50, 3,
            200, 6,
            500, 10,
            1000, 16,
        ],
        'line-opacity': 0.75,
    },
    filter: [ 'match', [ 'to-string', [ 'get', 'operator' ] ], [ 'none' ], true, false ],
    additionalLayers: [ fleetTransfersCompetitorsArrows, fleetTransfersCompetitorsEndArrow, fleetTransfersCompetitorsLabels ],
    availableFor: [ V.SUPER_ADMIN ],
};
const CHBdailyCountOperationArea = {
    id: 'Среднесуточное количество самокатов в статусе CHANGE_BATTERY в операционных зонах',
    label: defineMessage({ message: 'Среднесуточное количество самокатов в статусе CHANGE_BATTERY в операционных зонах' }),
    type: 'fill',
    'source-layer': 'martin.chb_daily_count_operation_area_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.chb_daily_count_operation_area_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'chb_daily_operation_zone' ] ],
            0,
            '#11c1e8',
            200,
            '#c572ee',
            400,
            '#f6515c',
        ],
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ScootersCount = {
    /*
        Parking scooter count
        updating every 2 mins by DAG dwh_rebalancing_updater
        https://1a11cfd0-6f1d-4642-b3ac-eee2bbf97102.c0.eu-west-1.airflow.amazonaws.com/dags/dwh_rebalancing_updater/grid
    */
    id: 'Количество самокатов на парковках',
    label: defineMessage({ message: 'Количество самокатов на парковках' }),
    type: 'circle',
    'source-layer': 'martin.parkings_actual_count_capacity_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parkings_actual_count_capacity_mvt.json`,
    },
    minzoom: 9,
    layout: { visibility: 'none' },
    paint: {
        'circle-opacity': 0.7,
        'circle-stroke-color': [ 'case',
            [ 'all',
                [ '==', [ 'to-number', [ 'get', 'actual_count' ] ], 1 ],
            ], 'black',
            'black' ],
        'circle-stroke-width': [ 'case',
            [ 'any',
                [ '==', [ 'to-number', [ 'get', 'actual_count' ] ], 1 ],
                [ '==', [ 'to-number', [ 'get', 'actual_count' ] ], 0 ],
            ], 1.5,
            0.7 ],
        'circle-color': [
            'interpolate',
            [ 'linear' ],
            [
                'get',
                'actual_count',
            ],
            0,
            'white',
            1,
            'yellow',
            2,
            '#11c1e8',
            9,
            '#c572ee',
            40,
            '#f6515c',
            50,
            '#f01625',
        ],
        'circle-radius': [
            'interpolate',
            [ 'linear' ],
            [
                'get',
                'actual_count',
            ],
            0,
            2.5,
            1,
            3,
            2,
            4,
            9,
            5,
            50,
            8,
        ],
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// DA-14276: функция принимает период '1d' | '7d' | '30d'
// Для 1д используется абсолютный счётчик (chb_count_1d),
// для 7д и 30д — медиана среднесуточных переходов
const CHB_PERIOD_FIELD = {
    '1d':  'chb_count_1d',
    '7d':  'chb_count_7d_median',
    '30d': 'chb_count_30d_median',
};
const chbOnParkingsLayer = (period = '30d') => {
    const field = CHB_PERIOD_FIELD[period] ?? CHB_PERIOD_FIELD['30d'];
    return {
        id: 'Среднесуточное количество переходов в CHANGE_BATTERY по парковкам',
        label: defineMessage({ message: 'Среднесуточное количество переходов в CHANGE_BATTERY по парковкам' }),
        type: 'circle',
        'source-layer': 'martin.chb_status_parking_count_last30days_mvt',
        source: {
            type: 'vector',
            url: `${V.API_GW_URL}/rpc/martin.chb_status_parking_count_last30days_mvt.json`,
        },
        minzoom: 9,
        layout: { visibility: 'none' },
        paint: {
            'circle-opacity': 0.7,
            'circle-stroke-color': 'black',
            'circle-stroke-width': 0.7,
            'circle-color': [
                'interpolate',
                [ 'linear' ],
                [ 'get', field ],
                0,
                'white',
                1,
                '#11c1e8',
                9,
                '#c572ee',
                40,
                '#f6515c',
                50,
                '#f01625',
            ],
            'circle-radius': [
                'interpolate',
                [ 'linear' ],
                [ 'get', field ],
                0,
                2,
                5,
                5,
                20,
                7,
                70,
                9,
            ],
        },
        availableFor: [ V.ADMIN, V.DESIGNER ],
    };
};
const CHBonParkings = chbOnParkingsLayer();
const aggHexRent = {
    id: 'Сглаженные показатели аренды',
    label: defineMessage({ message: 'Сглаженные показатели аренды' }),
    type: 'fill',
    'source-layer': 'martin.agg_hex_rents_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.agg_hex_rents_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'percentile' ] ],
            0,
            '#0b0405',
            1,
            '#28192f',
            2,
            '#3b2f5f',
            3,
            '#3f498e',
            4,
            '#366b9f',
            5,
            '#348ba6',
            6,
            '#38abac',
            7,
            '#55caad',
            8,
            '#a3e0ba',
            9,
            '#def5e5',
        ],
        'fill-opacity': 0.65,
        'fill-outline-color': 'black',
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const aggHexAttention = {
    id: 'Гексагоны для парковок',
    label: defineMessage({ message: 'Гексагоны для парковок' }),
    type: 'fill',
    'source-layer': 'martin.hexes_for_attention_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.hexes_for_attention_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'hex_index_norm' ] ],
            0,
            '#0b0405',
            0.1,
            '#28192f',
            0.2,
            '#3b2f5f',
            0.3,
            '#3f498e',
            0.4,
            '#366b9f',
            0.5,
            '#348ba6',
            0.6,
            '#38abac',
            0.7,
            '#55caad',
            0.8,
            '#a3e0ba',
            0.9,
            '#def5e5',
        ],
        'fill-opacity': 0.65,
        'fill-outline-color': 'black',
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const trackStarts = {
    id: 'Агрегированные старты треков',
    label: defineMessage({ message: 'Агрегированные старты' }),
    exportTitle: ({ filters }) => {
        const days = filters.startTrackDays;
        return days
            ? i18n._(defineMessage({ message: 'Агрегированные старты за {days} дней' }), { days })
            : '';
    },
    type: 'fill',
    'source-layer': 'martin.function_start_hex_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.function_start_hex_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'start_count_7d' ],
            getStartFinishTrackColorScope(7)[0],
            'hsla(301, 98%, 24%, 0)',
            getStartFinishTrackColorScope(7)[1],
            '#0d0887',
            getStartFinishTrackColorScope(7)[2],
            'rgb(127,3,168)',
            getStartFinishTrackColorScope(7)[3],
            'rgb(203,71,120)',
            getStartFinishTrackColorScope(7)[4],
            '#f07f4f',
            getStartFinishTrackColorScope(7)[5],
            '#f0f921',
        ],
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const trackStartsCompetitors = {
    id: 'Агрегированные старты (конкуренты)',
    label: defineMessage({ message: 'Агрегированные старты' }),
    // Заголовок экспорта (DA-15019): период в днях (mapFilters.CompetitorsDays).
    exportTitle: ({ filters }) => {
        const days = filters.CompetitorsDays;

        return days
            ? i18n._(defineMessage({ message: 'Агрегированные старты по операторам за {days} дней' }), { days })
            : i18n._(defineMessage({ message: 'Агрегированные старты по операторам' }));
    },
    type: 'fill',
    'source-layer': 'martin.function_start_hex_with_competitors_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.function_start_hex_with_competitors_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'leadership_7d',
            stops: [
                [
                    'whoosh',
                    V.CompetitorsLegend[0].color,
                ],
                [
                    'yandex',
                    V.CompetitorsLegend[1].color,
                ],
                [
                    'urent',
                    V.CompetitorsLegend[2].color,
                ],
                [
                    'jet',
                    V.CompetitorsLegend[3].color,
                ],
                [
                    'eleven',
                    V.CompetitorsLegend[4].color,
                ],
                [
                    'lime',
                    V.CompetitorsLegend[5].color,
                ],
                [
                    'lite',
                    V.CompetitorsLegend[6].color,
                ],
            ],
            'default': 'grey',
        },
        'fill-opacity': 0.6,
    },
    filter: [
        '>=',
        [
            '+',
            [ 'to-number', [ 'get', 'starts_whoosh_7d' ] ],
            [ 'to-number', [ 'get', 'starts_urent_7d' ] ],
            [ 'to-number', [ 'get', 'starts_yandex_7d' ] ],
        ],
        10,
    ],
    availableFor: [ V.SUPER_ADMIN ],
};
const trackDiffStartFinish = {
    id: 'Агрегированая разница стартов и финишей за 7 дней',
    label: defineMessage({ message: 'Агрегированая разница стартов и финишей за 7 дней' }),
    type: 'fill',
    'source-layer': 'martin.agg_start_finish_hex_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.agg_start_finish_hex_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 10,
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'sf_diff_7d' ],
            -50,
            '#d7191c',
            -5,
            '#fdae61',
            0,
            '#ffffbf',
            5,
            '#abd9e9',
            50,
            '#2c7bb6',
        ],
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
    filter: [ 'all',
        [ '>', [ 'to-number', [ 'get', 'start_count_7d' ] ], 0 ],
        [ '>', [ 'to-number', [ 'get', 'finish_count_7d' ] ], 0 ],
    ],
};
const populationKontur = {
    id: 'Население',
    label: defineMessage({ message: 'Население' }),
    type: 'fill',
    // Функция с проверкой доступа по регионам вместо table_source Мартина (DA-13733)
    'source-layer': 'martin.population_h3_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.population_h3_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 10,
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'population' ],
            500,
            '#30123b',
            1000,
            '#4664da',
            2000,
            '#1ad2d2',
            3000,
            '#88ff4e',
            4000,
            '#d4e735',
            5000,
            '#fea732',
            10000,
            '#7a0403',
        ],
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const trackFinish = {
    id: 'Агрегированные финиши треков',
    label: defineMessage({ message: 'Агрегированные финиши' }),
    exportTitle: ({ filters }) => {
        const days = filters.finishTrackDays;
        return days
            ? i18n._(defineMessage({ message: 'Агрегированные финиши за {days} дней' }), { days })
            : '';
    },
    type: 'fill',
    'source-layer': 'martin.function_finish_hex_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.function_finish_hex_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'finish_count_7d' ],
            getStartFinishTrackColorScope(7)[0],
            'hsla(301, 98%, 24%, 0)',
            getStartFinishTrackColorScope(7)[1],
            '#0d0887',
            getStartFinishTrackColorScope(7)[2],
            'rgb(127,3,168)',
            getStartFinishTrackColorScope(7)[3],
            'rgb(203,71,120)',
            getStartFinishTrackColorScope(7)[4],
            '#f07f4f',
            getStartFinishTrackColorScope(7)[5],
            '#f0f921',
        ],
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ScootersDowntime = {
    id: 'Агрегированный простой самокатов',
    label: defineMessage({ message: 'Агрегированный простой самокатов' }),
    exportTitle: ({ filters }) => {
        const opt = V.DowntimeParkingsPeriodFilterValues
            .find(o => String(o.value) === String(filters.aggScootersDowntimePeriodStatuses));
        return opt
            ? i18n._(defineMessage({ message: 'Простой самокатов за {period}' }), { period: i18n._(opt.label) })
            : '';
    },
    type: 'circle',
    'source-layer': 'martin.downtime_sb_hex_week_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.downtime_sb_hex_week_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 7,
    paint: {
        'circle-opacity': 0.9,
        'circle-color':
            [ 'case',
                [ 'all',
                    [ '<', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 2 ],
                ], 'green',
                [ 'all',
                    [ '>=', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 2 ],
                    [ '<', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 4 ],
                ], 'lightgreen',
                [ 'all',
                    [ '>=', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 2 ],
                    [ '<', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 6 ],
                ], 'white',
                [ 'all',
                    [ '>=', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 6 ],
                    [ '<', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 9 ],
                ], 'yellow',
                [ 'all',
                    [ '>=', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 9 ],
                    [ '<', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 12 ],
                ], 'orange',
                [ 'all',
                    [ '>=', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 12 ],
                    [ '<', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 24 ],
                ], '#b35500',
                [ 'all',
                    [ '>=', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 24 ],
                    [ '<', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 48 ],
                ], 'red',
                [ 'all',
                    [ '>=', [ 'to-number', [ 'get', 'avg_sb_time' ] ], 48 ],
                ], '#8B0000',
                'red',
            ],
        'circle-stroke-color': 'black',
        'circle-stroke-width': 1,
        'circle-radius':
            [
                'interpolate',
                [ 'linear' ],
                [ 'to-number', [ 'get', 'scooters_count' ] ],
                1,
                4,
                8,
                5,
                15,
                6,
                50,
                8,
            ],
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
    filter: [ '==', [ 'to-number', [ 'get', 'period_' ] ], 7 ],
};
const userTaps = {
    id: 'Тапы пользователей',
    label: defineMessage({ message:'Тапы пользователей' }),
    type: 'fill',
    'source-layer': 'martin.taps_opens_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.taps_opens_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 10,
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'taps_sum_7d' ] ],
            getUserTapsColorScope(7)[0],
            'hsla(242, 89%, 28%, 0.3)',
            getUserTapsColorScope(7)[1],
            '#792282',
            getUserTapsColorScope(7)[2],
            '#c23b75',
            getUserTapsColorScope(7)[3],
            '#f4695c',
            getUserTapsColorScope(7)[4],
            '#feb47b',
            getUserTapsColorScope(7)[5],
            '#fcfdbf',
        ],
        'fill-opacity': 0.7,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const userOpens = {
    id: 'Открытия приложения',
    label: defineMessage({ message:'Открытия приложения' }),
    exportTitle: ({ filters }) => {
        const days = filters.opensDays;
        return days
            ? i18n._(defineMessage({ message: 'Открытия приложения за {days} дней' }), { days })
            : '';
    },
    type: 'fill',
    'source-layer': 'martin.taps_opens_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.taps_opens_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 10,
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'opens_sum_7d' ] ],
            getUserTapsColorScope(7)[0],
            'hsla(242, 89%, 28%, 0.3)',
            getUserTapsColorScope(7)[1],
            '#792282',
            getUserTapsColorScope(7)[2],
            '#c23b75',
            getUserTapsColorScope(7)[3],
            '#f4695c',
            getUserTapsColorScope(7)[4],
            '#feb47b',
            getUserTapsColorScope(7)[5],
            '#fcfdbf',
        ],
        'fill-opacity': 0.7,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const userTapsZeroStarts = {
    id: 'Тапы пользователей без поездок',
    label: defineMessage({ message:'Тапы пользователей без поездок' }),
    type: 'fill',
    'source-layer': 'martin.taps_opens_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.taps_opens_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 10,
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'taps_sum_7d' ] ],
            0,
            'hsla(242, 89%, 28%, 0.3)',
            30,
            '#792282',
            70,
            '#c23b75',
            100,
            '#f4695c',
            150,
            '#feb47b',
            300,
            '#fcfdbf',
        ],
        'fill-opacity': 0.7,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
    filter: [ '==', [ 'to-number', [ 'get', 'starts_sum_7d' ] ], 0 ],
};
const regToTripConversion = {
    id: 'Конверсия из регистраций в старты',
    label: defineMessage({ message: 'Конверсия из регистраций в старты' }),
    type: 'fill',
    'source-layer': 'martin.reg_to_trip_conversion_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.reg_to_trip_conversion_mvt.json`,
        requiredQueryParams: [ 'h3_zoom' ],
    },
    layout: { visibility: 'none' },
    minzoom: 0,
    maxzoom: 24,
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'share' ] ],
            0,    '#67000d',
            0.1,  '#bd0026',
            0.2,  '#f03b20',
            0.3,  '#fd8d3c',
            0.4,  '#fecc5c',
            0.5,  '#ffffb2',
        ],
        'fill-opacity': 0.8,
    },
    legend: REG_TO_TRIP_CONVERSION_LEGEND,
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const openRide = {
    id: 'Открытий к стартам',
    label: defineMessage({ message:'Открытий к стартам' }),
    exportTitle: ({ filters }) => {
        const days = filters.OpensToStartDays;
        return days
            ? i18n._(defineMessage({ message: 'Отношение открытий к стартам за {days} дней' }), { days })
            : '';
    },
    type: 'fill',
    'source-layer': 'martin.taps_opens_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.taps_opens_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 10,
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'open_to_ride_7d' ] ],
            0,
            '#03559F',
            3,
            '#792282',
            6,
            '#c23b75',
            12,
            '#f4695c',
            50,
            '#feb47b',
            100,
            '#fcfdbf',
        ],
        'fill-opacity': 0.6,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
    filter: [ '>', [ 'to-number', [ 'get', 'starts_sum_7d' ] ], 3 ],
};
const scootersSpeed = {
    id: 'Медианная скорость (вчера)',
    label: defineMessage({ message: 'Медианная скорость' }),
    type: 'fill',
    'source-layer': 'martin.iot_track_agg_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.iot_track_agg_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'speed_median' ],
            10,
            '#02ca02',
            13,
            '#66bd6f',
            15,
            '#c8fde7',
            20,
            '#767676',
            23,
            '#404040',
        ],
        'fill-opacity': 0.7,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const HistoryUserTapsOpens = {
    id: 'Исторические тапы и открытия',
    label: defineMessage({ message:'Тапы и открытия (август)' }),
    type: 'fill',
    'source-layer': 'martin.taps_opens_hist_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.taps_opens_hist_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 10,
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'taps_sum_2026d' ] ],
            getUserTapsColorScope(7)[0],
            'hsla(242, 89%, 28%, 0.3)',
            getUserTapsColorScope(7)[1],
            '#792282',
            getUserTapsColorScope(7)[2],
            '#c23b75',
            getUserTapsColorScope(7)[3],
            '#f4695c',
            getUserTapsColorScope(7)[4],
            '#feb47b',
            getUserTapsColorScope(7)[5],
            '#fcfdbf',
        ],
        'fill-opacity': 0.7,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const roadIndex = {
    id: 'Неровность дороги (вчера)',
    label: defineMessage({ message: 'Неровность дороги' }),
    type: 'fill',
    'source-layer': 'martin.iot_track_agg_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.iot_track_agg_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'get', 'ri_median' ],
            0,
            'hsl(242, 89%, 20%)',
            // 0.2,
            // '#46039f',
            // 0.3,
            // '#7300a8',
            // 0.4,
            // '#9c179e',
            0.5,
            '#be3785',
            // 0.6,
            // '#d8576c',
            0.7,
            '#f0f921',
            // 0.8,
            // 'hsl(45, 98%, 57%)',
            // 1,
            // '#f0f921',
        ],
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const clusteredLocalMoransTaps7days = {
    id: 'Кластеризированные тапы (7 дней)',
    label: defineMessage({ message: 'Кластеризированные тапы (7 дней)' }),
    type: 'fill',
    minzoom: 8,
    'source-layer': 'martin.taps_clustering_moran_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.taps_clustering_moran_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'labels_taps_sum_7d',
            stops: [
                [
                    'not_Sign',
                    'grey',
                ],
                [
                    'LH',
                    '#89CFF0',
                ],
                [
                    'HH',
                    'red',
                ],
                [
                    'HL',
                    '#ffb2b2',
                ],
                [
                    'LL',
                    '#0096FF',
                ],
            ],
            'default': 'yellow',
        },
        'fill-opacity': 0.5,
        'fill-outline-color': 'black',
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const clusteredLocalMoransOpens7days = {
    id: 'Кластеризированные открытия (7 дней)',
    label: defineMessage({ message: 'Кластеризированные открытия (7 дней)' }),
    type: 'fill',
    minzoom: 8,
    'source-layer': 'martin.opens_clustering_moran_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.opens_clustering_moran_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'labels_opens_sum_7d',
            stops: [
                [
                    'not_Sign',
                    'grey',
                ],
                [
                    'LH',
                    '#89CFF0',
                ],
                [
                    'HH',
                    'red',
                ],
                [
                    'HL',
                    '#ffb2b2',
                ],
                [
                    'LL',
                    '#0096FF',
                ],
            ],
            'default': 'yellow',
        },
        'fill-opacity': 0.5,
        'fill-outline-color': 'black',
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const trasnportIsochrones = {
    id: 'Изохроны доступности ОТ',
    label: defineMessage({ message: 'Изохроны доступности ОТ' }),
    type: 'fill',
    'source-layer': 'martin.transport_isochrones_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.transport_isochrones_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': '#9F2B68',
        'fill-opacity': 0.5,
    },
    // additionalLayers: [ poiTransport ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const operationAreasCorrespondingMatrix = {
    id: 'deck:operation_areas_corresponding_matrix',
    label: defineMessage({ message: 'Матрица корреспонденций' }),
    availableFor: [ V.ADMIN ],
};
const failedTrips = {
    id: 'Неудачные старты (7 дней)',
    label: defineMessage({ message:'Неудачные старты (7 дней)' }),
    type: 'fill',
    'source-layer': 'martin.od_matrix_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.od_matrix_mvt.json`,
    },
    layout: { visibility: 'none' },
    minzoom: 10,
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'perc_ftrips' ] ],
            0,
            'hsla(242, 89%, 28%, 0.6)',
            0.1,
            '#792282',
            0.3,
            '#c23b75',
            0.5,
            '#f4695c',
            0.7,
            '#feb47b',
            1,
            '#fcfdbf',
        ],
        'fill-opacity': 1,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ParkingsBookings = {
    id: 'Бронирование самокатов на парковке',
    label: defineMessage({ message: 'Бронирование самокатов на парковке' }),
    // Заголовок экспорта: добавляем выбранный период (parkingsBookingsDateStatuses).
    exportTitle: ({ filters }) => {
        const opt = V.ParkingsBookingsTypes.find(o => o.value === filters.parkingsBookingsDateStatuses);
        return opt
            ? i18n._(defineMessage({ message: 'Бронирование самокатов на парковке — {period}' }), { period: i18n._(opt.label) })
            : '';
    },
    type: 'circle',
    'source-layer': 'martin.parkings_bookings_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parkings_bookings_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-color': [
            'case',
            [ 'all',
                [ '==', [ 'get', 'date_' ], 15 ],
            ],
            [
                'interpolate',
                [ 'linear' ],
                [ 'to-number', [ 'get', 'ttl_booked' ] ],
                0,
                'white',
                1,
                '#440154',
                2,
                '#414487',
                3,
                '#2a788e',
                4,
                '#22a884',
                5,
                '#7ad151',
                6,
                '#fde725',
            ],
            [ 'all',
                [ '==', [ 'get', 'date_' ], 1 ],
            ],
            [
                'interpolate',
                [ 'linear' ],
                [ 'to-number', [ 'get', 'ttl_booked' ] ],
                3,
                '#440154',
                6,
                '#414487',
                10,
                '#2a788e',
                15,
                '#22a884',
                20,
                '#7ad151',
                30,
                '#fde725',
            ],
            [ 'all',
                [ '==', [ 'get', 'date_' ], 7 ],
            ],
            [
                'interpolate',
                [ 'linear' ],
                [ 'to-number', [ 'get', 'ttl_booked' ] ],
                10,
                '#440154',
                20,
                '#414487',
                40,
                '#2a788e',
                60,
                '#22a884',
                80,
                '#7ad151',
                120,
                '#fde725',
            ],
            'blue',
        ],
        'circle-radius': [
            'case',
            [ 'all',
                [ '==', [ 'get', 'date_' ], 15 ],
            ],
            [
                'interpolate',
                [ 'linear' ],
                [ 'to-number', [ 'get', 'ttl_booked' ] ],
                1,
                4,
                2,
                5,
                3,
                6,
                4,
                7,
                5,
                8,
                6,
                9,
            ],
            [ 'all',
                [ '==', [ 'get', 'date_' ], 1 ],
            ],
            [
                'interpolate',
                [ 'linear' ],
                [ 'to-number', [ 'get', 'ttl_booked' ] ],
                3,
                4,
                6,
                5,
                10,
                6,
                15,
                7,
                20,
                8,
                30,
                9,
            ],
            [ 'all',
                [ '==', [ 'get', 'date_' ], 7 ],
            ],
            [
                'interpolate',
                [ 'linear' ],
                [ 'to-number', [ 'get', 'ttl_booked' ] ],
                10,
                4,
                20,
                6,
                40,
                6,
                60,
                7,
                80,
                8,
                150,
                9,
            ],
            4,
        ],
        'circle-opacity': 0.8,
        'circle-stroke-color': 'black',
        'circle-stroke-width': 0.7,
    },
    filter: [ '==', [ 'to-number', [ 'get', 'date_' ] ], 1 ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const AggregatedParkingAlerts = {
    id: 'Агрегированные алерты',
    label: defineMessage({ message: 'Агрегированные алерты' }),
    type: 'fill',
    minzoom: 8,
    'source-layer': 'martin.parking_attempts_hex_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parking_attempts_hex_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': {
            base: 1,
            type: 'categorical',
            property: 'labels_n_attempts',
            stops: [
                [
                    'not_Sign',
                    'grey',
                ],
                [
                    'LH',
                    '#89CFF0',
                ],
                [
                    'HH',
                    'red',
                ],
                [
                    'HL',
                    '#ffb2b2',
                ],
                [
                    'LL',
                    '#0096FF',
                ],
            ],
            'default': 'yellow',
        },
        'fill-opacity': 0.5,
    },
    filter:[ 'all',
        [ '==', [ 'get', 'permission' ], 1 ],
        [ '==', [ 'get', 'parking_choice' ], 1  ],
    ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ParkingAlertsPointsOutlinesLabelsText = {
    id: 'Алерты завершения парковки_label_text',
    label: defineMessage({ message: 'Алерты завершения парковки_label_text' }),
    type: 'symbol',
    'source-layer': 'martin.parkings_alerts_points_mvt',
    minzoom: 12,
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parkings_alerts_points_mvt.json`,
        promoteId: 'id',
    },
    layout: {
        visibility: 'none',
        'text-field': [ 'get', 'n_attemp' ],
        'text-size': 10,
        'text-anchor': 'bottom',
        'text-offset': [ 0, -1 ],
    },
    paint: {
        // 'text-halo-color': 'white',
        'text-color': [
            'case',
            [ 'boolean', [ 'feature-state', 'selected' ], false ],
            'black', // or any other color for selected features
            'rgba(0, 0, 0, 0)', // set to transparent for non-selected features
        ],
        'text-halo-color': [
            'case',
            [ 'boolean', [ 'feature-state', 'selected' ], false ],
            'white',
            'transparent',
        ],
        'text-halo-width': [
            'case',
            [ 'boolean', [ 'feature-state', 'selected' ], false ],
            1,
            0,
        ],
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const ParkingAlertsPointsOutlines = {
    id: 'Алерты завершения парковки_outline',
    label: defineMessage({ message: 'Алерты завершения парковки_outline' }),
    type: 'circle',
    'source-layer': 'martin.parkings_alerts_points_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parkings_alerts_points_mvt.json`,
        promoteId: 'id',
    },
    layout: {
        visibility: 'none',
    },
    paint: {
        'circle-opacity': 0.0,
        'circle-color': {
            base: 1,
            type: 'categorical',
            property: 'parking_choice',
            stops: [
                [
                    'DEVICE',
                    '#E34234',
                ],
                [
                    'PHONE',
                    '#6495ED',
                ],
            ],
            default: 'black',
        },
        'circle-radius': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'distance' ] ],
            // Value, radius
            25, 1,
            30, 2,
            100, 3,
            400, 5,
        ],
        'circle-stroke-width': [
            'case',
            [ 'boolean', [ 'feature-state', 'selected' ], false ],
            2,
            0,
        ],
        'circle-stroke-color': 'white',
    },
    availableFor: [ V.ADMIN ],
};
// Слой-подсветка: кольцо вокруг алертов одной парковки. Стартовый фильтр не
// матчит ничего — фильтр выставляется из Map.jsx по кнопке в попапе.
const ParkingAlertsPointsHighlight = {
    id: 'Алерты завершения парковки_highlight',
    label: defineMessage({ message: 'Алерты завершения парковки_highlight' }),
    type: 'circle',
    'source-layer': 'martin.parkings_alerts_points_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parkings_alerts_points_mvt.json`,
        promoteId: 'id',
    },
    layout: {
        visibility: 'none',
    },
    // -1 — заведомо несуществующий parking_id: подсветка пустая, пока не нажата кнопка
    filter: [ '==', [ 'get', 'parking_id' ], -1 ],
    paint: {
        'circle-color': 'rgba(0, 0, 0, 0)',
        'circle-radius': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'distance' ] ],
            25, 4,
            30, 5,
            100, 6,
            400, 8,
        ],
        'circle-stroke-color': '#FFD400',
        'circle-stroke-width': 3,
    },
    availableFor: [ V.ADMIN ],
};
const ParkingAlertsPoints = {
    id: 'Алерты завершения парковки',
    label: defineMessage({ message: 'Алерты завершения парковки' }),
    type: 'circle',
    'source-layer': 'martin.parkings_alerts_points_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.parkings_alerts_points_mvt.json`,
        promoteId: 'id',
    },
    layout: {
        visibility: 'none',
    },
    paint: {
        'circle-opacity': 0.7,
        'circle-color': {
            base: 1,
            type: 'categorical',
            property: 'parking_choice',
            stops: [
                [
                    'DEVICE',
                    '#E34234',
                ],
                [
                    'PHONE',
                    '#6495ED',
                ],
            ],
            default: 'black',
        },
        'circle-radius': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'distance' ] ],
            // Value, radius
            25, 1,
            30, 2,
            100, 3,
            400, 5,
        ],
        'circle-stroke-width': 1,
    },
    filter: [ 'all',
        [ '<=', [ 'to-number', [ 'get', 'period_' ] ], 3  ],
        [ 'match', [ 'get', 'parking_choice' ], [ 'PHONE', 'DEVICE' ], true, false ],
    ],
    additionalLayers: [
        ParkingAlertsPointsOutlines,
        ParkingAlertsPointsOutlinesLabelsText,
        ParkingAlertsPointsHighlight,
    ],
    availableFor: [ V.ADMIN ],
};
const dynamicTariffZonesLines = {
    id: 'Особые зоны динамического тарифа_lines',
    label: defineMessage({ message: 'Особые зоны динамического тарифа_lines' }),
    type: 'line',
    'source-layer': 'martin.dynamic_tariff_zones_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.dynamic_tariff_zones_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'line-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'zone_code' ] ],
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[0].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[0].color,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[1].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[1].color,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[2].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[2].color,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[3].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[3].color,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[4].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[4].color,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[5].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[5].color,
        ],
        'line-opacity': 0.9,
        'line-width': 3,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const dynamicTariffZones = {
    id: 'Особые зоны динамического тарифа',
    label: defineMessage({ message: 'Зоны динамического тарифа' }),
    type: 'fill',
    'source-layer': 'martin.dynamic_tariff_zones_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.dynamic_tariff_zones_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'zone_code' ] ],
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[0].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[0].color,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[1].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[1].color,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[2].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[2].color,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[3].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[3].color,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[4].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[4].color,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[5].value,
            DYNAMIC_TARIFF_ZONES_STYLE.zoneCode.items[5].color,
        ],
        'fill-opacity': 0.5,
    },
    additionalLayers: [ dynamicTariffZonesLines ],
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
const dynamicTariffH3 = {
    id: 'Динамический тариф_h3',
    label: defineMessage({ message: 'Динамический тариф' }),
    type: 'fill',
    'source-layer': 'martin.dynamic_tariff_score_h3_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.dynamic_tariff_score_h3_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'score' ] ],
            1,
            'hsla(240, 100%, 27%, 0.4)',
            3,
            '#0d0887',
            6,
            'rgb(127,3,168)',
            10,
            'rgb(203,71,120)',
            13,
            '#f07f4f',
            17,
            '#f0f921',
        ],
        'fill-opacity': 0.5,
    },
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// Panels ----------------------------------------------------------
// ─────────────────────────────────────────────────────────────────────────────
// Фактический ребаланс
// MVT-функция: martin.fact_rebalance_parkings_mvt (Aurora PostGIS)
// Таблица данных: martin.fact_rebalance_parkings
// Поля: parking_id, period_index (0–7), rebalance_from, rebalance_to,
//        action ('took'|'brought'|'both'), region_id
// Фильтрация: по period_index (client-side) и дате (queryParam 'date')
// Паттерн: аналогичен startsByHourLayer — функция, принимающая selectedPeriod
// ─────────────────────────────────────────────────────────────────────────────
const factRebalanceLayer = (selectedPeriod = 0) => {
    const [ took, brought, both ] = [
        FACT_REBALANCE_COLORS_STYLE.actions[0].color,
        FACT_REBALANCE_COLORS_STYLE.actions[1].color,
        FACT_REBALANCE_COLORS_STYLE.actions[2].color,
    ];

    return {
        id: 'Фактический ребаланс',
        label: defineMessage({ message: 'Фактический ребаланс' }),
        // Заголовок экспорта: дата ребаланса (map-слайс selectedRebalanceDate, 'YYYY-MM-DD').
        // В режиме «Временной интервал» (selectedRebalanceDayMode === 'period') к дате
        // добавляется выбранный интервал суток (selectedRebalancePeriod → FACT_REBALANCE_PERIODS).
        exportTitle: ({ controls, locale }) => {
            const date = controls.selectedRebalanceDate;
            if (!date) {
                return '';
            }
            const [ y, m, d ] = date.split('-').map(Number);
            const formatted = new Intl.DateTimeFormat(locale || 'ru', { day: 'numeric', month: 'long', year: 'numeric' })
                .format(new Date(y, m - 1, d));
            if (controls.selectedRebalanceDayMode === 'period') {
                const periodDef = FACT_REBALANCE_PERIODS[controls.selectedRebalancePeriod] ?? FACT_REBALANCE_PERIODS[0];
                const interval = i18n._(periodDef.label);
                return i18n._(defineMessage({ message: 'Фактический ребаланс на {date}, {interval}' }), { date: formatted, interval });
            }
            return i18n._(defineMessage({ message: 'Фактический ребаланс на {date}' }), { date: formatted });
        },
        type: 'circle',
        'source-layer': 'martin.fact_rebalance_parkings_mvt',
        source: {
            id: 'martin.fact_rebalance_mvt',
            type: 'vector',
            url: `${V.API_GW_URL}/rpc/martin.fact_rebalance_parkings_mvt.json`,
            promoteId: 'parking_id',
            requiredQueryParams: [ 'rebalance_date' ],
        },
        layout: { visibility: 'none' },
        // Фильтр по ролям/minCount устанавливается через buildRebalanceFilter.
        // period_index фильтруется на сервере через query_param 'period'.
        filter: [ 'all' ], // пропускаем всё; реальный фильтр ставится в initLayer/FactRebalanceFilter
        paint: {
            // Цвет: только увезли → красный, только привезли → зелёный, оба → синий
            'circle-color': [ 'match', [ 'get', 'action' ],
                'took',    took,
                'brought', brought,
                'both',    both,
                '#cccccc',
            ],
            // Радиус по суммарному числу перевозок: sqrt(total) * 2, ограничен 4–20px
            'circle-radius': [ 'interpolate', [ 'linear' ],
                [ '+',
                    [ 'to-number', [ 'get', 'rebalance_from' ], 0 ],
                    [ 'to-number', [ 'get', 'rebalance_to'   ], 0 ],
                ],
                1,  4,
                4,  8,
                9,  12,
                16, 16,
                36, 20,
            ],
            'circle-opacity': 0.85,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1,
        },
        minzoom: 4,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    };
};
const rebalancing = [
    ClassifiedParkings,
    ClassifiedParkingsDayPart,
    ClassifiedParkingsRebalanced,
    ClassifiedParkingsDayPartRebalanced,
    ScootersCount,
    ScootersDowntime,
    parkingDensity,
    parkingsDowntime48h,
    // DA-14276: единый слой CHB с колонками сезоны + rolling 1/7/30 дней
    rechargingStats,
    // devicesDeficit,
    // devicesExcess,
    ParkingsBookings,
    rebalanceForecastTaba,
    factRebalanceLayer(),
];
const deviceLayers = [
    // rebalancingAllRegionsDeficitExcess,
    // devicesBalanceAtOperationAreas,
    // devicesBalanceAtOperationAreasAccordingToLot,
    // CHBonParkings,
    // CHBdailyCountOperationArea,
    SbWithErrors,
];
// Слой "Старты по часам" для раздела "Поездки"
const startsByHourLayer = (selectedHour = 9) => {
    const hourKey = `cnt_${selectedHour}`;
    const prevHour = selectedHour === 0 ? 23 : selectedHour - 1;
    const prevHourKey = `cnt_${prevHour}`;

    return {
        id: 'Старты по часам',
        label: defineMessage({ message: 'Старты по часам' }),
        type: 'circle',
        'source-layer': 'martin.geo_starts_mvt',
        source: {
            id: 'martin.geo_starts_mvt',
            type: 'vector',
            url: `${V.API_GW_URL}/rpc/martin.geo_starts_mvt.json`,
            generateId: true,
            requiredQueryParams: [ 'date' ],
        },
        layout: { visibility: 'none' },
        // Фильтр: показывает фичи для текущего и предыдущего часа, обновляется при смене часа
        filter: [ 'any', [ '>', [ 'get', hourKey ], 0 ], [ '>', [ 'get', prevHourKey ], 0 ] ],
        paint: {
            'circle-radius': [
                'coalesce',
                [ 'feature-state', 'animatedRadius' ],
                // Условие: скрываем точки если значение 0, иначе применяем интерполяцию по городам
                [
                    'case',
                    // Если значение в текущем часе равно 0, радиус = 0 (скрыто)
                    [ '==', [ 'get', hourKey ], 0 ],
                    0,
                    // Иначе применяем интерполяцию по городам
                    [
                        'case',
                        [ 'boolean', [ 'feature-state', 'isMajorCity' ], false ],
                        // Большие города: основная шкала
                        [
                            'interpolate',
                            [ 'linear' ],
                            [ 'get', hourKey ],
                            1, 2,
                            100, 100,
                        ],
                        // Малые города: адаптивная шкала
                        [
                            'interpolate',
                            [ 'linear' ],
                            [ 'get', hourKey ],
                            1, 2,
                            50, 60,
                        ],
                    ],
                ],
            ],
            'circle-color': [
                'case',
                [ '>', [ 'to-number', [ 'coalesce', [ 'get', 'parking_id' ], 0 ] ], 0 ],
                '#ff0000',
                '#3388ff',
            ],
            // Увеличиваем opacity для лучшей видимости при дебаге
            'circle-opacity': 0.6,
            'circle-stroke-width': 0,
        },
        minzoom: 8,
        availableFor: [ V.ADMIN, V.DESIGNER ],
        // Атрибуты: parking_id, h3, date, cnt_0 ... cnt_23
    };
};
// ─────────────────────────────────────────────────────────────────────────────
// DA-15040 — слой ДТП с участием СИМ (ГИБДД). MVT-функция martin.sim_accidents_mvt.
// Точки ДТП; раскраска переключается по категоризации (тип/оператор/нарушитель),
// размер — по числу пострадавших, красный контур у ДТП с погибшими. Фильтрация
// (период/тоглы) и пересборка цвета — клиентские, в Map.jsx (applySimAccidentsStyle).
// Доп. MVT-слой 'metadata' функции отдаёт границы и даты инцидентов для таймлайна.
// ─────────────────────────────────────────────────────────────────────────────
export const SIM_ACCIDENTS_LAYER_ID = 'ДТП СИМ';
const SIM_ACCIDENTS_SOURCE = {
    id: 'martin.sim_accidents_mvt',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.sim_accidents_mvt.json`,
    promoteId: 'id',
};
// Цвет-выражение по выбранной категоризации (accident_type/operator/offender).
// Экспортируется — Map.jsx пересобирает его при смене категоризации (applySimAccidentsStyle).
export const simAccidentsColorExpr = (categorizeBy = 'accident_type') => {
    const cats = SIM_ACCIDENTS_STYLE.categories[categorizeBy] || [];
    return [
        'match', [ 'coalesce', [ 'get', categorizeBy ], '' ],
        ...cats.flatMap(c => [ c.value, c.color ]),
        SIM_ACCIDENTS_STYLE.fallbackColor,
    ];
};
// Число пострадавших = ранено + погибло; задаёт радиус точки.
const SIM_ACCIDENTS_VICTIMS = [ '+', [ 'coalesce', [ 'get', 'injured' ], 0 ], [ 'coalesce', [ 'get', 'killed' ], 0 ] ];
export const SIM_ACCIDENTS_RADIUS = [ 'interpolate', [ 'linear' ], SIM_ACCIDENTS_VICTIMS, 0, 2, 1, 3.67, 2, 7, 4, 9.5, 8, 13 ];
const SIM_ACCIDENTS_HAS_KILLED = [ '>', [ 'coalesce', [ 'get', 'killed' ], 0 ], 0 ];
const simAccidents = {
    id: SIM_ACCIDENTS_LAYER_ID,
    label: defineMessage({ message: 'ДТП с самокатами' }),
    // Динамический заголовок при экспорте растра (DA-15019/DA-15040): базовое название +
    // активные фильтры панели — период, раскраска (категоризация) и тоглы погибшие/раненые/дети.
    exportTitle: ({ filters }) => {
        const {
            simAccidentsPeriodMode,
            simAccidentsDate,
            simAccidentsRangeFrom,
            simAccidentsRangeTo,
            simAccidentsCategorizeBy,
            simAccidentsKilled,
            simAccidentsInjured,
            simAccidentsChildren,
        } = filters;
        // Период
        const fmtDate = iso => { const [ y, m, d ] = iso.split('-'); return `${d}.${m}.${y}` };
        let period;
        if (simAccidentsPeriodMode === '2025') {
            period = i18n._(defineMessage({ message: 'за 2025 г.' }));
        } else if (simAccidentsPeriodMode === '2026') {
            period = i18n._(defineMessage({ message: 'за 2026 г.' }));
        } else if (simAccidentsPeriodMode === 'date' && simAccidentsDate) {
            period = i18n._(defineMessage({ message: 'за {date}' }), { date: fmtDate(simAccidentsDate) });
        } else if (simAccidentsPeriodMode === 'range' && simAccidentsRangeFrom && simAccidentsRangeTo) {
            period = i18n._(defineMessage({ message: 'за {from} – {to}' }), {
                from: fmtDate(simAccidentsRangeFrom), to: fmtDate(simAccidentsRangeTo),
            });
        } else {
            period = i18n._(defineMessage({ message: 'за всё время' }));
        }
        // Раскраска (категоризация)
        let cat = '';
        if (simAccidentsCategorizeBy === 'accident_type') {
            cat = i18n._(defineMessage({ message: 'по типу инцидента' }));
        } else if (simAccidentsCategorizeBy === 'operator') {
            cat = i18n._(defineMessage({ message: 'по оператору' }));
        } else if (simAccidentsCategorizeBy === 'offender') {
            cat = i18n._(defineMessage({ message: 'по нарушителю' }));
        }
        // Активные тоглы
        const flags = [];
        if (simAccidentsKilled) flags.push(i18n._(defineMessage({ message: 'погибшие' })));
        if (simAccidentsInjured) flags.push(i18n._(defineMessage({ message: 'раненые' })));
        if (simAccidentsChildren) flags.push(i18n._(defineMessage({ message: 'дети на СИМ' })));

        let title = `${i18n._(defineMessage({ message: 'ДТП с участием СИМ' }))}, ${period}`;
        if (cat) title += `, ${cat}`;
        if (flags.length) title += ` (${flags.join(', ')})`;

        return title;
    },
    type: 'circle',
    'source-layer': 'martin.sim_accidents_mvt',
    source: SIM_ACCIDENTS_SOURCE,
    layout: { visibility: 'none' },
    paint: {
        'circle-color': simAccidentsColorExpr('accident_type'),
        'circle-radius': SIM_ACCIDENTS_RADIUS,
        'circle-opacity': 0.85,
        // Красный контур у ДТП с погибшими, иначе тонкая тёмная обводка.
        'circle-stroke-color': [ 'case', SIM_ACCIDENTS_HAS_KILLED, '#d50000', 'rgba(0,0,0,0.35)' ],
        'circle-stroke-width': [ 'case', SIM_ACCIDENTS_HAS_KILLED, 3, 1 ],
    },
    minzoom: 0,
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    // Атрибуты: id, dtp_id, accident_ts, accident_date, accident_type, operator, inz,
    // vehicles_count, killed, killed_children, injured, injured_children_u18/u16,
    // main_violation, aux_violation, offender, zone_ring, children_driving, okrug, road,
    // street, house, lighting, uds_object, uds_object_near, weather, place_path,
    // n_children, n_children_injured, child_min_age, child_max_age.
};
const tripLayers = [
    startsByHourLayer(),
    adminAggregatedTracksV2,
    adminAggregatedTracksMonthly,
    aggregatedTracksCohort,
    aggregatedTracksUserCohorts,
    AggregatedTripsAugustH3,
    trackStarts,
    trackFinish,
    trackDiffStartFinish,
    trackDiffStartFinishAugust,
    aggStartAugust,
    aggFinishAugust,
    scootersSpeed,
    operationAreasCorrespondingMatrix,
    failedTrips,
    AggregatedParkingAlerts,
    ParkingAlertsPoints,
    routeAnomaliesH3,
    simAccidents,
];
const competitors = [
    trackStartsCompetitors,
    operationAreasCompetitors,
    restrictionZonesCompetitors,
    ridingZonesCompetitors,
    competitorsZonesChangesOutline,
    competitorsParkings,
    competitorsParkingsChanges,
    fleetTransfersCompetitors,
];
// DA-15309: слой «Типология застройки» — здания и территории, классифицированные по bldg_class
const URBAN_TYPOLOGY_ZONES_SOURCE = {
    id: 'martin.urban_typology_zones_mvt_source',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.urban_typology_zones_mvt.json`,
};
const URBAN_TYPOLOGY_ZONES_SOURCE_LAYER = 'martin.urban_typology_zones_mvt';
const URBAN_TYPOLOGY_BUILDINGS_SOURCE = {
    id: 'martin.urban_typology_buildings_mvt_source',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.urban_typology_buildings_mvt.json`,
};
const URBAN_TYPOLOGY_BUILDINGS_SOURCE_LAYER = 'martin.urban_typology_buildings_mvt';
// Общая раскраска по bldg_class для обоих под-слоёв; null/незнакомое значение → бирюзовый.
// 'прочее' — здания, 'unclassified' — территории: витрина urban_zones_classified сводит туда и
// 'прочее', и NULL, у зданий классификатор оставляет 'прочее' как есть.
// DA-15202 (2026-09-09): добавлен green_park. Ветки для него не было вовсе, поэтому парки
// проваливались в дефолт — тот же бирюзовый #14B8A6, что у 'unclassified', и на карте были
// неотличимы от мусора классификации (12 262 зоны, 207 331 га по проду). Зелень разведена по
// насыщенности: благоустроенное озеленение (green_park — leisure=park/garden) забрало прежний
// зелёный #22C55E, неблагоустроенная зелень (green — natural=wood/grassland/wetland/scrub,
// landuse=forest/grass, leisure=nature_reserve) ушла в салатовый #84CC16. Порядок именно такой,
// а не наоборот: парк — более «городской» объект, ему насыщенный цвет.
export const URBAN_TYPOLOGY_COLOR = [
    'match', [ 'get', 'bldg_class' ],
    'residential_individual', '#FACC15',
    'residential_multi', '#F97316',
    'commercial', '#EF4444',
    'industrial', '#D4D4D8',
    'campus', '#BE123C',
    'education', '#EC4899',
    'healthcare', '#0EA5E9',
    'green', '#84CC16',
    'green_park', '#22C55E',
    'sport', '#6366F1',
    'transport_hub', '#A855F7',
    'service', '#FFFFFF',
    'прочее', '#14B8A6',
    'unclassified', '#14B8A6',
    '#14B8A6',
];
// Альтернативные раскраски слоя «Типология застройки» (переключаются радиокнопками в легенде).
// Стопы вынесены в константы, а не зашиты в mapbox-выражение: легенда рисует градиент и подписи
// из этих же значений, поэтому шкала на карте и в легенде не могут разъехаться.
// Пороги подобраны по перцентилям dwh.buildings_classified, а не по max: у обоих полей длинный
// правый хвост выбросов (этажность до 553, население упирается в потолок smallint 32767), по
// которому линейная шкала выродилась бы в один цвет на весь город.
// Этажность (p50=1, p90=2, p99=12): белый → жёлтый → оранжевый → красный.
export const URBAN_TYPOLOGY_LEVELS_STOPS = [
    { value: 1, color: '#FFFFFF' },
    { value: 3, color: '#FEF08A' },
    { value: 5, color: '#FACC15' },
    { value: 9, color: '#F97316' },
    { value: 16, color: '#DC2626' },
];
// Население (p50=2, p90=8, p99=167): жёлтый → оранжевый → красный, без населения — белый.
export const URBAN_TYPOLOGY_POPULATION_STOPS = [
    { value: 1, color: '#FEF08A' },
    { value: 10, color: '#FACC15' },
    { value: 50, color: '#F97316' },
    { value: 150, color: '#EF4444' },
    { value: 500, color: '#DC2626' },
];
// Цвет для фич без значения атрибута. В MVT NULL-колонка не кодируется в фичу вообще, поэтому
// проверка именно через 'has', а не сравнение с null.
export const URBAN_TYPOLOGY_NO_DATA_COLOR = '#FFFFFF';
const buildStopsColor = (property, stops) => [
    'case',
    [ '!', [ 'has', property ] ], URBAN_TYPOLOGY_NO_DATA_COLOR,
    [
        'interpolate', [ 'linear' ], [ 'to-number', [ 'get', property ] ],
        ...stops.flatMap(({ value, color }) => [ value, color ]),
    ],
];
export const URBAN_TYPOLOGY_LEVELS_COLOR = buildStopsColor('levels', URBAN_TYPOLOGY_LEVELS_STOPS);
export const URBAN_TYPOLOGY_POPULATION_COLOR = buildStopsColor('population', URBAN_TYPOLOGY_POPULATION_STOPS);
// Ключи — значения mapFilters.urbanTypologyColorMode.
export const URBAN_TYPOLOGY_COLOR_BY_MODE = {
    class: URBAN_TYPOLOGY_COLOR,
    levels: URBAN_TYPOLOGY_LEVELS_COLOR,
    population: URBAN_TYPOLOGY_POPULATION_COLOR,
};
// Границы кварталов — каркас квартальной сетки (dwh.quaters_generated), из которого цепочка
// зонирования нарезает территории. Подслой «Типологии территорий»: собственного чекбокса в
// сайдбаре нет, включается тумблером в легенде (mapFilters.urbanTypologyShowBlocks), по
// умолчанию выключен. Только обводка без заливки — слой служит сеткой поверх раскрашенных зон.
const URBAN_TYPOLOGY_BLOCKS_SOURCE = {
    id: 'martin.quaters_generated_mvt_source',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.quaters_generated_mvt.json`,
};
const URBAN_TYPOLOGY_BLOCKS_SOURCE_LAYER = 'martin.quaters_generated_mvt';
export const URBAN_TYPOLOGY_BLOCKS_LAYER_ID = 'Типология территорий_кварталы';
export const URBAN_TYPOLOGY_BLOCKS_COLOR = '#DC2626';
const urbanTypologyBlocksLayer = {
    id: URBAN_TYPOLOGY_BLOCKS_LAYER_ID,
    type: 'line',
    'source-layer': URBAN_TYPOLOGY_BLOCKS_SOURCE_LAYER,
    source: URBAN_TYPOLOGY_BLOCKS_SOURCE,
    layout: { visibility: 'none' },
    paint: {
        'line-color': URBAN_TYPOLOGY_BLOCKS_COLOR,
        'line-width': 1,
        'line-opacity': 0.9,
    },
    availableFor: [ V.SUPER_ADMIN ],
};
// Разделены на 2 независимых слоя (были одним с под-слоями Здания/Территории и переключателями
// в легенде) — независимые чекбоксы в сайдбаре сделали переключатели в легенде избыточными.
// Территории идут в areaLayers ПЕРЕД зданиями: при равном zClass ('polygon') reorderOverlays
// сохраняет порядок добавления, поэтому здания рендерятся поверх территорий.
const urbanTypologyZonesLayer = {
    id: 'Типология территорий',
    label: defineMessage({ message: 'Типология территорий' }),
    type: 'fill',
    'source-layer': URBAN_TYPOLOGY_ZONES_SOURCE_LAYER,
    source: URBAN_TYPOLOGY_ZONES_SOURCE,
    layout: { visibility: 'none' },
    paint: { 'fill-color': URBAN_TYPOLOGY_COLOR, 'fill-opacity': 0.5 },
    availableFor: [ V.SUPER_ADMIN ],
    additionalLayers: [ urbanTypologyBlocksLayer ],
};
// Зум, с которого MVT-функция отдаёт отдельные здания со всеми атрибутами. Ниже — ST_Collect
// по группе (bldg_class + source + огрублённые до порогов этажность/население), см. lowzoom_max
// в database-architecture/macros/create_udfs.sql. Значение должно совпадать с lowzoom_max там.
export const URBAN_TYPOLOGY_DETAIL_MINZOOM = 13;
// Экструзия по этажности — компаньон-слой к зданиям, переключается тумблером в легенде
// (mapFilters.urbanTypologyExtrudeBuildings), а не собственным чекбоксом в сайдбаре: видим либо
// плоский fill, либо эта экструзия, никогда оба разом (см. спец-кейсы toggleLayer/componentDidUpdate
// в Map.jsx). Ограничена детальным зумом: ниже 'levels' хоть и есть (огрублённый), но геометрия
// схлопнута в одну мультиполигональную группу — экструзия дала бы бесформенную "стену" одинаковой
// высоты на весь квартал. Высота — грубая оценка 3м/этаж, levels IS NULL -> 1 этаж по умолчанию.
const urbanTypologyBuildingsExtrusionLayer = {
    id: 'Типология застройки_3D',
    type: 'fill-extrusion',
    'source-layer': URBAN_TYPOLOGY_BUILDINGS_SOURCE_LAYER,
    source: URBAN_TYPOLOGY_BUILDINGS_SOURCE,
    minzoom: URBAN_TYPOLOGY_DETAIL_MINZOOM,
    layout: { visibility: 'none' },
    paint: {
        'fill-extrusion-color': URBAN_TYPOLOGY_COLOR,
        'fill-extrusion-height': [ '*', [ 'coalesce', [ 'get', 'levels' ], 1 ], 3 ],
        'fill-extrusion-opacity': 0.9,
    },
    availableFor: [ V.SUPER_ADMIN ],
};
const urbanTypologyBuildingsLayer = {
    id: 'Типология застройки',
    label: defineMessage({ message: 'Типология застройки' }),
    type: 'fill',
    'source-layer': URBAN_TYPOLOGY_BUILDINGS_SOURCE_LAYER,
    source: URBAN_TYPOLOGY_BUILDINGS_SOURCE,
    minzoom: 9,
    layout: { visibility: 'none' },
    paint: { 'fill-color': URBAN_TYPOLOGY_COLOR, 'fill-opacity': 1 },
    availableFor: [ V.SUPER_ADMIN ],
    additionalLayers: [ urbanTypologyBuildingsExtrusionLayer ],
};
// DA-15433: предложения по парковкам — выпуклые оболочки кластеров алертов завершения
// парковки. Алерт — попытка закончить поездку там, где парковки нет, поэтому скопления
// таких событий подсвечивают места, где парковку стоит рассмотреть.
// Источник: martin.parking_areas_proposals через функцию parking_areas_proposals_mvt.
const PARKING_PROPOSALS_SOURCE = {
    id: 'martin.parking_areas_proposals_mvt',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.parking_areas_proposals_mvt.json`,
};
const PARKING_PROPOSALS_SOURCE_LAYER = 'martin.parking_areas_proposals_mvt';
// Красим по demand_pct — доле кластеров ГОРОДА со спросом не выше данного. Нормировка
// внутри города обязательна: спрос в Москве и во Владимире различается на два порядка,
// и по абсолютному значению Москва залилась бы насыщенным цветом целиком, а малые
// города остались бы бледными.
// Шкала magma: тёмно-синий (низкий спрос) → жёлтый (высокий). Тот же семейный
// градиент, что у aggUserTaps/aggUserOpens, чтобы слои читались одинаково.
export const PARKING_PROPOSALS_DEMAND_STOPS = [
    [ 0.2, '#2c1a72' ],
    [ 0.4, '#792282' ],
    [ 0.6, '#c23b75' ],
    [ 0.8, '#f4695c' ],
    [ 1.0, '#fcfdbf' ],
];
// to-number — страховка от кэша: PostgreSQL numeric уезжает в MVT СТРОКОЙ, и на такой
// тайл 'step' падает с «Expected value to be of type number, but found string instead»,
// а слой заливается чёрным. В витрине тип уже исправлен на double precision, но ранее
// отданные тайлы могут жить в кэше CDN и браузера.
const PARKING_PROPOSALS_COLOR = [
    'step', [ 'to-number', [ 'get', 'demand_pct' ] ],
    PARKING_PROPOSALS_DEMAND_STOPS[0][1],
    0.2, PARKING_PROPOSALS_DEMAND_STOPS[1][1],
    0.4, PARKING_PROPOSALS_DEMAND_STOPS[2][1],
    0.6, PARKING_PROPOSALS_DEMAND_STOPS[3][1],
    0.8, PARKING_PROPOSALS_DEMAND_STOPS[4][1],
];
// Обводка: кляксы (is_oversized) выделены пунктиром и синим — это известный дефект
// кластеризации, а не находка. В Москве таких 14 из ~10 тысяч кластеров; они оставлены
// на карте намеренно, чтобы их можно было рассмотреть глазами.
const parkingProposalsOutline = {
    id: 'Предложения по парковкам_обводка',
    label: defineMessage({ message: 'Предложения по парковкам_обводка' }),
    type: 'line',
    'source-layer': PARKING_PROPOSALS_SOURCE_LAYER,
    source: PARKING_PROPOSALS_SOURCE,
    minzoom: 10,
    layout: { visibility: 'none' },
    // Источник отдаёт три вида фич (оболочки, точки, круги радиуса) в одном слое —
    // разбираем по feature_kind. Переключатели легенды переопределяют этот фильтр
    // через Map.setParkingProposalsFilters.
    filter: [ '==', [ 'get', 'feature_kind' ], 'hull' ],
    paint: {
        // Обводка тем же цветом, что и заливка: при opacity 0.9 против 0.55 у заливки
        // это даёт чёткий край, не вводя второй цветовой признак.
        // Кляксы отличаются только пунктиром и толщиной — цвет у них общий со всеми.
        'line-color': PARKING_PROPOSALS_COLOR,
        'line-width': [ 'case', [ '==', [ 'get', 'is_oversized' ], true ], 2, 1 ],
        'line-dasharray': [ 'case', [ '==', [ 'get', 'is_oversized' ], true ], [ 'literal', [ 2, 2 ] ], [ 'literal', [ 1, 0 ] ] ],
        'line-opacity': 0.9,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// DA-15433: точечные предложения. Точка — парковка конкурента в 50 м от оболочки,
// ближайшая к центру масс кластера: место под парковку там физически существует,
// конкурент его уже нашёл. Круг радиуса — 30 м покрытия от этой точки.
// Круг рисуется подложкой под точкой, поэтому идёт в списке раньше.
const parkingProposalsRadius = {
    id: 'Предложения по парковкам_радиус',
    label: defineMessage({ message: 'Предложения по парковкам_радиус' }),
    type: 'fill',
    'source-layer': PARKING_PROPOSALS_SOURCE_LAYER,
    source: PARKING_PROPOSALS_SOURCE,
    minzoom: 10,
    layout: { visibility: 'none' },
    filter: [ '==', [ 'get', 'feature_kind' ], 'radius' ],
    paint: {
        // Нейтральный зелёный, а не шкала спроса: круг — это предложение, а не боль,
        // и он не должен спорить по цвету с оболочкой, поверх которой лежит.
        'fill-color': '#1b9e77',
        'fill-opacity': 0.25,
        'fill-outline-color': '#1b9e77',
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
const parkingProposalsPoints = {
    id: 'Предложения по парковкам_точки',
    label: defineMessage({ message: 'Предложения по парковкам_точки' }),
    type: 'circle',
    'source-layer': PARKING_PROPOSALS_SOURCE_LAYER,
    source: PARKING_PROPOSALS_SOURCE,
    minzoom: 10,
    layout: { visibility: 'none' },
    filter: [ '==', [ 'get', 'feature_kind' ], 'point' ],
    paint: {
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 10, 1.5, 16, 4 ],
        'circle-color': '#1b9e77',
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
const parkingProposals = {
    id: 'Предложения по парковкам',
    label: defineMessage({ message: 'Предложения по парковкам' }),
    type: 'fill',
    'source-layer': PARKING_PROPOSALS_SOURCE_LAYER,
    source: PARKING_PROPOSALS_SOURCE,
    minzoom: 10,
    layout: { visibility: 'none' },
    filter: [ '==', [ 'get', 'feature_kind' ], 'hull' ],
    paint: {
        'fill-color': PARKING_PROPOSALS_COLOR,
        'fill-opacity': 0.55,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    additionalLayers: [ parkingProposalsOutline, parkingProposalsRadius, parkingProposalsPoints ],
};
// DA-14919: WalkScore по H3. С DA-15880 слой живёт в группе «Зоны» (был DEV-слоем пилота
// Москвы) и считается по всем городам подготовки данных, а не по одному пилотному.
// Источник martin.walkscore_h3_mvt отдаёт все 21 метрику (15 категорий + 5 групп + общий)
// атрибутами одного тайла. Переключатель метрики в WalkscoreFilter меняет крашенный атрибут
// через setWalkscorePaint в Map.jsx. По умолчанию красим общий скор r_all. Шкала 0–100.
const walkscoreH3 = {
    id: 'WalkScore',
    label: defineMessage({ message: 'Walkscore' }),
    // Заголовок экспорта: WalkScore + выбранная метрика (walkscoreMetricLabels).
    exportTitle: ({ filters }) => {
        const label = V.walkscoreMetricLabels[filters.walkscoreMetric];
        return label
            ? i18n._(defineMessage({ message: 'WalkScore — {metric}' }), { metric: i18n._(label) })
            : '';
    },
    type: 'fill',
    zClass: 'wash', // DA-14969: заливка-размывка над полигонами, но под точками/линиями
    minzoom: 0,
    maxzoom: 24,
    'source-layer': 'martin.walkscore_h3_mvt',
    source: {
        id: 'walkscore_h3_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.walkscore_h3_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'r_all' ] ],
            0,
            '#440154',
            25,
            '#3b528b',
            50,
            '#21918c',
            75,
            '#5ec962',
            100,
            '#fde725',
        ],
        'fill-opacity': 0.7,
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
    },
    legend: WALKSCORE_LEGEND,
    availableFor: [ V.ADMIN, V.DESIGNER ],
};
// DA-13733: зоны обслуживания объектов (martin.charging_service_zones) — подсветка зоны
// при наведении в «Загрузке зарядных объектов»
const chargingServiceZonesSource = {
    id: 'charging_service_zones_source',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.charging_service_zones_mvt.json`,
};
// DA-13733: зона обслуживания объекта под курсором из martin.charging_service_zones:
// kind='zone' — ячейки 80% замен (устойчивый спрос, оранжевый), kind='tail' — оставшиеся
// 20% (остаточный спрос, бледно-жёлтый), kind='cand' — ожидаемая зона места под новый объект
// (obj_id = −номер кандидата, у варианта «самокат» — −(100 + номер), бирюзовый). Фильтр по obj_id выставляет обработчик mousemove в Map.jsx;
// исходный 0 не совпадает ни с одним объектом: у действующих id > 0, у кандидатов obj_id < 0.
export const chargingObjectsLoadZoneFilter = objId => [
    'all', [ 'in', [ 'get', 'kind' ], [ 'literal', [ 'zone', 'tail', 'cand' ] ] ], [ '==', [ 'to-number', [ 'get', 'obj_id' ] ], objId ],
];
const chargingObjectsLoadZoneFill = {
    id: 'Загрузка зарядных объектов_зона',
    label: defineMessage({ message: 'Загрузка зарядных объектов_зона' }),
    type: 'fill',
    zClass: 'wash',
    'source-layer': 'martin.charging_service_zones_mvt',
    source: chargingServiceZonesSource,
    filter: chargingObjectsLoadZoneFilter(0),
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [ 'match', [ 'get', 'kind' ], 'zone', '#FF7033', 'cand', '#00a3a3', '#FDB92E' ],
        'fill-opacity': [ 'match', [ 'get', 'kind' ], 'zone', 0.25, 'cand', 0.25, 0.15 ],
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
const chargingObjectsLoadZoneLine = {
    id: 'Загрузка зарядных объектов_зона_обводка',
    label: defineMessage({ message: 'Загрузка зарядных объектов_зона_обводка' }),
    type: 'line',
    'source-layer': 'martin.charging_service_zones_mvt',
    source: chargingServiceZonesSource,
    filter: chargingObjectsLoadZoneFilter(0),
    layout: { visibility: 'none' },
    paint: {
        'line-color': [ 'match', [ 'get', 'kind' ], 'zone', '#D9591A', 'cand', '#007a7a', '#E0A000' ],
        'line-width': [ 'match', [ 'get', 'kind' ], 'tail', 1, 2.5 ],
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// DA-13733: предложения по развитию сети (martin.charging_network_proposals) — подслои
// «Загрузки зарядных объектов». По умолчанию фильтр ничего не пропускает; переключатель
// в легенде (mapFilters.chargingProposals) меняет его через setChargingProposalsFilter в Map.jsx.
export const CHARGING_PROPOSALS_OFF_FILTER = [ '==', [ 'get', 'kind' ], '__off' ];
export const CHARGING_PROPOSALS_OPEN_ID = 'Загрузка зарядных объектов_предложения_открыть';
export const CHARGING_PROPOSALS_RING_ID = 'Загрузка зарядных объектов_предложения';
// Два варианта модели доступности (variant): walk — пешком 1,5 км, scooter — 15 мин на самокате
// в форвардинге (15 км/ч, 3,75 км). Переключатель в легенде включает предложения, радиокнопки
// выбирают вариант. Оба варианта идут из одного источника и при включённых предложениях держат
// свои фильтры постоянно, неактивный лишь прозрачен (как у «Землепользования»): тайлы уже
// загружены, и смена варианта отрисовывается сразу.
const chargingProposalsFilter = (kinds, variant) => [
    'all',
    [ 'in', [ 'get', 'kind' ], [ 'literal', kinds ] ],
    [ '==', [ 'coalesce', [ 'get', 'variant' ], 'walk' ], variant ],
];
const CHARGING_PROPOSALS_OPEN_KINDS = [ 'open', 'open_walk' ];
const CHARGING_PROPOSALS_RING_KINDS = [ 'close', 'shrink', 'expand', 'offload' ];
export const CHARGING_PROPOSALS_OPEN_FILTER = chargingProposalsFilter(CHARGING_PROPOSALS_OPEN_KINDS, 'walk');
export const CHARGING_PROPOSALS_RING_FILTER = chargingProposalsFilter(CHARGING_PROPOSALS_RING_KINDS, 'walk');
export const CHARGING_PROPOSALS_SCOOTER_OPEN_ID = 'Загрузка зарядных объектов_предложения_самокат_открыть';
export const CHARGING_PROPOSALS_SCOOTER_RING_ID = 'Загрузка зарядных объектов_предложения_самокат';
export const CHARGING_PROPOSALS_SCOOTER_OPEN_FILTER = chargingProposalsFilter(CHARGING_PROPOSALS_OPEN_KINDS, 'scooter');
export const CHARGING_PROPOSALS_SCOOTER_RING_FILTER = chargingProposalsFilter(CHARGING_PROPOSALS_RING_KINDS, 'scooter');
// Третий вариант — согласованный план (variant='plan'): добавлен kind review («проверить»)
export const CHARGING_PROPOSALS_PLAN_OPEN_ID = 'Загрузка зарядных объектов_предложения_план_открыть';
export const CHARGING_PROPOSALS_PLAN_RING_ID = 'Загрузка зарядных объектов_предложения_план';
export const CHARGING_PROPOSALS_PLAN_OPEN_FILTER = chargingProposalsFilter(CHARGING_PROPOSALS_OPEN_KINDS, 'plan');
export const CHARGING_PROPOSALS_PLAN_RING_FILTER = chargingProposalsFilter([ ...CHARGING_PROPOSALS_RING_KINDS, 'review' ], 'plan');
const chargingProposalsSource = {
    id: 'charging_network_proposals_source',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.charging_network_proposals_mvt.json`,
};
const chargingProposalsColor = [
    'match',
    [ 'get', 'kind' ],
    ...CHARGING_PROPOSALS_STYLE.kinds.items.flatMap(({ value, color }) => [ value, color ]),
    '#999999',
];
const chargingProposalsRing = {
    id: CHARGING_PROPOSALS_RING_ID,
    label: defineMessage({ message: 'Загрузка зарядных объектов_предложения' }),
    type: 'circle',
    'source-layer': 'martin.charging_network_proposals_mvt',
    source: chargingProposalsSource,
    filter: CHARGING_PROPOSALS_OFF_FILTER,
    layout: { visibility: 'none' },
    paint: {
        // Радиус — на 5 px больше точки объекта (та же шкала по числу слотов)
        'circle-radius': [
            'interpolate',
            [ 'linear' ],
            [ 'zoom' ],
            8,
            [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'slots' ] ], 40, 9, 360, 17 ],
            14,
            [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'slots' ] ], 40, 13, 360, 31 ],
        ],
        'circle-color': 'rgba(0, 0, 0, 0)',
        'circle-stroke-color': chargingProposalsColor,
        'circle-stroke-width': 3,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
const chargingProposalsOpen = {
    id: CHARGING_PROPOSALS_OPEN_ID,
    label: defineMessage({ message: 'Загрузка зарядных объектов_предложения_открыть' }),
    type: 'circle',
    'source-layer': 'martin.charging_network_proposals_mvt',
    source: chargingProposalsSource,
    filter: CHARGING_PROPOSALS_OFF_FILTER,
    layout: { visibility: 'none' },
    paint: {
        // Кольцо без заливки, как у остальных предложений, — чтобы не путать с действующими объектами
        // open — нужна новая мощность (крупное кольцо), open_walk — мощность есть у недогруженных
        // соседей, место лишь улучшает шаговую доступность (мелкое кольцо)
        'circle-radius': [
            'interpolate', [ 'linear' ], [ 'zoom' ],
            8, [ 'match', [ 'get', 'kind' ], 'open', 8, 4 ],
            14, [ 'match', [ 'get', 'kind' ], 'open', 18, 9 ],
        ],
        'circle-color': 'rgba(0, 0, 0, 0)',
        'circle-stroke-color': chargingProposalsColor,
        'circle-stroke-width': [ 'match', [ 'get', 'kind' ], 'open', 3.5, 2 ],
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// Вариант «самокат»: те же кольца, что у пешего варианта, в своих подслоях.
const chargingProposalsScooterRing = {
    ...chargingProposalsRing,
    id: CHARGING_PROPOSALS_SCOOTER_RING_ID,
    label: defineMessage({ message: 'Загрузка зарядных объектов_предложения_самокат' }),
};
const chargingProposalsScooterOpen = {
    ...chargingProposalsOpen,
    id: CHARGING_PROPOSALS_SCOOTER_OPEN_ID,
    label: defineMessage({ message: 'Загрузка зарядных объектов_предложения_самокат_открыть' }),
};
// Вариант «согласованный план»: свои цвета kind (плюс «проверить»)
const chargingProposalsPlanColor = [
    'match',
    [ 'get', 'kind' ],
    ...CHARGING_PROPOSALS_PLAN_STYLE.kinds.items.flatMap(({ value, color }) => [ value, color ]),
    '#999999',
];
const chargingProposalsPlanRing = {
    ...chargingProposalsRing,
    id: CHARGING_PROPOSALS_PLAN_RING_ID,
    label: defineMessage({ message: 'Загрузка зарядных объектов_предложения_план' }),
    paint: { ...chargingProposalsRing.paint, 'circle-stroke-color': chargingProposalsPlanColor },
};
const chargingProposalsPlanOpen = {
    ...chargingProposalsOpen,
    id: CHARGING_PROPOSALS_PLAN_OPEN_ID,
    label: defineMessage({ message: 'Загрузка зарядных объектов_предложения_план_открыть' }),
    paint: { ...chargingProposalsOpen.paint, 'circle-stroke-color': chargingProposalsPlanColor },
};
// DA-13733: загрузка зарядных объектов Москвы на 24.09.2026. util_pct — оборот слота от потолка
// 7 зарядов в сутки: по счётчикам электроэнергии, у объектов без счётчика — по пешей модели
// (тёмная обводка). Цвет — класс загрузки, радиус — число слотов.
export const CHARGING_OBJECTS_LOAD_ID = 'Загрузка зарядных объектов';
// Пока в легенде оставлен только согласованный план; варианты walk и scooter остаются в данных
export const CHARGING_PROPOSALS_ACTIVE_VARIANT = 'plan';
// Фильтр действующих объектов по тоглам легенды: весь слой и подрядчики (contractor).
// Тот же фильтр накладывается на кольца предложений по действующим объектам.
export const chargingObjectsFilter = ({ chargingObjects = true, chargingAksel = true, chargingInnovat = true } = {}) => {
    // Выражение, а не legacy-фильтр: ['==', 1, 0] Mapbox читает как старый синтаксис и не скрывает ничего
    if (!chargingObjects) return [ '==', [ 'to-string', [ 'get', 'id' ] ], '__off' ];
    const hidden = [ !chargingAksel && 'Аксель', !chargingInnovat && 'Инноватт' ].filter(Boolean);
    return [ '!', [ 'in', [ 'coalesce', [ 'get', 'contractor' ], '' ], [ 'literal', hidden ] ] ];
};
const chargingObjectsLoad = {
    id: CHARGING_OBJECTS_LOAD_ID,
    label: defineMessage({ message: 'Загрузка зарядных объектов' }),
    type: 'circle',
    minzoom: 0,
    maxzoom: 24,
    'source-layer': 'martin.charging_objects_load_mvt',
    source: {
        id: 'charging_objects_load_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.charging_objects_load_mvt.json`,
    },
    layout: { visibility: 'none' },
    paint: {
        'circle-color': [
            'step',
            [ 'to-number', [ 'get', 'util_pct' ] ],
            CHARGING_OBJECTS_LOAD_STYLE.utilization.items[0].color,
            30,
            CHARGING_OBJECTS_LOAD_STYLE.utilization.items[1].color,
            60,
            CHARGING_OBJECTS_LOAD_STYLE.utilization.items[2].color,
            80,
            CHARGING_OBJECTS_LOAD_STYLE.utilization.items[3].color,
        ],
        'circle-radius': [
            'interpolate',
            [ 'linear' ],
            [ 'zoom' ],
            8,
            [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'slots' ] ], 40, 4, 360, 12 ],
            14,
            [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'slots' ] ], 40, 8, 360, 26 ],
        ],
        'circle-opacity': 0.9,
        'circle-stroke-color': [ 'match', [ 'get', 'util_source' ], 'модель', '#333333', '#ffffff' ],
        'circle-stroke-width': [ 'match', [ 'get', 'util_source' ], 'модель', 2.5, 1.5 ],
    },
    additionalLayers: [
        chargingObjectsLoadZoneFill,
        chargingObjectsLoadZoneLine,
        chargingProposalsRing,
        chargingProposalsOpen,
        chargingProposalsScooterRing,
        chargingProposalsScooterOpen,
        chargingProposalsPlanRing,
        chargingProposalsPlanOpen,
    ],
    legend: CHARGING_OBJECTS_LOAD_LEGEND,
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// Определение стоит здесь, выше areaLayers: const не поднимается, и слой,
// перечисленный в группе до своего объявления, падал бы на инициализации модуля.
const areaLayers = [
    restrictionZonesV2,
    operationAreas,
    cityAreas,
    osmAdminBoundaries,
    regionRadius,
    analyticAreas,
    featureAreas,
    urbanTypologyZonesLayer,
    urbanTypologyBuildingsLayer,
    parkingProposals,
    // DA-14919/DA-15880: перенесён из группы DEV — слой вышел из пилота. Побочный эффект
    // переноса: DEV видна только SUPER_ADMIN, «Зоны» — ещё и ADMIN, поэтому слой открылся
    // администраторам (availableFor самого слоя не менялся).
    walkscoreH3,
];
// Объявлена после areaLayers: включает chargingObjectsLoad, а const не поднимается
const infrastructureLayers = [
    landuse,
    poi,
    houses,
    housesHeatmap,
    newBuildings,
    populationKontur,
    parkingLots,
    parking1300,
    ParkingRadius,
    parkingDensity,
    parkingIsochrones,
    adminBikePath,
    adminSupposedBikePath,
    aggHexRent,
    roadIndex,
    ServiceCentresIsochrones,
    trasnportIsochrones,
    enclosures,
    // DA-13733: перенесён из группы «Зоны»
    chargingObjectsLoad,
];
const appActions = [
    userTaps,
    userOpens,
    userTapsZeroStarts,
    regToTripConversion,
    openRide,
    clusteredLocalMoransTaps7days,
    clusteredLocalMoransOpens7days,
    HistoryUserTapsOpens,
];
const Tariff = [
    dynamicTariffZones,
    dynamicTariffH3,
];
// Scenaries
const NewCitiesScenario = [
    parkingLots,
    ParkingRadius,
    parkingIsochrones,
    ServiceCentresIsochrones,
    trasnportIsochrones,
    poi,
    houses,
    housesHeatmap,
    aggHexRent,
    newBuildings,
    adminBikePath,
    functionalAreas,
    cityAreas,
];
const ParkingAudit = [
    aggHexAttention,
    parkingLots,
    ParkingRadius,
    parkingIsochrones,
    poi,
    houses,
    newBuildings,
    ClassifiedParkings,
    functionalAreas,
    userTaps,
    userOpens,
    clusteredLocalMoransTaps7days,
    ServiceCentresIsochrones,
];
const Rebalancing = [
    ScootersCount,
    ScootersDowntime,
    // devicesExcess,
    // devicesDeficit,
    ClassifiedParkings,
    ClassifiedParkingsDayPart,
    // operationAreasCorrespondingMatrix,
    functionalAreas,
    operationAreas,
];
const ChangingZones = [
    poi,
    houses,
    newBuildings,
    adminBikePath,
    ServiceCentresIsochrones,
    aggHexRent,
    parkingLots,
    ClassifiedParkings,
    userTaps,
    userOpens,
    functionalAreas,
    cityAreas,
];
const WorkingWithAdministration = [
    adminBikePath,
    adminSupposedBikePath,
    trasnportIsochrones,
    parkingLots,
    // operationAreasCorrespondingMatrix,
    functionalAreas,
    operationAreas,
    speedLimitAreas,
    roadIndex,
];
const LimitedZones = [
    poi,
    adminBikePath,
    scootersSpeed,
    functionalAreas,
    speedLimitAreas,
    roadIndex,
];
// Слой тайловой сетки — управляется через Map.jsx (GeoJSON, не MVT).
// Запись нужна только для отображения переключателя в сайдбаре DEV-группы.
const tileBoundaries = {
    id: 'Границы тайлов',
    label: defineMessage({ message: 'Границы тайлов' }),
    type: 'fill', // фиктивный тип — реальные слои добавляются в Map.jsx
    source: { type: 'geojson', data: { type: 'FeatureCollection', features: [] } },
    layout: { visibility: 'none' },
    paint: {},
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// DA-12966: слой потерянных самокатов (LOST / DELETED) — 3 подслоя
const LOST_DEVICES_SOURCE = {
    id: 'martin.lost_devices_mvt',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.lost_devices_mvt.json`,
};
const LOST_DEVICES_SOURCE_LAYER = 'martin.lost_devices_mvt';
// Радиус кластера: пропорциональная интерполяция 2→4px .. 500→24px
// fieldOrExpr — строка (имя поля) или готовое GL-выражение
const lostClusterRadius = fieldOrExpr => [
    'interpolate', [ 'linear' ],
    Array.isArray(fieldOrExpr) ? fieldOrExpr : [ 'get', fieldOrExpr ],
    2,   4,
    500, 24,
];
// Отступ метки от центра в em (font-size 12px):
// при radius r отступ = (r + 4) / 12
// r(2)=4 → 0.67em, r(500)=24 → 2.33em
const lostClusterLabelOffset = field => [
    'interpolate', [ 'linear' ], [ 'get', field ],
    2,   [ 'literal', [ 0.67, 0 ] ],
    500, [ 'literal', [ 2.33, 0 ] ],
];
// Выражения для mixed-кластеров: max/min из двух count'ов
const lostMixedMaxCount = [ 'max', [ 'get', 'count_lost' ], [ 'get', 'count_deleted' ] ];
const lostMixedMinCount = [ 'min', [ 'get', 'count_lost' ], [ 'get', 'count_deleted' ] ];
// Цвет нижнего (большего) кружка: жёлтый если count_lost >= count_deleted, иначе красный
const lostMixedBottomColor = [
    'case',
    [ '>=', [ 'get', 'count_lost' ], [ 'get', 'count_deleted' ] ],
    '#eab308',
    '#ef4444',
];
// Цвет верхнего (меньшего) кружка: противоположный нижнему
const lostMixedTopColor = [
    'case',
    [ '>=', [ 'get', 'count_lost' ], [ 'get', 'count_deleted' ] ],
    '#ef4444',
    '#eab308',
];
// ── Группа A: кластеры с LOST (count_lost > 0) ──────────────────────────────
// Показывается когда LOST включён. Покрывает и LOST-only, и mixed-кластеры.
// Когда ОБА включены — mixed-кластеры дополнительно покрываются группой C сверху.
const lostClusterCircleLost = {
    id: 'LOST: кластеры (lost)',
    label: defineMessage({ message: 'LOST: кластеры (lost)' }),
    type: 'circle',
    'source-layer': LOST_DEVICES_SOURCE_LAYER,
    source: LOST_DEVICES_SOURCE,
    filter: [ 'all',
        [ '==', [ 'get', 'is_cluster' ], true ],
        [ '>', [ 'get', 'count_lost' ], 0 ],
    ],
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': lostClusterRadius('count_lost'),
        'circle-color': '#eab308',
        'circle-stroke-width': 1,
        'circle-stroke-color': [ 'case', [ '==', [ 'get', 'near_service_center' ], true ], '#3b82f6', '#fff' ],
        'circle-opacity': 0.85,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
const lostClusterLabelLost = {
    id: 'LOST: метки кластеров (lost)',
    label: defineMessage({ message: 'LOST: метки кластеров (lost)' }),
    type: 'symbol',
    'source-layer': LOST_DEVICES_SOURCE_LAYER,
    source: LOST_DEVICES_SOURCE,
    filter: [ 'all',
        [ '==', [ 'get', 'is_cluster' ], true ],
        [ '>', [ 'get', 'count_lost' ], 0 ],
    ],
    layout: {
        visibility: 'none',
        'text-field': [ 'to-string', [ 'get', 'count_lost' ] ],
        'text-font': [ 'DIN Pro Medium', 'Arial Unicode MS Bold' ],
        'text-size': 12,
        'text-anchor': 'left',
        'text-offset': lostClusterLabelOffset('count_lost'),
        'text-allow-overlap': true,
    },
    paint: {
        'text-color': '#eab308',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.5,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// ── Группа B: кластеры с DELETED (count_deleted > 0) ────────────────────────
// Показывается когда DELETED включён. Покрывает и DELETED-only, и mixed-кластеры.
const lostClusterCircleDeleted = {
    id: 'LOST: кластеры (deleted)',
    label: defineMessage({ message: 'LOST: кластеры (deleted)' }),
    type: 'circle',
    'source-layer': LOST_DEVICES_SOURCE_LAYER,
    source: LOST_DEVICES_SOURCE,
    filter: [ 'all',
        [ '==', [ 'get', 'is_cluster' ], true ],
        [ '>', [ 'get', 'count_deleted' ], 0 ],
    ],
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': lostClusterRadius('count_deleted'),
        'circle-color': '#ef4444',
        'circle-stroke-width': 1,
        'circle-stroke-color': [ 'case', [ '==', [ 'get', 'near_service_center' ], true ], '#3b82f6', '#fff' ],
        'circle-opacity': 0.85,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
const lostClusterLabelDeleted = {
    id: 'LOST: метки кластеров (deleted)',
    label: defineMessage({ message: 'LOST: метки кластеров (deleted)' }),
    type: 'symbol',
    'source-layer': LOST_DEVICES_SOURCE_LAYER,
    source: LOST_DEVICES_SOURCE,
    filter: [ 'all',
        [ '==', [ 'get', 'is_cluster' ], true ],
        [ '>', [ 'get', 'count_deleted' ], 0 ],
    ],
    layout: {
        visibility: 'none',
        'text-field': [ 'to-string', [ 'get', 'count_deleted' ] ],
        'text-font': [ 'DIN Pro Medium', 'Arial Unicode MS Bold' ],
        'text-size': 12,
        'text-anchor': 'left',
        'text-offset': lostClusterLabelOffset('count_deleted'),
        'text-allow-overlap': true,
    },
    paint: {
        'text-color': '#ef4444',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.5,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// ── Группа C: смешанные кластеры (count_lost > 0 AND count_deleted > 0) ────
// C1: нижний кружок — тот, у которого больше count (радиус по max, цвет динамический)
const lostClusterCircleMixedBottom = {
    id: 'LOST: кластеры (mixed, низ)',
    label: defineMessage({ message: 'LOST: кластеры (mixed, низ)' }),
    type: 'circle',
    'source-layer': LOST_DEVICES_SOURCE_LAYER,
    source: LOST_DEVICES_SOURCE,
    filter: [ 'all',
        [ '==', [ 'get', 'is_cluster' ], true ],
        [ '>', [ 'get', 'count_lost' ], 0 ],
        [ '>', [ 'get', 'count_deleted' ], 0 ],
    ],
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': lostClusterRadius(lostMixedMaxCount),
        'circle-color': lostMixedBottomColor,
        'circle-stroke-width': 1,
        'circle-stroke-color': [ 'case', [ '==', [ 'get', 'near_service_center' ], true ], '#3b82f6', '#fff' ],
        'circle-opacity': 0.85,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// C2: верхний кружок — тот, у которого меньше count (радиус по min, пропорционально меньше)
const lostClusterCircleMixedTop = {
    id: 'LOST: кластеры (mixed, верх)',
    label: defineMessage({ message: 'LOST: кластеры (mixed, верх)' }),
    type: 'circle',
    'source-layer': LOST_DEVICES_SOURCE_LAYER,
    source: LOST_DEVICES_SOURCE,
    filter: [ 'all',
        [ '==', [ 'get', 'is_cluster' ], true ],
        [ '>', [ 'get', 'count_lost' ], 0 ],
        [ '>', [ 'get', 'count_deleted' ], 0 ],
    ],
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': lostClusterRadius(lostMixedMinCount),
        'circle-color': lostMixedTopColor,
        'circle-stroke-width': [ 'case', [ '==', [ 'get', 'near_service_center' ], true ], 1.5, 0 ],
        'circle-stroke-color': '#3b82f6',
        'circle-opacity': 0.9,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// C3: метка — две строки (count_lost жёлтый / count_deleted красный)
const lostClusterLabelMixed = {
    id: 'LOST: метки кластеров (mixed)',
    label: defineMessage({ message: 'LOST: метки кластеров (mixed)' }),
    type: 'symbol',
    'source-layer': LOST_DEVICES_SOURCE_LAYER,
    source: LOST_DEVICES_SOURCE,
    filter: [ 'all',
        [ '==', [ 'get', 'is_cluster' ], true ],
        [ '>', [ 'get', 'count_lost' ], 0 ],
        [ '>', [ 'get', 'count_deleted' ], 0 ],
    ],
    layout: {
        visibility: 'none',
        'text-field': [
            'format',
            [ 'to-string', [ 'get', 'count_lost' ] ],   { 'text-color': '#eab308' },
            '\n',                                         {},
            [ 'to-string', [ 'get', 'count_deleted' ] ], { 'text-color': '#ef4444' },
        ],
        'text-font': [ 'DIN Pro Medium', 'Arial Unicode MS Bold' ],
        'text-size': 12,
        'text-anchor': 'left',
        'text-offset': lostClusterLabelOffset('total_count'),
        'text-line-height': 1.3,
        'text-allow-overlap': true,
    },
    paint: {
        'text-color': '#1f2937',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.5,
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// Подслой 1: одиночные точки (is_cluster = false) — главный слой с additionalLayers
const lostDevices = {
    id: 'Потерянные самокаты',
    label: defineMessage({ message: 'Потерянные самокаты' }),
    type: 'circle',
    'source-layer': LOST_DEVICES_SOURCE_LAYER,
    source: LOST_DEVICES_SOURCE,
    filter: [ '==', [ 'get', 'is_cluster' ], false ],
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': 3,
        'circle-stroke-width': [ 'case', [ '==', [ 'get', 'near_service_center' ], true ], 1.5, 0 ],
        'circle-stroke-color': '#3b82f6',
        'circle-opacity': 0.85,
        'circle-color': [
            'match',
            [ 'get', 'combined_status' ],
            'LOST',    '#eab308',
            'DELETED', '#ef4444',
            '#aaaaaa',
        ],
    },
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    additionalLayers: [
        lostClusterCircleLost, lostClusterLabelLost,
        lostClusterCircleDeleted, lostClusterLabelDeleted,
        lostClusterCircleMixedBottom, lostClusterCircleMixedTop, lostClusterLabelMixed,
    ],
};
// DA-12966: Агрегированные потери — агрегат событий потери по H3-ячейкам
const lostDevicesH3 = {
    id: 'Агрегированные потери',
    label: defineMessage({ message: 'Агрегированные потери' }),
    type: 'fill',
    minzoom: 4,
    'source-layer': 'martin.lost_devices_h3_mvt',
    source: {
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.lost_devices_h3_mvt.json`,
        requiredQueryParams: [ 'h3_zoom' ],
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'cnt_total' ] ],
            1,  '#0d0887',
            2,  '#6b00a8',
            5,  '#e16461',
            15, '#fca736',
            50, '#f0f921',
        ],
        'fill-opacity': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'cnt_total' ] ],
            1,  0.4,
            2,  0.5,
            5,  0.6,
            15, 0.6,
            50, 0.6,
        ],
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
    },
    legend: LOST_DEVICES_H3_LEGEND,
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
};
// DA-12966: перенесены из DEV в группу Самокаты
deviceLayers.push(lostDevices, lostDevicesH3);

// Драфт: вероятные призраки — стоящий самокат показан > 300 м от финиша последней поездки.
// На самокат три фичи с kind: shown (где показан), likely (финиш — где вероятно стоит), link (линия).
// В модели только самокаты с подтверждённым телефоном финишем (is_r6 всегда true).
export const GHOST_DEVICES_LAYER_ID = 'Вероятные призраки';
export const GHOST_DEVICES_SHOWN_ID = 'Вероятные призраки_показан';
export const GHOST_DEVICES_LIKELY_ID = 'Вероятные призраки_здесь';
export const GHOST_DEVICES_COLOR_SHOWN = '#ef4444';
export const GHOST_DEVICES_COLOR_LIKELY = '#16a34a';
export const GHOST_DEVICES_COLOR_CHANGE_BATTERY = '#f59e0b';
export const GHOST_DEVICES_ACCURACY_FILL_ID = 'Вероятные призраки_точность';
export const GHOST_DEVICES_ACCURACY_LINE_ID = 'Вероятные призраки_точность_контур';
export const GHOST_DEVICES_FINISH_ACCURACY_FILL_ID = 'Вероятные призраки_точность финиша';
export const GHOST_DEVICES_FINISH_ACCURACY_LINE_ID = 'Вероятные призраки_точность финиша_контур';
// Статусы самокатов (status) — тумблерами группы «Статусы» в легенде (mapFilters.ghostDevicesShowStandBy /
// ghostDevicesShowChangeBattery); фича без status — STAND_BY. LOST и NOT_READY были тестово с 14.09
// и убраны 20.09: полевая проверка показала, что финиш последней поездки у них не подсказка.
export const ghostDevicesStatuses = ({ ghostDevicesShowStandBy, ghostDevicesShowChangeBattery }) => [
    ...(ghostDevicesShowStandBy ? [ 'STAND_BY' ] : []),
    ...(ghostDevicesShowChangeBattery ? [ 'CHANGE_BATTERY' ] : []),
];
export const GHOST_DEVICES_DEFAULT_STATUSES = [ 'STAND_BY', 'CHANGE_BATTERY' ];
export const ghostDevicesFilter = (kind, statuses = GHOST_DEVICES_DEFAULT_STATUSES) => [
    'all',
    [ '==', [ 'get', 'kind' ], kind ],
    [ 'in', [ 'coalesce', [ 'get', 'status' ], 'STAND_BY' ], [ 'literal', statuses ] ],
];
// Круги точности (kind = 'accuracy' — показанной позиции, 'finish_accuracy' — финиша) включаются
// тумблерами в легенде (mapFilters.ghostDevicesShowAccuracy / ghostDevicesShowFinishAccuracy).
// Видимостью подслоя управляет родительский toggleLayer, поэтому выключаем фильтром, а не visibility.
export const ghostDevicesAccuracyFilter = (kind, show, statuses = GHOST_DEVICES_DEFAULT_STATUSES) => (show
    ? ghostDevicesFilter(kind, statuses)
    : [ '==', [ 'get', 'kind' ], '__off__' ]);
const GHOST_DEVICES_SOURCE = {
    id: 'martin.ghost_devices_mvt',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.ghost_devices_mvt.json`,
};
const GHOST_DEVICES_SOURCE_LAYER = 'martin.ghost_devices_mvt';
const GHOST_DEVICES_ROLES = [ V.ADMIN, V.SUPER_ADMIN, V.URBANIST, V.DESIGNER ];
// показанная позиция самоката в CHANGE_BATTERY — янтарная, чтобы отличать от стоящих
const ghostShownColor = [
    'match', [ 'get', 'status' ],
    'CHANGE_BATTERY', GHOST_DEVICES_COLOR_CHANGE_BATTERY,
    GHOST_DEVICES_COLOR_SHOWN,
];
const ghostDevicesShown = {
    id: GHOST_DEVICES_SHOWN_ID,
    label: defineMessage({ message: 'Вероятные призраки: где показан' }),
    type: 'circle',
    'source-layer': GHOST_DEVICES_SOURCE_LAYER,
    source: GHOST_DEVICES_SOURCE,
    filter: ghostDevicesFilter('shown'),
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 10, 2.5, 16, 6 ],
        'circle-color': '#ffffff',
        'circle-stroke-width': 2,
        'circle-stroke-color': ghostShownColor,
        'circle-opacity': 0.9,
    },
    availableFor: GHOST_DEVICES_ROLES,
};
const ghostDevicesLikely = {
    id: GHOST_DEVICES_LIKELY_ID,
    label: defineMessage({ message: 'Вероятные призраки: вероятно здесь' }),
    type: 'circle',
    'source-layer': GHOST_DEVICES_SOURCE_LAYER,
    source: GHOST_DEVICES_SOURCE,
    filter: ghostDevicesFilter('likely'),
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 10, 3, 16, 7 ],
        'circle-color': GHOST_DEVICES_COLOR_LIKELY,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.95,
    },
    availableFor: GHOST_DEVICES_ROLES,
};
// круг радиусом accuracy вокруг показанной позиции — полигон из модели (kind = 'accuracy')
const ghostDevicesAccuracyFill = {
    id: GHOST_DEVICES_ACCURACY_FILL_ID,
    label: defineMessage({ message: 'Вероятные призраки: точность позиции' }),
    type: 'fill',
    'source-layer': GHOST_DEVICES_SOURCE_LAYER,
    source: GHOST_DEVICES_SOURCE,
    filter: ghostDevicesAccuracyFilter('accuracy', false),
    layout: { visibility: 'none' },
    paint: {
        'fill-color': ghostShownColor,
        'fill-opacity': 0.08,
    },
    availableFor: GHOST_DEVICES_ROLES,
};
const ghostDevicesAccuracyLine = {
    id: GHOST_DEVICES_ACCURACY_LINE_ID,
    label: defineMessage({ message: 'Вероятные призраки: контур точности' }),
    type: 'line',
    'source-layer': GHOST_DEVICES_SOURCE_LAYER,
    source: GHOST_DEVICES_SOURCE,
    filter: ghostDevicesAccuracyFilter('accuracy', false),
    layout: { visibility: 'none' },
    paint: {
        'line-color': ghostShownColor,
        'line-width': 1,
        'line-opacity': 0.6,
    },
    availableFor: GHOST_DEVICES_ROLES,
};
// круг вокруг финиша радиусом расхождения самоката и телефона на финише (kind = 'finish_accuracy')
const ghostDevicesFinishAccuracyFill = {
    id: GHOST_DEVICES_FINISH_ACCURACY_FILL_ID,
    label: defineMessage({ message: 'Вероятные призраки: точность финиша' }),
    type: 'fill',
    'source-layer': GHOST_DEVICES_SOURCE_LAYER,
    source: GHOST_DEVICES_SOURCE,
    filter: ghostDevicesAccuracyFilter('finish_accuracy', true),
    layout: { visibility: 'none' },
    paint: {
        'fill-color': GHOST_DEVICES_COLOR_LIKELY,
        'fill-opacity': 0.12,
    },
    availableFor: GHOST_DEVICES_ROLES,
};
const ghostDevicesFinishAccuracyLine = {
    id: GHOST_DEVICES_FINISH_ACCURACY_LINE_ID,
    label: defineMessage({ message: 'Вероятные призраки: контур точности финиша' }),
    type: 'line',
    'source-layer': GHOST_DEVICES_SOURCE_LAYER,
    source: GHOST_DEVICES_SOURCE,
    filter: ghostDevicesAccuracyFilter('finish_accuracy', true),
    layout: { visibility: 'none' },
    paint: {
        'line-color': GHOST_DEVICES_COLOR_LIKELY,
        'line-width': 1,
        'line-opacity': 0.7,
    },
    availableFor: GHOST_DEVICES_ROLES,
};
const ghostDevices = {
    id: GHOST_DEVICES_LAYER_ID,
    label: defineMessage({ message: 'Вероятные призраки' }),
    // Модель пересчитывается раз в 10 мин, а MVT-функция ещё и сверяет каждую строку с текущим
    // статусом и позицией самоката — тайлы стоит перезапрашивать, пока пользователь за картой
    liveRefresh: true,
    type: 'line',
    'source-layer': GHOST_DEVICES_SOURCE_LAYER,
    source: GHOST_DEVICES_SOURCE,
    filter: ghostDevicesFilter('link'),
    layout: { visibility: 'none', 'line-cap': 'round' },
    paint: {
        'line-color': ghostShownColor,
        'line-width': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 10, 1, 16, 2.5 ],
        'line-dasharray': [ 2, 2 ],
        'line-opacity': 0.8,
    },
    availableFor: GHOST_DEVICES_ROLES,
    additionalLayers: [
        ghostDevicesAccuracyFill, ghostDevicesAccuracyLine,
        ghostDevicesFinishAccuracyFill, ghostDevicesFinishAccuracyLine,
        ghostDevicesShown, ghostDevicesLikely,
    ],
};
deviceLayers.push(ghostDevices);

// Самокаты → «Поиск лостов» (пилот, полевая проверка; до 23.10.2026 — в группе DEV).
// Хвост последних известных координат самоката перед уходом в LOST: по нему видно и разброс
// сигналов, и куда самокат уехал. Истории позиции в хранилище нет — «трек» собран из событий,
// которые что-то записали (смена статуса, скан, бронь, LBS-запрос, собственный GNSS-фикс).
// На самокат много фич с kind: point (точка хвоста), radius (круг точности), track (линия),
// anchor (куда идти искать — медиана последних трёх точек), finish (конец последней поездки).
export const LOST_SEARCH_LAYER_ID = 'Поиск лостов';
export const LOST_SEARCH_POINTS_ID = 'Поиск лостов_точки';
export const LOST_SEARCH_RADIUS_FILL_ID = 'Поиск лостов_точность';
export const LOST_SEARCH_RADIUS_LINE_ID = 'Поиск лостов_точность_контур';
export const LOST_SEARCH_ANCHOR_ID = 'Поиск лостов_якорь';
export const LOST_SEARCH_THEFT_ID = 'Поиск лостов_кража';
export const LOST_SEARCH_FINISH_ID = 'Поиск лостов_финиш';
export const LOST_SEARCH_ZONE_FILL_ID = 'Поиск лостов_зона';
export const LOST_SEARCH_ZONE_LINE_ID = 'Поиск лостов_зона_контур';
export const LOST_SEARCH_HIGHLIGHT_ID = 'Поиск лостов_подсветка';
// Цвета классов. Порядок смысловой: чем «холоднее», тем ближе самокат к финишу.
export const LOST_SEARCH_COLORS = {
    A: '#16a34a',  // умер на финише — облако в 150 м
    B: '#a855f7',  // выбросы координат — врёт GNSS самоката
    C: '#f59e0b',  // сместился <1 км — реальный переезд
    D1: '#64748b', // разрядился на финише, точек после нет
    D2: '#ef4444', // пропал с живой батареей — подозрение на кражу
    E: '#0ea5e9',  // уехал >1 км
};
export const LOST_SEARCH_CLASS_LABELS = {
    A: 'Умер на финише',
    B: 'Выбросы координат',
    C: 'Сместился < 1 км',
    D1: 'Разрядился на финише',
    D2: 'Пропал с зарядом (кража?)',
    E: 'Уехал > 1 км',
};
export const LOST_SEARCH_DEFAULT_CLASSES = [ 'A', 'B', 'C', 'D1', 'D2', 'E' ];
// Тумблеры классов в легенде (mapFilters.lostSearchShow<Класс>)
export const lostSearchClasses = ({
    lostSearchShowA, lostSearchShowB, lostSearchShowC,
    lostSearchShowD1, lostSearchShowD2, lostSearchShowE,
}) => [
    ...(lostSearchShowA ? [ 'A' ] : []),
    ...(lostSearchShowB ? [ 'B' ] : []),
    ...(lostSearchShowC ? [ 'C' ] : []),
    ...(lostSearchShowD1 ? [ 'D1' ] : []),
    ...(lostSearchShowD2 ? [ 'D2' ] : []),
    ...(lostSearchShowE ? [ 'E' ] : []),
];
export const lostSearchFilter = (kind, classes = LOST_SEARCH_DEFAULT_CLASSES) => [
    'all',
    [ '==', [ 'get', 'kind' ], kind ],
    [ 'in', [ 'get', 'cls' ], [ 'literal', classes ] ],
];
// Подслой выключается фильтром, а не visibility: видимостью управляет родительский toggleLayer
export const lostSearchToggleFilter = (kind, show, classes = LOST_SEARCH_DEFAULT_CLASSES) => (show
    ? lostSearchFilter(kind, classes)
    : [ '==', [ 'get', 'kind' ], '__off__' ]);
// Круги точности отдельных точек по умолчанию скрыты: на полусотне точек они сливаются
// в кляксу. Показывается ровно один — у точки, по которой кликнули, либо у той, что сейчас
// горит в анимации трека. sel — { device_id, ts_local, src } или null.
export const lostSearchPointKey = props => (props
    ? `${props.device_id}|${props.ts_local}|${props.src}`
    : null);
const lostSearchSelMatch = (kind, sel) => [
    'all',
    [ '==', [ 'get', 'kind' ], kind ],
    [ '==', [ 'get', 'device_id' ], sel.device_id ],
    [ '==', [ 'get', 'ts_local' ], sel.ts_local ],
    [ '==', [ 'get', 'src' ], sel.src ],
];
export const lostSearchSelFilter = (kind, sel) => (sel
    ? lostSearchSelMatch(kind, sel)
    : [ '==', [ 'get', 'kind' ], '__off__' ]);
// Только последняя точка самоката, замолчавшего с живой батареей (is_theft_suspect) —
// двойная обводка поверх якоря, независимо от класса
export const lostSearchTheftFilter = (show, classes = LOST_SEARCH_DEFAULT_CLASSES) => (show
    ? [ 'all', lostSearchFilter('anchor', classes), [ '==', [ 'get', 'is_theft_suspect' ], true ] ]
    : [ '==', [ 'get', 'kind' ], '__off__' ]);
const LOST_SEARCH_SOURCE = {
    id: 'martin.lost_search_mvt',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.lost_search_mvt.json`,
};
const LOST_SEARCH_SOURCE_LAYER = 'martin.lost_search_mvt';
const LOST_SEARCH_ROLES = [ V.ADMIN ];
const lostSearchColor = [
    'match', [ 'get', 'cls' ],
    'A', LOST_SEARCH_COLORS.A,
    'B', LOST_SEARCH_COLORS.B,
    'C', LOST_SEARCH_COLORS.C,
    'D1', LOST_SEARCH_COLORS.D1,
    'D2', LOST_SEARCH_COLORS.D2,
    'E', LOST_SEARCH_COLORS.E,
    '#64748b',
];
// ненадёжная точка (is_weak — улетела от якоря дальше километра) рисуется прозрачной:
// видно, что сигнал был, но идти туда не надо
const lostSearchWeakOpacity = weak => [ 'case', [ '==', [ 'get', 'is_weak' ], true ], weak, 1 ];
const lostSearchPoints = {
    id: LOST_SEARCH_POINTS_ID,
    label: defineMessage({ message: 'Поиск лостов: точки хвоста' }),
    type: 'circle',
    'source-layer': LOST_SEARCH_SOURCE_LAYER,
    source: LOST_SEARCH_SOURCE,
    filter: lostSearchFilter('point'),
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 10, 2, 16, 5 ],
        'circle-color': lostSearchColor,
        'circle-opacity': lostSearchWeakOpacity(0.3),
        'circle-stroke-width': 1,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-opacity': lostSearchWeakOpacity(0.3),
    },
    availableFor: LOST_SEARCH_ROLES,
};
// круг точности вокруг точки — полигон из модели (круг-символ на фронте резался бы по тайлам).
// Радиусы больше километра модель не отдаёт вовсе: от них нет пользы.
const lostSearchRadiusFill = {
    id: LOST_SEARCH_RADIUS_FILL_ID,
    label: defineMessage({ message: 'Поиск лостов: точность' }),
    type: 'fill',
    'source-layer': LOST_SEARCH_SOURCE_LAYER,
    source: LOST_SEARCH_SOURCE,
    filter: lostSearchToggleFilter('radius', true),
    layout: { visibility: 'none' },
    paint: {
        'fill-color': lostSearchColor,
        'fill-opacity': 0.08,
    },
    availableFor: LOST_SEARCH_ROLES,
};
const lostSearchRadiusLine = {
    id: LOST_SEARCH_RADIUS_LINE_ID,
    label: defineMessage({ message: 'Поиск лостов: точность (контур)' }),
    type: 'line',
    'source-layer': LOST_SEARCH_SOURCE_LAYER,
    source: LOST_SEARCH_SOURCE,
    filter: lostSearchToggleFilter('radius', true),
    layout: { visibility: 'none' },
    paint: {
        'line-color': lostSearchColor,
        'line-width': 0.8,
        'line-opacity': 0.5,
    },
    availableFor: LOST_SEARCH_ROLES,
};
// якорь: медиана последних трёх точек — куда идти искать
const lostSearchAnchor = {
    id: LOST_SEARCH_ANCHOR_ID,
    label: defineMessage({ message: 'Поиск лостов: где искать' }),
    type: 'circle',
    'source-layer': LOST_SEARCH_SOURCE_LAYER,
    source: LOST_SEARCH_SOURCE,
    filter: lostSearchFilter('anchor'),
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 10, 4, 16, 9 ],
        'circle-color': lostSearchColor,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
    },
    availableFor: LOST_SEARCH_ROLES,
};
// двойной кружок вокруг якоря: самокат замолчал с живой батареей (≥30%) — подозрение на кражу
const lostSearchTheft = {
    id: LOST_SEARCH_THEFT_ID,
    label: defineMessage({ message: 'Поиск лостов: подозрение на кражу' }),
    type: 'circle',
    'source-layer': LOST_SEARCH_SOURCE_LAYER,
    source: LOST_SEARCH_SOURCE,
    filter: lostSearchTheftFilter(true),
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 10, 8, 16, 15 ],
        'circle-color': 'rgba(0, 0, 0, 0)',
        'circle-stroke-width': 2,
        'circle-stroke-color': LOST_SEARCH_COLORS.D2,
        'circle-stroke-opacity': 0.9,
    },
    availableFor: LOST_SEARCH_ROLES,
};
// финиш последней поездки: для D1/D2 единственная точка, где самокат видели живым
const lostSearchFinish = {
    id: LOST_SEARCH_FINISH_ID,
    label: defineMessage({ message: 'Поиск лостов: финиш поездки' }),
    type: 'circle',
    'source-layer': LOST_SEARCH_SOURCE_LAYER,
    source: LOST_SEARCH_SOURCE,
    filter: lostSearchToggleFilter('finish', true),
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 10, 3, 16, 6 ],
        'circle-color': '#ffffff',
        'circle-stroke-width': 2,
        'circle-stroke-color': lostSearchColor,
    },
    availableFor: LOST_SEARCH_ROLES,
};
// Зона поиска вокруг якоря: радиус, накрывающий последние десять точек хвоста. Единственный
// круг, который виден по умолчанию — остальные включаются точечно по клику.
const lostSearchZoneFill = {
    id: LOST_SEARCH_ZONE_FILL_ID,
    label: defineMessage({ message: 'Поиск лостов: зона поиска' }),
    type: 'fill',
    'source-layer': LOST_SEARCH_SOURCE_LAYER,
    source: LOST_SEARCH_SOURCE,
    filter: lostSearchToggleFilter('anchor_radius', true),
    layout: { visibility: 'none' },
    paint: {
        'fill-color': lostSearchColor,
        'fill-opacity': 0.1,
    },
    availableFor: LOST_SEARCH_ROLES,
};
const lostSearchZoneLine = {
    id: LOST_SEARCH_ZONE_LINE_ID,
    label: defineMessage({ message: 'Поиск лостов: зона поиска (контур)' }),
    type: 'line',
    'source-layer': LOST_SEARCH_SOURCE_LAYER,
    source: LOST_SEARCH_SOURCE,
    filter: lostSearchToggleFilter('anchor_radius', true),
    layout: { visibility: 'none' },
    paint: {
        'line-color': lostSearchColor,
        'line-width': 1.2,
        'line-opacity': 0.6,
        'line-dasharray': [ 3, 2 ],
    },
    availableFor: LOST_SEARCH_ROLES,
};
// Точка, которая сейчас горит: выбрана кликом или идёт по треку в анимации
const lostSearchHighlight = {
    id: LOST_SEARCH_HIGHLIGHT_ID,
    label: defineMessage({ message: 'Поиск лостов: подсветка точки' }),
    type: 'circle',
    'source-layer': LOST_SEARCH_SOURCE_LAYER,
    source: LOST_SEARCH_SOURCE,
    filter: lostSearchSelFilter('point', null),
    layout: { visibility: 'none' },
    paint: {
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 10, 6, 16, 12 ],
        'circle-color': 'rgba(0, 0, 0, 0)',
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-opacity': 0.9,
    },
    availableFor: LOST_SEARCH_ROLES,
};
const lostSearch = {
    id: LOST_SEARCH_LAYER_ID,
    label: defineMessage({ message: 'Поиск лостов' }),
    type: 'line',
    'source-layer': LOST_SEARCH_SOURCE_LAYER,
    source: LOST_SEARCH_SOURCE,
    filter: lostSearchFilter('track'),
    layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' },
    paint: {
        'line-color': lostSearchColor,
        'line-width': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 10, 1, 16, 2.5 ],
        'line-opacity': 0.7,
    },
    availableFor: LOST_SEARCH_ROLES,
    additionalLayers: [
        lostSearchZoneFill, lostSearchZoneLine,
        lostSearchRadiusFill, lostSearchRadiusLine,
        lostSearchFinish, lostSearchPoints, lostSearchHighlight,
        lostSearchTheft, lostSearchAnchor,
    ],
};
deviceLayers.push(lostSearch);
// DA-14587: ошибки геометрии операционных зон
const OP_AREA_ERR_SOURCE = {
    id: 'martin.operation_area_err_mvt_source',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.operation_area_err_mvt.json`,
};
const OP_AREA_ERR_SOURCE_LAYER = 'martin.operation_area_err_mvt';
// Цветовая градация по тяжести: салатовый (косметика) → синий (критично)
const OP_AREA_ERR_COLOR = [
    'match', [ 'get', 'error_type' ],
    'hole',              '#A3E635',  // салатовый    — дырка в полигоне
    'multipolygon_part', '#34D399',  // изумрудный  — лишняя часть
    'duplicate_feature', '#22D3EE',  // голубой     — дубликат зоны
    'self_intersection', '#60A5FA',  // синеватый   — невалидная геометрия
    'overlap',           '#2563EB',  // синий       — перекрытие зон
    '#60A5FA',
];
const operationAreaErrFill = {
    id: 'Ошибки опер. зон: заливка',
    type: 'fill',
    'source-layer': OP_AREA_ERR_SOURCE_LAYER,
    source: OP_AREA_ERR_SOURCE,
    layout: { visibility: 'none' },
    paint: { 'fill-color': OP_AREA_ERR_COLOR, 'fill-opacity': 0.5 },
    filter: [ '==', [ 'get', 'geom_role' ], 'polygon' ],
    availableFor: [ V.SUPER_ADMIN ],
};
const operationAreaErrLine = {
    id: 'Ошибки опер. зон: контур',
    type: 'line',
    'source-layer': OP_AREA_ERR_SOURCE_LAYER,
    source: OP_AREA_ERR_SOURCE,
    layout: { visibility: 'none' },
    paint: { 'line-color': OP_AREA_ERR_COLOR, 'line-width': 2 },
    filter: [ '==', [ 'get', 'geom_role' ], 'line' ],
    availableFor: [ V.SUPER_ADMIN ],
};
const operationAreaErrPoint = {
    id: 'Ошибки опер. зон: точки',
    type: 'circle',
    'source-layer': OP_AREA_ERR_SOURCE_LAYER,
    source: OP_AREA_ERR_SOURCE,
    layout: { visibility: 'none' },
    paint: { 'circle-color': OP_AREA_ERR_COLOR, 'circle-radius': 6, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1 },
    filter: [ '==', [ 'get', 'geom_role' ], 'point' ],
    availableFor: [ V.SUPER_ADMIN ],
};
// DA-14587: центроиды ошибок — видимы только на малых зумах (z < 11), для поиска ошибок в масштабе мира
const operationAreaErrCentroid = {
    id: 'Ошибки опер. зон: центроиды',
    type: 'circle',
    'source-layer': OP_AREA_ERR_SOURCE_LAYER,
    source: OP_AREA_ERR_SOURCE,
    maxzoom: 11,
    layout: { visibility: 'none' },
    paint: { 'circle-color': OP_AREA_ERR_COLOR, 'circle-radius': 5, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1 },
    filter: [ '==', [ 'get', 'geom_role' ], 'centroid' ],
    availableFor: [ V.SUPER_ADMIN ],
};
// DA-14587: ошибки геометрии функциональных зон (area_v2)
const FUNC_AREA_ERR_SOURCE = {
    id: 'martin.functional_area_err_mvt_source',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.functional_area_err_mvt.json`,
};
const FUNC_AREA_ERR_SOURCE_LAYER = 'martin.functional_area_err_mvt';
// Цветовая градация функц. зон: жёлтый (косметика) → тёмно-красный (критично)
const FUNC_AREA_ERR_COLOR = [
    'match', [ 'get', 'error_type' ],
    'hole',              '#FDE047',  // жёлтый       — дырка в полигоне
    'multipolygon_part', '#F97316',  // оранжевый    — лишняя часть
    'duplicate_feature', '#EA580C',  // тём.-оранжевый — дубликат зоны
    'self_intersection', '#DC2626',  // красный      — невалидная геометрия
    'overlap',           '#991B1B',  // тём.-красный  — перекрытие зон
    '#e53e3e',
];
const functionalAreaErrFill = {
    id: 'Ошибки функц. зон: заливка',
    type: 'fill',
    'source-layer': FUNC_AREA_ERR_SOURCE_LAYER,
    source: FUNC_AREA_ERR_SOURCE,
    layout: { visibility: 'none' },
    paint: { 'fill-color': FUNC_AREA_ERR_COLOR, 'fill-opacity': 0.5 },
    filter: [ '==', [ 'get', 'geom_role' ], 'polygon' ],
    availableFor: [ V.SUPER_ADMIN ],
};
const functionalAreaErrLine = {
    id: 'Ошибки функц. зон: контур',
    type: 'line',
    'source-layer': FUNC_AREA_ERR_SOURCE_LAYER,
    source: FUNC_AREA_ERR_SOURCE,
    layout: { visibility: 'none' },
    paint: { 'line-color': FUNC_AREA_ERR_COLOR, 'line-width': 2 },
    filter: [ '==', [ 'get', 'geom_role' ], 'line' ],
    availableFor: [ V.SUPER_ADMIN ],
};
const functionalAreaErrPoint = {
    id: 'Ошибки функц. зон: точки',
    type: 'circle',
    'source-layer': FUNC_AREA_ERR_SOURCE_LAYER,
    source: FUNC_AREA_ERR_SOURCE,
    layout: { visibility: 'none' },
    paint: { 'circle-color': FUNC_AREA_ERR_COLOR, 'circle-radius': 6, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1 },
    filter: [ '==', [ 'get', 'geom_role' ], 'point' ],
    availableFor: [ V.SUPER_ADMIN ],
};
const functionalAreaErrCentroid = {
    id: 'Ошибки функц. зон: центроиды',
    type: 'circle',
    'source-layer': FUNC_AREA_ERR_SOURCE_LAYER,
    source: FUNC_AREA_ERR_SOURCE,
    maxzoom: 11,
    layout: { visibility: 'none' },
    paint: { 'circle-color': FUNC_AREA_ERR_COLOR, 'circle-radius': 5, 'circle-stroke-color': '#fff', 'circle-stroke-width': 1 },
    filter: [ '==', [ 'get', 'geom_role' ], 'centroid' ],
    availableFor: [ V.SUPER_ADMIN ],
};
const operationAreaErrLayer = {
    id: 'Ошибки геометрии',
    label: defineMessage({ message: 'Ошибки геометрии' }),
    type: 'fill',
    'source-layer': OP_AREA_ERR_SOURCE_LAYER,
    source: OP_AREA_ERR_SOURCE,
    layout: { visibility: 'none' },
    paint: { 'fill-color': '#e53e3e', 'fill-opacity': 0 },
    availableFor: [ V.SUPER_ADMIN ],
    additionalLayers: [
        operationAreaErrFill, operationAreaErrLine, operationAreaErrPoint, operationAreaErrCentroid,
        functionalAreaErrFill, functionalAreaErrLine, functionalAreaErrPoint, functionalAreaErrCentroid,
    ],
};
// ─────────────────────────────────────────────────────────────────────────────
// DA-14933 — Треки исполнителей (scr_v2 + WCMA)
// MVT-функция: martin.scr_tracks_mvt (Aurora PostGIS), параметр query_param 'date'.
// Геометрия: линии-сегменты смены (одна смена разрезана на сегменты в точках событий).
// Цвет — по роли исполнителя (role_target: ranger / service_car / charger / coordinator).
// promoteId: shift_id — общий id для всех сегментов одной смены (для выбора/детализации).
// Динамические состояния (выбор смены → dimming остальных, подсветка часа,
// выключение ролей) применяются в Map.jsx (applyScrTracksStyle / setFilter).
// ─────────────────────────────────────────────────────────────────────────────
const SCR_TRACKS_SOURCE = {
    id: 'martin.scr_tracks_mvt',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.scr_tracks_mvt.json`,
    promoteId: 'shift_id',
    requiredQueryParams: [ 'date' ],
};
// Цвет-выражение по роли исполнителя (из общего SCR_TRACKS_STYLE.roles).
// Экспортируется — Map.jsx переиспользует его в applyScrTracksStyle.
export const SCR_TRACKS_ROLE_COLOR = [
    'match', [ 'get', 'role_target' ],
    ...SCR_TRACKS_STYLE.roles.flatMap(r => [ r.value, r.color ]),
    '#757575',
];
// Базовая ширина (режим просмотра слоя целиком) и утолщённая (×2) для подсветки часа.
export const SCR_TRACKS_WIDTH_BASE  = [ 'interpolate', [ 'linear' ], [ 'zoom' ], 8, 1.5, 14, 3, 18, 5   ];
export const SCR_TRACKS_WIDTH_THICK = [ 'interpolate', [ 'linear' ], [ 'zoom' ], 8, 3,   14, 6, 18, 10  ];
// Стоячие операции (transit_mode='на месте', этап 12) — вырожденные сегменты-точки.
// Рисуем их явным маркером-кружком поверх линии (line-cap:round давал лишь намёк).
// transit_mode есть только в detail-ветке (z>=12), поэтому слой работает с z12.
export const SCR_TRACKS_STATIONARY_ID = 'Треки исполнителей (стоянки)';
export const SCR_TRACKS_STATIONARY_BASE_FILTER = [ '==', [ 'get', 'transit_mode' ], 'на месте' ];
const scrTracksStationary = {
    id: SCR_TRACKS_STATIONARY_ID,
    type: 'circle',
    'source-layer': 'martin.scr_tracks_mvt',
    source: SCR_TRACKS_SOURCE,
    layout: { visibility: 'none' },
    filter: SCR_TRACKS_STATIONARY_BASE_FILTER,
    paint: {
        'circle-color': SCR_TRACKS_ROLE_COLOR,
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 12, 3, 16, 5, 18, 6.5 ],
        'circle-stroke-color': '#2b2b2b', // цвет тёмного фона карты (как у ключевых точек)
        'circle-stroke-width': 3,
        'circle-opacity': 0.9,
        'circle-stroke-opacity': 0.5,
    },
    minzoom: 12,
};
// Инфраструктура снаппинга (подслой scr_infra MVT-функции): парковки (с z12, выдаёт сама
// функция), СЦ/зарядные шкафы/станции — на всех зумах. Большие точки с белой обводкой,
// как ключевые точки трека. Цвет по виду (kind); видимость видов — через setFilter из легенды.
export const SCR_INFRA_ID = 'Треки исполнителей (инфраструктура)';
export const SCR_INFRA_KIND_COLOR = [
    'match', [ 'get', 'kind' ],
    ...SCR_INFRA_STYLE.kinds.flatMap(k => [ k.value, k.color ]),
    '#9e9e9e',
];
const scrInfra = {
    id: SCR_INFRA_ID,
    type: 'circle',
    'source-layer': 'scr_infra',
    source: SCR_TRACKS_SOURCE,
    layout: { visibility: 'none' },
    paint: {
        'circle-color': SCR_INFRA_KIND_COLOR,
        'circle-radius': [ 'interpolate', [ 'linear' ], [ 'zoom' ], 8, 3.5, 12, 4.5, 16, 6, 18, 7.5 ],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1,
        'circle-opacity': 0.9,
        'circle-stroke-opacity': 0.9,
    },
};
const scrTracks = {
    id: 'Треки исполнителей',
    label: defineMessage({ message: 'Треки исполнителей' }),
    type: 'line',
    'source-layer': 'martin.scr_tracks_mvt',
    source: SCR_TRACKS_SOURCE,
    layout: { visibility: 'none', 'line-cap': 'round', 'line-join': 'round' },
    paint: {
        'line-color': SCR_TRACKS_ROLE_COLOR,
        'line-width': SCR_TRACKS_WIDTH_BASE,
        'line-opacity': 0.85,
    },
    minzoom: 8,
    availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    additionalLayers: [ scrInfra, scrTracksStationary ],
    // Атрибуты (z>=12): seg_id, shift_id, employee_user_id, employee_name, move_mode, transit_mode,
    // role_target, seg_seq, ts_from, ts_to, ts_from_epoch, ts_to_epoch, dur_s, seg_len_m,
    // routed, n_active, activity_json, endpoint_infra, geo_problems, shift_start, shift_end, bbox
    // transit_mode (этап 12): 'на месте' | 'пешком/самокат' | 'авто' — фактический режим ноги по скорости.
    // activity_json: [{device_code, action_label, active_ride, cargo, battery_swapped, service_scooter}].
};
rebalancing.push(scrTracks);
// ─────────────────────────────────────────────────────────────────────────────
// rebalance-chains — Статистика цепочек трипов
// MVT-функции: martin.chain_statistics_parkings_mvt (точки), martin.chain_statistics_edges_mvt (линии).
// Источник данных — разовый снэпшот public_mart.rebalance_chains_trips (Redshift), см.
// database-architecture/models/martin/chain_statistics_parkings.sql|chain_statistics_edges.sql.
// Три состояния клика (см. Map.jsx: setChainStatsView, handleChainStatsClick,
// handleChainStatsBackgroundClick):
//   0 — вся сеть: точки-парковки, размер по медианной глубине цепочки;
//   1 — открыта цепочка одной парковки (клик на точку уровня 0): видны только точки этой
//       цепочки (корень + все конечные точки её рёбер) + сами рёбра;
//   2 — подсветка узла/ребра внутри цепочки (клик на точку/линию внутри уровня 1): связанные
//       рёбра/узлы в полной непрозрачности, остальные — бледные.
// Состояние — в mapFilters (chainStatsSelectedParkingId, chainStatsHighlightedNodeId и т.д.),
// а не в отдельном редьюсере map — по образцу rebalanceForecastTabaMode.
// ─────────────────────────────────────────────────────────────────────────────
export const CHAIN_STATS_PARKINGS_ID = 'Статистика цепочек трипов';
export const CHAIN_STATS_EDGES_ID = 'Статистика цепочек трипов_edges';
// Фиктивные рёбра — синтетические переходы поверх разрывов цепочки (см.
// chain_statistics_edges.sql: is_fictitious), рисуются пунктиром отдельным слоем поверх
// того же source-layer — line-dasharray не поддерживает data-driven expressions ('get'),
// поэтому сплошные/пунктирные рёбра разведены по двум paint-слоям с разными filter,
// а не одним слоем с условной толщиной штриха.
export const CHAIN_STATS_EDGES_FICTITIOUS_ID = 'Статистика цепочек трипов_edges_fictitious';
export const CHAIN_STATS_EDGE_LAYER_IDS = [ CHAIN_STATS_EDGES_ID, CHAIN_STATS_EDGES_FICTITIOUS_ID ];
const CHAIN_STATS_IS_FICTITIOUS = [ '==', [ 'get', 'is_fictitious' ], true ];
const CHAIN_STATS_IS_REAL = [ '!=', [ 'get', 'is_fictitious' ], true ];
// Фильтр по умолчанию — ничего не показывает, пока не выбрана парковка (заполняется в Map.jsx).
const CHAIN_STATS_EDGES_EMPTY_FILTER = [ 'all', [ '==', [ 'get', 'start_parking_id' ], -1 ], CHAIN_STATS_IS_REAL ];
const CHAIN_STATS_EDGES_FICTITIOUS_EMPTY_FILTER = [ 'all', [ '==', [ 'get', 'start_parking_id' ], -1 ], CHAIN_STATS_IS_FICTITIOUS ];
// Толщина — по max_steps (убывающая "оставшаяся глубина" от точки этого перехода до конца
// цепочки; см. chain_statistics_edges.sql), не по trip_count (тот про частоту перехода).
// Распределение: медиана=3, p90=7, p99=12, максимум=57. Общая для сплошных и пунктирных рёбер.
const CHAIN_STATS_EDGE_WIDTH = [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'max_steps' ], 0 ],
    1,  1,
    3,  2,
    7,  4,
    12, 6,
    20, 8,
    57, 12,
];
// Цвет узлов/рёбер — градуирован по числу поездок (не по глубине цепочки — та кодируется
// размером/толщиной выше): мало — синий, дальше зелёный → оранжевый, много — красный.
// Общая палитра для обеих сущностей; стопы свои — trip_count и total_trips сильно
// различаются по масштабу (у рёбер медиана=1, p99=7; у узлов медиана=18, p99=1866).
// Экспортированы для легенды (ChainStatisticsLegend) — единый источник цветов для стиля и подписи.
export const CHAIN_STATS_COLOR_LOW = '#1e88e5'; // синий — мало поездок
export const CHAIN_STATS_COLOR_MID_LOW = '#43a047'; // зелёный
export const CHAIN_STATS_COLOR_MID_HIGH = '#fb8c00'; // оранжевый
export const CHAIN_STATS_COLOR_HIGH = '#e53935'; // красный — много поездок
// trip_count по рёбрам: медиана=1, p75=1, p90=2, p99=7, максимум=6850 (длинный хвост).
// Стопы экспортированы отдельно (CHAIN_STATS_EDGE_COLOR_STOPS) для подписей в легенде.
export const CHAIN_STATS_EDGE_COLOR_STOPS = [ 1, 2, 7, 50 ];
const CHAIN_STATS_EDGE_COLOR = [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'trip_count' ], 0 ],
    CHAIN_STATS_EDGE_COLOR_STOPS[0], CHAIN_STATS_COLOR_LOW,
    CHAIN_STATS_EDGE_COLOR_STOPS[1], CHAIN_STATS_COLOR_MID_LOW,
    CHAIN_STATS_EDGE_COLOR_STOPS[2], CHAIN_STATS_COLOR_MID_HIGH,
    CHAIN_STATS_EDGE_COLOR_STOPS[3], CHAIN_STATS_COLOR_HIGH,
];
// total_trips по узлам: медиана=18, p75=57, p90=168, p99=1866, максимум=47316.
export const CHAIN_STATS_NODE_COLOR_STOPS = [ 18, 57, 168, 1866 ];
const CHAIN_STATS_NODE_COLOR = [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'total_trips' ], 0 ],
    CHAIN_STATS_NODE_COLOR_STOPS[0], CHAIN_STATS_COLOR_LOW,
    CHAIN_STATS_NODE_COLOR_STOPS[1], CHAIN_STATS_COLOR_MID_LOW,
    CHAIN_STATS_NODE_COLOR_STOPS[2], CHAIN_STATS_COLOR_MID_HIGH,
    CHAIN_STATS_NODE_COLOR_STOPS[3], CHAIN_STATS_COLOR_HIGH,
];
const CHAIN_STATS_EDGES_SOURCE = {
    id: 'martin.chain_statistics_edges_mvt',
    type: 'vector',
    url: `${V.API_GW_URL}/rpc/martin.chain_statistics_edges_mvt.json`,
    promoteId: 'edge_id',
};
const chainStatisticsEdges = {
    id: CHAIN_STATS_EDGES_ID,
    type: 'line',
    'source-layer': 'martin.chain_statistics_edges_mvt',
    source: CHAIN_STATS_EDGES_SOURCE,
    layout: { visibility: 'none', 'line-cap': 'round' },
    filter: CHAIN_STATS_EDGES_EMPTY_FILTER,
    paint: {
        'line-color': CHAIN_STATS_EDGE_COLOR,
        'line-width': CHAIN_STATS_EDGE_WIDTH,
        'line-opacity': 0.8,
    },
};
const chainStatisticsEdgesFictitious = {
    id: CHAIN_STATS_EDGES_FICTITIOUS_ID,
    type: 'line',
    'source-layer': 'martin.chain_statistics_edges_mvt',
    source: CHAIN_STATS_EDGES_SOURCE,
    layout: { visibility: 'none', 'line-cap': 'butt' },
    filter: CHAIN_STATS_EDGES_FICTITIOUS_EMPTY_FILTER,
    paint: {
        'line-color': CHAIN_STATS_EDGE_COLOR,
        'line-width': CHAIN_STATS_EDGE_WIDTH,
        'line-opacity': 0.8,
        'line-dasharray': [ 2, 2 ],
    },
};
// Радиус узла — общие для уровня 0 (эта константа) и уровня 1/2 (Map.jsx: setChainStatsView,
// строит свой interpolate по вычисленному на клиенте значению узла, теми же остановками)
// stops. Уменьшены на 30% от первоначальных (4/6/9/13/18), затем ещё на 30% (2.8/4.2/6.3/9.1/12.6).
export const CHAIN_STATS_NODE_RADIUS_STOPS = [
    0,  2.0,
    1,  2.9,
    3,  4.4,
    6,  6.4,
    10, 8.8,
];
// Размер точки на уровне 0 — по median_chain_length или max_chain_length (переключатель
// в легенде, chainStatsSizeMetric в mapFilters); выбор между ними — в Map.jsx/setChainStatsView.
export const CHAIN_STATS_RADIUS_BY_MEDIAN = [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'median_chain_length' ], 0 ],
    ...CHAIN_STATS_NODE_RADIUS_STOPS,
];
export const CHAIN_STATS_RADIUS_BY_MAX = [ 'interpolate', [ 'linear' ], [ 'to-number', [ 'get', 'max_chain_length' ], 0 ],
    ...CHAIN_STATS_NODE_RADIUS_STOPS,
];
// Пороги слайдера «Число поездок через парковку не менее» (total_trips). Распределение
// сильно скошено (min=0, p25=4, p50=18, p75=57, p90=168, p95=357, p99=1866, p99.9=8416,
// max=47316) — линейный слайдер по нему бесполезен (99% диапазона легло бы в первые
// несколько % длины бегунка). Индексный слайдер по этим порогам (см. ChainStatisticsLegend —
// MUI v4 Slider не поддерживает логарифмический scale, поэтому индекс/значение мапятся вручную).
export const CHAIN_STATS_MIN_TOTAL_TRIPS_STOPS = [ 0, 5, 20, 50, 150, 500, 2000, 10000 ];
const chainStatisticsParkings = {
    id: CHAIN_STATS_PARKINGS_ID,
    label: defineMessage({ message: 'Статистика цепочек трипов' }),
    type: 'circle',
    'source-layer': 'martin.chain_statistics_parkings_mvt',
    source: {
        id: 'martin.chain_statistics_parkings_mvt',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.chain_statistics_parkings_mvt.json`,
        promoteId: 'parking_id',
    },
    layout: { visibility: 'none' },
    paint: {
        // Заливка и обводка — один и тот же цвет (заливка с прозрачностью, обводка без —
        // контрастная кайма поверх), градуированный по total_trips (см. CHAIN_STATS_NODE_COLOR).
        'circle-color': CHAIN_STATS_NODE_COLOR,
        'circle-radius': CHAIN_STATS_RADIUS_BY_MEDIAN,
        'circle-opacity': 0.5,
        'circle-stroke-color': CHAIN_STATS_NODE_COLOR,
        'circle-stroke-opacity': 1,
        'circle-stroke-width': 2.6,
    },
    minzoom: 4,
    availableFor: [ V.SUPER_ADMIN ],
    additionalLayers: [ chainStatisticsEdges, chainStatisticsEdgesFictitious ],
    // Атрибуты точек: parking_id, chain_count, max_chain_length, median_chain_length
    // Атрибуты рёбер: edge_id, start_parking_id, trip_start_parking_id, trip_finish_parking_id, is_fictitious, trip_count, max_steps
};
// DA-16000: точки доступа Wi-Fi, собранные по сканам самокатов (dwh.wifi_ap).
// В базе только класс stable — тот, что участвует в позиционировании: мобильные хотспоты
// (разброс наблюдений по ячейкам H3 res=7) и точки с одним-двумя наблюдениями отсеяны
// при сборке, на карту их и не подаём.
//
// Два представления одного слоя, переключаются радиокнопкой в легенде (WifiApLegend):
//   points — отдельные точки, размер по числу наблюдений;
//   h3     — агрегат по гексагонам с выбором уровня H3.
// Переключаем именно visibility, а не прозрачность (как в «Землепользовании»): точечный
// источник — 950 тыс. фич, держать его загруженным в режиме агрегата незачем.
export const WIFI_AP_LAYER_ID = 'Wifi точки';
export const WIFI_AP_H3_LAYER_ID = 'Wifi точки_h3';
const wifiApH3 = {
    id: WIFI_AP_H3_LAYER_ID,
    label: defineMessage({ message: 'Wifi точки (плотность покрытия)' }),
    type: 'fill',
    minzoom: 0,
    maxzoom: 24,
    'source-layer': 'martin.wifi_ap_h3_mvt',
    source: {
        id: 'wifi_ap_h3_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.wifi_ap_h3_mvt.json`,
        requiredQueryParams: [ 'h3_zoom' ],
    },
    layout: { visibility: 'none' },
    paint: {
        'fill-color': [
            'interpolate',
            [ 'linear' ],
            // Масштабирование cnt_ap * 4^(zoom - 12): без него фиксированные пороги врут
            // при смене уровня H3 — в ячейку уровнем ниже попадает кратно больше точек,
            // и в режиме «авто» карта то выцветает в синее, то насыщается в жёлтое при
            // обычном зумировании. База 12 (самый мелкий уровень), чтобы подписи легенды
            // на нём были сырыми числами, а не долями точки; множитель 4 подобран по
            // фактическому росту p90 (4, 15, 62, 302, 1654, 8612 на уровнях 12→7),
            // а не по геометрии H3 — часть дочерних ячеек пуста, и «семёрка» завышает.
            // zoom приходит атрибутом тайла — его отдаёт макрос create_martin_h3_mvt.
            [ '*',
                [ 'to-number', [ 'get', 'cnt_ap' ] ],
                [ '^', 4, [ '-', [ 'to-number', [ 'get', 'zoom' ] ], 12 ] ],
            ],
            0,
            'hsla(240, 100%, 27%, 0)',
            0.5,
            'hsla(240, 100%, 27%, 0.4)',
            1,
            '#0d0887',
            2,
            '#6b00a8',
            4,
            '#e16461',
            8,
            '#fca736',
            16,
            '#f0f921',
        ],
        'fill-opacity': 0.6,
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
    },
    legend: WIFI_AP_H3_LEGEND,
    availableFor: [ V.SUPER_ADMIN ],
};
const wifiAp = {
    id: WIFI_AP_LAYER_ID,
    label: defineMessage({ message: 'Wifi точки' }),
    type: 'circle',
    // Порог 12 (тот же зашит в MVT-функции и в конфиге Martin — там он и бережёт базу).
    // Цена известна: в Москве около 900 точек на км², тайл z12 несёт ~30 тыс. фич против
    // ~4 тыс. на z14. Ниже 12-го работает «Плотность покрытия».
    minzoom: 12,
    maxzoom: 24,
    'source-layer': 'martin.wifi_ap_mvt',
    source: {
        id: 'wifi_ap_source',
        type: 'vector',
        url: `${V.API_GW_URL}/rpc/martin.wifi_ap_mvt.json`,
    },
    layout: {
        visibility: 'none',
        // Порядок отрисовки внутри слоя: чем больше наблюдений, тем выше точка.
        // Иначе в плотной застройке жёлтые (сотни наблюдений) прячутся под синими
        // (два-три наблюдения), которых на порядок больше. Сортировка действует
        // внутри тайла — это штатное поведение circle-sort-key.
        'circle-sort-key': [ 'to-number', [ 'get', 'n_obs' ] ],
    },
    paint: {
        // Размер одинаковый у всех точек — различает их только цвет. Зависимость от зума
        // оставлена: на 12-м в тайле около 30 тыс. точек, крупные кружки сливаются в пятно.
        // Разброс наблюдений r_m виден в попапе.
        'circle-radius': [
            'interpolate',
            [ 'linear' ],
            [ 'zoom' ],
            12, 2,
            14, 3.5,
            18, 7,
        ],
        // Цвет — число наблюдений, plasma-палитра как у агрегированных треков.
        // Пороги по перцентилям: p50 = 8, p90 = 38, p99 = 174.
        'circle-color': [
            'interpolate',
            [ 'linear' ],
            [ 'to-number', [ 'get', 'n_obs' ] ],
            2,   '#0d0887',
            8,   '#6b00a8',
            25,  '#e16461',
            80,  '#fca736',
            300, '#f0f921',
        ],
        'circle-opacity': 0.8,
        'circle-stroke-width': 0.4,
        'circle-stroke-color': 'rgba(0, 0, 0, 0.35)',
    },
    availableFor: [ V.SUPER_ADMIN ],
    additionalLayers: [ wifiApH3 ],
};
const devLayers = [
    tileBoundaries,
    wifiAp,
    h3GridLayer,
    operationAreaErrLayer,
    operationAreasDev,
    // chainStatisticsParkings — слой «Статистика цепочек трипов» временно снят с показа
    // (неактуален). Определение слоя, фильтр и легенда оставлены на месте: чтобы вернуть,
    // достаточно раскомментировать эту строку.
];

// Поле id — стабильный идентификатор группы для пользовательских настроек
// (скрытие групп из панели, DA-15900). Хранится в настройках пользователя, поэтому:
// новой группе — следующий свободный номер независимо от её позиции в списке,
// id удалённой группы не переиспользовать. Иначе тем, кто скрыл старую группу,
// молча спрячется новая на её номере.
export const allLayersGroups = [
    {
        id: 1,
        label: defineMessage({ message: 'Инфраструктура' }),
        layers: infrastructureLayers,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 2,
        label: defineMessage({ message: 'Ребалансинг' }),
        layers: rebalancing,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 3,
        label: defineMessage({ message: 'Самокаты' }),
        layers: deviceLayers,
        // URBANIST/DESIGNER видят в группе только слои, открытые им (сейчас — «Вероятные призраки»)
        availableFor: [ V.ADMIN, V.SUPER_ADMIN, V.URBANIST, V.DESIGNER ],
    },
    {
        id: 4,
        label: defineMessage({ message: 'Поездки' }),
        layers: tripLayers,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 5,
        label: defineMessage({ message: 'Конкуренты' }),
        layers: competitors,
        availableFor: [ V.SUPER_ADMIN ],
    },
    {
        id: 6,
        label: defineMessage({ message: 'Зоны' }),
        layers: areaLayers,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 7,
        label: defineMessage({ message: 'Действия с приложением' }),
        layers: appActions,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 8,
        label: defineMessage({ message: 'Тариф' }),
        layers: Tariff,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 9,
        label: defineMessage({ message: 'СЦЕНАРИЙ: Открытие нового города' }),
        layers: NewCitiesScenario,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 10,
        label: defineMessage({ message: 'СЦЕНАРИЙ: Аудит парковок' }),
        layers: ParkingAudit,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 11,
        label: defineMessage({ message: 'СЦЕНАРИЙ: Ребалансинг' }),
        layers: Rebalancing,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 12,
        label: defineMessage({ message: 'СЦЕНАРИЙ: Изменение зон' }),
        layers: ChangingZones,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 13,
        label: defineMessage({ message: 'СЦЕНАРИЙ: Взаимодействие с городскими властями' }),
        layers: WorkingWithAdministration,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 14,
        label: defineMessage({ message: 'СЦЕНАРИЙ: Зоны ограничения скорости' }),
        layers: LimitedZones,
        availableFor: [ V.ADMIN, V.SUPER_ADMIN ],
    },
    {
        id: 15,
        label: defineMessage({ message: 'DEV' }),
        layers: devLayers,
        availableFor: [ V.SUPER_ADMIN ],
    },
];

// Живые слои (см. utils/liveTileRefresh.js): по id включённых слоёв собирает источники,
// у которых тайлы надо периодически перезапрашивать, и их интервалы. liveRefresh: true —
// интервалы по умолчанию, объект — переопределение (activeMs / idleMs / stopAfterMs).
const allLayersFlat = allLayersGroups.reduce((acc, group) => acc.concat(group.layers || []), []);
export const getLiveRefreshLayers = (displayedLayers = []) => displayedLayers
    .map(({ id }) => allLayersFlat.find(layer => layer && layer.id === id))
    .filter(layer => layer && layer.liveRefresh && layer.source)
    .map(layer => ({
        sourceId: typeof layer.source === 'object' ? (layer.source.id || `${layer.id}_source`) : layer.source,
        ...(typeof layer.liveRefresh === 'object' ? layer.liveRefresh : {}),
    }))
    .filter(({ sourceId }) => Boolean(sourceId));
